"use client";

import { useAuth } from "@/contexts/AuthContext";
import {
  useBrandGroup,
  type ProjectResourceAssignmentInput,
} from "@/contexts/BrandGroupContext";
import type { WorkspaceIntegration } from "@/contexts/AuthContext/useWorkspaceIntegrations";
import type { WorkspaceMemberDisplay } from "@/contexts/AuthContext/useWorkspaceMembers";
import { useDialog } from "@/contexts/DialogContext";
import { useToast } from "@/contexts/ToastContext";
import { useTranslation } from "@client-app/i18n";
import {
  authService,
  type ConnectionScene,
  type UpdateWorkspaceConnectionAccessGrant,
  type WorkspaceConnectionAccessGrant,
  type WorkspaceGoogleAdsCustomerAccessGrant,
} from "@common/api/authService";
import {
  projectService,
  type IntegrationContractResult,
  type IntegrationResource,
  type ProjectIntegrationsBootstrapResponse,
  type ProjectUserResourceGrant,
} from "@common/api/brandGroupService";
import { getCurrentProductCode, isPluginProduct, withPluginProductHeaders } from '@common/api/productContext';
import { mcpConnectorService } from "@common/api/mcpConnectorService";
import { Checkbox, Tooltip } from "@common/index";
import {
  CaretDown,
  CaretLeft,
  CaretRight,
  Check,
  Briefcase,
  CircleNotch,
  CirclesFour,
  LinkBreak,
  MagnifyingGlass,
  PencilSimple,
  Plus,
  Question,
  User,
  Users,
} from "@phosphor-icons/react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import BusinessGroupTooltipList, {
  businessGroupTooltipContentClassName,
} from "./BusinessGroupTooltipList";
import clsx from "clsx";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { resolveShopifyConnectionDomain } from "../../Onboarding/storeUrlNormalization";
import { getOwnedConnectionRows, getVisibleConnectionRows } from "./connectionAccessVisibility";
import { getConnectionScopeCounts, type ConnectionScopeCounts } from "./integrations/connectionScopeFilterVisibility";
import {
  getBusinessGroupDisplayName,
  type BrandConnection,
  type BrandResource,
} from "./brandGroupViewModel";
import type { ConnectionData } from "./integrations/ConnectionCard";
import { getBrandIcon, getBrandIconFrameClassName, getBrandIconImageClassName } from "./integrations/IntegrationCard";
import BusinessGroupAssignmentModal from "./integrations/BusinessGroupAssignmentModal";
import {
  AVAILABLE_INTEGRATION_STATUS,
  getIntegrationConnectionStatusMeta,
  isIntegrationConnectionActive,
  resolveKlaviyoIntegrationAvailability,
} from "./integrations/integrationStatus";
import {
  isMcpBackedDisconnectConnection,
  resolveMcpDisconnectConnectionMode,
  resolveMcpConnectorForDisconnect,
} from "./integrations/connectionMcpDisconnect";
import {
  getManageableProjectAssignmentChanges,
  getNewBulkAssignmentAccessLevel,
  resolveCanonicalProjectConnectionIdWithRetry,
} from "./integrations/connectionProjectAssignment";
import {
  getParentConnectionKeysWithoutSelectedResources,
  prioritizePersistedAssignedUnits,
} from "./integrations/bulkBusinessGroupAssignment";
import {
  getCanonicalProjectSecondaryResources,
  hasAssignedCanonicalProjectSecondaryResource,
  isCanonicalProjectSecondaryResource,
} from "./projectIntegrationProjection";
import ApplicationAccessPermissionControl from "./ApplicationAccessPermissionControl";
import BrandSelectMenu from "./BrandSelectMenu";
import Modal from "./Modal";
import { getAdminProductFeatures } from "../../../utils/adminProductFeatures";
import { formatRoleLabel } from "./roleLabels";
import { logAdminWrite, startAdminWrite } from "@/lib/behaviorTracking";
import {
  canAssignConnectionToBusinessGroups,
  getApplicationOwnerAssignableBusinessGroups,
  getBusinessGroupCreatorName,
  getManageableBusinessGroups,
} from "./brandProjectActions";
import {
  applyProjectContractResourceAccess,
  getProjectResourceIdentityKey,
  isProjectContractIntegrationUsable,
  resolveProjectConnectionAccess,
  resolveProjectOwnerAccess,
  resolveProjectResourceContractAccess,
  resolveProjectResourceAccess,
  resolveProjectResourceSecondaryColumn,
  type ProjectAssignmentCeiling,
} from "./projectResourceAccess";
import { refreshBrandGroupAfterAssignmentSave } from "./connectionAccessSaveFlow";
import {
  canEditConnectionMemberAccess,
  canEditConnectionMemberAccessTarget,
  shouldShowPluginConnectionAccessAction,
} from "./pluginConnectionAccessVisibility";
import {
  sortBusinessGroupApplicationsNewestFirst,
  type BusinessGroupMemberAccessTarget,
} from "./businessGroupApplicationAccess";

type DelegatedProjectAccessLevel = "READ" | "WRITE";
type ProjectAssignmentBatchPayload = Parameters<
  typeof projectService.applyProjectAssignmentBatch
>[1];
type ProjectAssignmentBatchResourceSync = NonNullable<
  ProjectAssignmentBatchPayload["resourceSyncs"]
>[number];
type ProjectAssignmentBatchResourceUpsert =
  ProjectAssignmentBatchResourceSync["resources"][number];
type ProjectAssignmentBatchResourceRemoval =
  ProjectAssignmentBatchResourceSync["removals"][number];

/**
 * DISABLED is the stored pending-association state. The application-owner
 * delegation editor intentionally offers READ/WRITE only, so a pending row
 * starts at READ without changing the persisted ceiling until Save.
 */
const toDelegatedProjectAccessLevel = (
  ceiling: ProjectAssignmentCeiling,
  fallback: DelegatedProjectAccessLevel = "READ",
): DelegatedProjectAccessLevel => {
  if (ceiling === "WRITE" || ceiling === "READ") return ceiling;
  if (ceiling === "DISABLED") return "READ";
  return fallback;
};

const ACCESS_TABLE_SKELETON_ROWS = [0, 1, 2];
const MAX_INLINE_ASSIGNED_BUSINESS_GROUPS = 3;

const ConnectionAccessTableSkeleton = () => (
  <div className="rounded-lg border border-slate-200 bg-white" aria-busy="true">
    <div className="animate-pulse divide-y divide-slate-100">
      {ACCESS_TABLE_SKELETON_ROWS.map((index) => (
        <div
          key={index}
          className="grid grid-cols-[minmax(300px,1fr)_140px_200px] gap-4 items-center px-4 py-3"
        >
          <div className="flex min-w-0 items-center gap-3">
            <div className="h-8 w-8 shrink-0 rounded-full bg-slate-100" />
            <div className="min-w-0 space-y-1.5">
              <div className="h-3 w-24 rounded bg-slate-100" />
              <div className="h-2.5 w-36 rounded bg-slate-50" />
            </div>
          </div>
          <div className="h-3 w-16 rounded bg-slate-50" />
          <div className="flex justify-start">
            <div className="h-8 w-[156px] rounded-lg bg-slate-50 border border-slate-100" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

interface BulkAccessDropdownProps {
  onChange: (level: ConnectionAccessLevel) => void;
  disabled?: boolean;
  t: (key: string) => string;
  accessLabel: (level: ConnectionAccessLevel) => string;
}

const BulkAccessDropdown = ({ onChange, disabled, t, accessLabel }: BulkAccessDropdownProps) => {
  const [open, setOpen] = useState(false);

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={clsx(
            "inline-flex h-7 items-center justify-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors outline-none",
            disabled
              ? "opacity-50 cursor-not-allowed text-slate-400"
              : "text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-slate-200",
          )}
        >
          {t("website.integrationsSettings.applyToAll") || "Apply to all"}
          <CaretDown
            size={12}
            weight="bold"
            className={clsx("transition-transform duration-200", open && "rotate-180")}
          />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          className="z-[10001] w-[190px] animate-in fade-in zoom-in-95 rounded-lg border border-slate-200 bg-white p-1 shadow-lg"
        >
          {ACCESS_LEVELS.map((level) => (
            <DropdownMenu.Item
              key={level}
              onSelect={() => onChange(level)}
              className="flex cursor-pointer select-none items-center rounded-md px-2.5 py-2 text-sm text-slate-600 outline-none transition-colors data-[highlighted]:bg-slate-50 data-[highlighted]:text-slate-900"
            >
              {accessLabel(level)}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

type ConnectionAccessLevel = "no_access" | "read" | "action";

const ACCESS_LEVELS: ConnectionAccessLevel[] = ["no_access", "read", "action"];

interface ManageAccessSwitchProps {
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  ariaLabel: string;
}

const ManageAccessSwitch = ({ checked, disabled, onChange, ariaLabel }: ManageAccessSwitchProps) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={ariaLabel}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={clsx(
      "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full p-0.5 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]/20 focus-visible:ring-offset-2",
      checked ? "bg-[#2563EB]" : "bg-[#D7DBE5]",
      disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer",
    )}
  >
    <span
      className={clsx(
        "pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200",
        checked ? "translate-x-4" : "translate-x-0",
      )}
    />
  </button>
);

interface ManageAccessStatusProps {
  enabled: boolean;
  enabledLabel: string;
  disabledLabel: string;
}

const ManageAccessStatus = ({ enabled, enabledLabel, disabledLabel }: ManageAccessStatusProps) => (
  <span
    className={clsx(
      "inline-flex h-7 min-w-[92px] items-center justify-center rounded-full px-2.5 text-xs font-medium leading-none",
      enabled ? "bg-blue-50 text-blue-600 ring-1 ring-inset ring-blue-500/20" : "text-[#8A93B2]",
    )}
  >
    {enabled ? enabledLabel : disabledLabel}
  </span>
);

interface ConnectionActionsMenuProps {
  manageAccessText?: string;
  assignBrandsText?: string;
  disconnectText: string;
  disconnectTooltip?: string;
  removeFromBrandText?: string;
  removeFromBrandTooltip?: string;
  onManageAccess?: () => void;
  onAssignBrands?: () => void;
  onDisconnect?: () => void;
  onRemoveFromBrand?: () => void;
  isDisconnecting?: boolean;
}

const ConnectionActionsMenu = ({
  manageAccessText = "",
  assignBrandsText = "",
  disconnectText,
  disconnectTooltip = "",
  removeFromBrandText = "",
  removeFromBrandTooltip = "",
  onManageAccess,
  onAssignBrands,
  onDisconnect,
  onRemoveFromBrand,
  isDisconnecting = false,
}: ConnectionActionsMenuProps) => {
  return (
    <div className="flex items-center gap-1">
      {onManageAccess ? (
        <Tooltip content={manageAccessText} side="top">
          <button
            type="button"
            onClick={onManageAccess}
            aria-label={manageAccessText}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[#8A93B2] outline-none transition-colors hover:bg-[#F4F5F8] hover:text-[#1A162F] focus-visible:ring-2 focus-visible:ring-[#1A162F]/20"
          >
            <Users size={18} weight="regular" />
          </button>
        </Tooltip>
      ) : null}
      {onAssignBrands ? (
        <Tooltip content={assignBrandsText} side="top">
          <button
            type="button"
            onClick={onAssignBrands}
            aria-label={assignBrandsText}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[#8A93B2] outline-none transition-colors hover:bg-[#F4F5F8] hover:text-[#1A162F] focus-visible:ring-2 focus-visible:ring-[#1A162F]/20"
          >
            <Briefcase size={18} weight="regular" />
          </button>
        </Tooltip>
      ) : null}
      {onDisconnect ? (
        <Tooltip content={disconnectTooltip || disconnectText} side="top">
          <span className="inline-flex">
            <button
              type="button"
              onClick={onDisconnect}
              disabled={isDisconnecting}
              aria-label={disconnectText}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[#8A93B2] outline-none transition-colors hover:bg-[#FFF1F0] hover:text-[#B42318] focus-visible:ring-2 focus-visible:ring-[#B42318]/20 disabled:cursor-wait disabled:opacity-50"
            >
              {isDisconnecting ? (
                <CircleNotch size={14} weight="bold" className="animate-spin" />
              ) : (
                <LinkBreak size={18} weight="regular" />
              )}
            </button>
          </span>
        </Tooltip>
      ) : null}
      {onRemoveFromBrand ? (
        <Tooltip content={removeFromBrandTooltip || removeFromBrandText} side="top">
          <button
            type="button"
            onClick={onRemoveFromBrand}
            aria-label={removeFromBrandText}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[#8A93B2] outline-none transition-colors hover:bg-[#FFF1F0] hover:text-[#B42318] focus-visible:ring-2 focus-visible:ring-[#B42318]/20"
          >
            <Briefcase size={18} weight="regular" />
          </button>
        </Tooltip>
      ) : null}
    </div>
  );
};

// ─── Access Level Dropdown Component ─────────────────────────────────────────
interface AccessLevelDropdownProps {
  value: ConnectionAccessLevel;
  onChange: (level: ConnectionAccessLevel) => void;
  disabled?: boolean;
  accessLabel: (level: ConnectionAccessLevel) => string;
}

const AccessLevelDropdown = ({ value, onChange, disabled, accessLabel }: AccessLevelDropdownProps) => {
  const [open, setOpen] = useState(false);

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={clsx(
            "inline-flex h-8 w-[156px] items-center justify-between gap-1 rounded-lg border px-2.5 text-xs font-medium outline-none transition-colors",
            disabled
              ? "cursor-not-allowed border-[#E8EBF3] bg-[#F8F9FC] text-[#8A93B2] opacity-60"
              : clsx(
                  "text-[#1A162F] focus-visible:border-[#C8D0E2] focus-visible:bg-[#F8F9FC] focus-visible:ring-2 focus-visible:ring-[#C8D0E2]/35",
                  open
                    ? "border-[#C8D0E2] bg-[#F8F9FC]"
                    : "border-[#E8EBF3] bg-white hover:border-[#C8D0E2] hover:bg-[#F8F9FC]",
                ),
          )}
        >
          <span className="truncate">{accessLabel(value)}</span>
          <CaretDown
            size={12}
            weight="bold"
            className={clsx("text-slate-400 transition-transform duration-200 shrink-0", open && "rotate-180")}
          />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          className="z-[10001] w-[168px] animate-in fade-in zoom-in-95 rounded-lg border border-slate-200 bg-white p-1 shadow-lg"
        >
          {ACCESS_LEVELS.map((level) => (
            <DropdownMenu.Item
              key={level}
              onSelect={() => onChange(level)}
              className={clsx(
                "flex cursor-pointer select-none items-center rounded-md px-2.5 py-1.5 text-xs outline-none transition-colors",
                value === level
                  ? "bg-slate-50 text-slate-900 font-semibold"
                  : "text-slate-600 data-[highlighted]:bg-slate-50/70 data-[highlighted]:text-slate-900",
              )}
            >
              {accessLabel(level)}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

type GoogleAdsCustomerSubAccount = NonNullable<ConnectionData["subAccounts"]>[number] & {
  projectAccess?: ConnectionAccessLevel;
};

type WorkspaceResourceLabelTarget = {
  resourceKey: string;
  resourceType: string;
  label?: string | null;
};

interface ConnectionAccessRow {
  id: string;
  connectionId?: string;
  connectionBackend?: ConnectionData["connectionBackend"];
  workspaceIntegrationId?: string;
  slugId?: string;
  authType?: string;
  native?: boolean;
  connectionMode: "INTERNAL_APP" | "MCP_SERVER";
  provider: string;
  integrationType: string;
  name: string;
  accountId: string;
  alias: string;
  status: ConnectionData["status"];
  statusLabel?: string;
  statusMessage?: string;
  isUnavailable?: boolean;
  connectedAt: string;
  connectedBy: string;
  subAccounts?: GoogleAdsCustomerSubAccount[];
  resourceType?: string;
  resourceKey?: string;
  isProjectResourceRow?: boolean;
  connectionOwnerKey: string | null;
  isConnectionOwnedByCurrentUser: boolean;
  currentUserAccess: ConnectionAccessLevel;
  projectAccessByResource: Record<string, ConnectionAccessLevel>;
  isManagedByCurrentUser: boolean;
  canManageAccess: boolean;
  brandIds: string[];
  subAccountBrandIds: Record<string, string[]>;
}

const getGoogleAdsCustomerBrandKey = (connectionId: string, customerId: string) => `${connectionId}:${customerId}`;

interface MemberAccessColumn {
  key: string;
  userId: string;
  name: string;
  email: string;
  role: string;
  avatar?: string | null;
  isCurrentUser: boolean;
  isWorkspaceOwner: boolean;
}

const mapBackendConnectionAccessToUi = (accessLevel?: string | null): ConnectionAccessLevel => {
  const normalized = String(accessLevel || "")
    .trim()
    .toUpperCase();
  if (normalized === "WRITE") return "action";
  if (normalized === "READ") return "read";
  return "no_access";
};

const resolveCurrentUserConnectionAccess = (
  mapping: Record<string, any> | undefined,
  integrationRecord: Record<string, any>,
): ConnectionAccessLevel => {
  return mapBackendConnectionAccessToUi(
    firstString(
      mapping?.yourAccess,
      mapping?.accessLevel,
      mapping?.currentUserAccess,
      mapping?.current_user_access,
      integrationRecord.yourAccess,
      integrationRecord.accessLevel,
      integrationRecord.currentUserAccess,
      integrationRecord.current_user_access,
    ),
  );
};

const mapUiConnectionAccessToBackend = (
  accessLevel: ConnectionAccessLevel,
): UpdateWorkspaceConnectionAccessGrant["accessLevel"] => {
  if (accessLevel === "action") return "WRITE";
  if (accessLevel === "read") return "READ";
  return "DISABLED";
};

const findMemberForAccessGrant = (grant: WorkspaceConnectionAccessGrant, members: MemberAccessColumn[]) => {
  const grantUserId = normalizeText(grant.userId);
  const grantEmail = normalizeText(grant.email);
  return members.find(
    (member) =>
      normalizeText(member.key) === grantUserId || (!!grantEmail && normalizeText(member.email) === grantEmail),
  );
};

const buildAccessMatrixFromBackendGrants = (
  grants: WorkspaceConnectionAccessGrant[],
  members: MemberAccessColumn[],
) => {
  const accessByMember = members.reduce<Record<string, ConnectionAccessLevel>>((next, member) => {
    next[member.key] = "no_access";
    return next;
  }, {});

  grants.forEach((grant) => {
    const member = findMemberForAccessGrant(grant, members);
    if (!member) return;
    accessByMember[member.key] = mapBackendConnectionAccessToUi(grant.accessLevel);
  });

  return accessByMember;
};

const buildManageAccessMatrixFromBackendGrants = (
  grants: WorkspaceConnectionAccessGrant[],
  members: MemberAccessColumn[],
) => {
  const manageAccessByMember = members.reduce<Record<string, boolean>>((next, member) => {
    next[member.key] = false;
    return next;
  }, {});

  grants.forEach((grant) => {
    const member = findMemberForAccessGrant(grant, members);
    if (!member) return;
    manageAccessByMember[member.key] = Boolean(grant.canManageAccess);
  });

  return manageAccessByMember;
};

const buildGoogleAdsCustomerAccessMatrixFromBackendGrants = (
  grants: WorkspaceGoogleAdsCustomerAccessGrant[] | undefined,
  members: MemberAccessColumn[],
) => {
  const accessByMember = members.reduce<Record<string, string[]>>((next, member) => {
    next[member.key] = [];
    return next;
  }, {});

  (grants ?? []).forEach((grant) => {
    const member = findMemberForAccessGrant(grant as WorkspaceConnectionAccessGrant, members);
    const customerId = String(grant.customerId || "").trim();
    if (!member || !customerId) return;
    accessByMember[member.key] = [...(accessByMember[member.key] ?? []), customerId];
  });

  return accessByMember;
};

const buildGoogleAdsCustomerAccessLevelMatrixFromBackendGrants = (
  grants: WorkspaceGoogleAdsCustomerAccessGrant[] | undefined,
  members: MemberAccessColumn[],
) => {
  const accessByMember = members.reduce<Record<string, Record<string, ConnectionAccessLevel>>>((next, member) => {
    next[member.key] = {};
    return next;
  }, {});

  (grants ?? []).forEach((grant) => {
    const member = findMemberForAccessGrant(grant as WorkspaceConnectionAccessGrant, members);
    const customerId = String(grant.customerId || "").trim();
    if (!member || !customerId) return;
    accessByMember[member.key] = {
      ...(accessByMember[member.key] ?? {}),
      [customerId]: mapBackendConnectionAccessToUi(grant.accessLevel),
    };
  });

  return accessByMember;
};

const buildConnectionAccessUpdateGrants = (
  members: MemberAccessColumn[],
  draftAccess: Record<string, ConnectionAccessLevel>,
  savedAccess: Record<string, ConnectionAccessLevel>,
  draftManageAccess: Record<string, boolean>,
  savedManageAccess: Record<string, boolean>,
): UpdateWorkspaceConnectionAccessGrant[] =>
  members
    .filter((member) => member.userId)
    .map((member) => ({
      userId: member.userId,
      accessLevel: mapUiConnectionAccessToBackend(draftAccess[member.key] ?? savedAccess[member.key] ?? "no_access"),
      canManageAccess: draftManageAccess[member.key] ?? savedManageAccess[member.key] ?? false,
    }));

interface LoadConnectionAccessGrantsOptions {
  silent?: boolean;
}

const canCurrentMemberManageConnectionAccess = (
  row: ConnectionAccessRow,
  currentMemberKey: string | null,
  manageAccessByMember?: Record<string, boolean>,
) => {
  if (!currentMemberKey) {
    return false;
  }

  // The person who established the OAuth connection owns its lifecycle.
  // They must always be able to manage access and disconnect this connection.
  if (row.isConnectionOwnedByCurrentUser) {
    return true;
  }

  if (row.canManageAccess) {
    return true;
  }

  if (!manageAccessByMember) {
    return false;
  }

  return Boolean(manageAccessByMember[currentMemberKey]);
};

const shouldPrefetchConnectionAccess = (row: ConnectionAccessRow) =>
  isIntegrationConnectionActive(row) && Boolean(getConnectionAccessBackendId(row));

const PROVIDER_LABELS: Record<string, string> = {
  shopify: "Shopify",
  woocommerce: "WooCommerce",
  bigcommerce: "BigCommerce",
  magento: "Magento",
  meta: "Meta Ads",
  "meta-ads": "Meta Ads",
  google: "Google Ads",
  "google-ads": "Google Ads",
  "google-analytics": "Google Analytics",
  "google-search-console": "Google Search Console",
  klaviyo: "Klaviyo",
  "klaviyo-oauth": "Klaviyo",
  mailchimp: "Mailchimp",
  "mailchimp-oauth": "Mailchimp",
  feishu: "Feishu",
  dingtalk: "DingTalk",
  semrush: "Semrush",
  ahrefs: "Ahrefs",
  omnisend: "Omnisend",
  okendo: "Okendo",
  "insider-one": "Insider One",
  "insider-one-oauth": "Insider One",
  emarsys: "Emarsys",
  "emarsys-oauth": "Emarsys",
  judgeme: "Judge.me",
  "facebook-page": "Facebook Page",
  instagram: "Instagram",
  "pinterest-ads": "Pinterest Ads",
  "x-ads": "X Ads",
  wix: "Wix",
  "wix-oauth": "Wix",
};

const PROVIDER_ALIASES: Record<string, string> = {
  "adobe-commerce": "magento",
  googleads: "google-ads",
  klaviyo: "klaviyo-oauth",
  "judgeme-oauth": "judgeme",
  "judge-me": "judgeme",
  "judge.me": "judgeme",
  judge: "judgeme",
  "insider-one-oauth": "insider-one",
  insider: "insider-one",
  useinsider: "insider-one",
  "emarsys-oauth": "emarsys",
  "sap-emarsys": "emarsys",
  "sap-engagement-cloud": "emarsys",
  metaads: "meta-ads",
  facebook: "facebook-page",
  pinterestads: "pinterest-ads",
  pinterest: "pinterest-ads",
  xads: "x-ads",
  "twitter-ads": "x-ads",
  twitterads: "x-ads",
  "twitter-x": "x-ads",
  twitterx: "x-ads",
  twitter: "x-ads",
  x: "x-ads",
};

const normalizeText = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const normalizeProvider = (value: unknown) => {
  const normalized = normalizeText(value).replace(/[\s_]+/g, "-");
  return PROVIDER_ALIASES[normalized] || normalized;
};

const normalizeConnectionIdentity = (value: unknown) =>
  normalizeText(value)
    .replace(/^https?:\/\//, "")
    .replace(/\/$/, "");

const firstString = (...values: unknown[]) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return "";
};

const getIntegrationMappings = (integration: WorkspaceIntegration) => {
  const mappings = Array.isArray(integration.mappings) ? integration.mappings : [];
  return mappings.length > 0 ? mappings : [undefined];
};

const getProviderLabel = (provider: string) => {
  const key = normalizeProvider(provider);
  return PROVIDER_LABELS[key] ?? firstString(provider, key) ?? "Connection";
};

const getConnectionGroupKey = (provider: string) => normalizeProvider(provider);
const getConnectionGroupElementId = (provider: string) => `connection-group-${getConnectionGroupKey(provider)}`;
const getConnectionRowElementKey = (row: Pick<ConnectionAccessRow, "id">) =>
  `connection-row-${normalizeConnectionIdentity(row.id).replace(/[^a-z0-9-]/g, "-") || "item"}`;

const getConnectionRowIdentityKeys = (
  row: Pick<ConnectionAccessRow, "id" | "connectionId" | "workspaceIntegrationId" | "slugId" | "accountId" | "name">,
) => {
  const keys = new Set<string>();
  [row.id, row.connectionId, row.workspaceIntegrationId, row.slugId, row.accountId, row.name].forEach((value) => {
    const normalizedValue = normalizeConnectionIdentity(value);
    if (normalizedValue) {
      keys.add(normalizedValue);
    }
  });
  return keys;
};

const getConnectionAccessBackendId = (row: Pick<ConnectionAccessRow, "id" | "workspaceIntegrationId">) =>
  row.workspaceIntegrationId || (Number.isFinite(Number(row.id)) ? row.id : "");

const isScrollableAncestor = (element: HTMLElement) => {
  const { overflowY } = window.getComputedStyle(element);
  return /(auto|scroll|overlay)/.test(overflowY) && element.scrollHeight > element.clientHeight;
};

const findClosestScrollContainer = (element: HTMLElement | null) => {
  let current = element?.parentElement ?? null;

  while (current) {
    if (isScrollableAncestor(current)) {
      return current;
    }
    current = current.parentElement;
  }

  return null;
};

const scrollConnectionGroupIntoView = (element: HTMLElement) => {
  const scrollContainer = findClosestScrollContainer(element);

  if (!scrollContainer) {
    element.scrollIntoView({ behavior: "smooth", block: "start" });
    return;
  }

  const containerRect = scrollContainer.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const topOffset = 24;
  const maxScrollTop = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
  const nextScrollTop = Math.min(
    Math.max(0, scrollContainer.scrollTop + (elementRect.top - containerRect.top) - topOffset),
    maxScrollTop,
  );

  scrollContainer.scrollTo({
    top: nextScrollTop,
    behavior: "smooth",
  });
};

const normalizeConnectionStatusValue = (
  status: unknown,
  fallback: ConnectionData["status"] = "disconnected",
): ConnectionData["status"] => {
  const normalized = normalizeText(status);

  if (!normalized) {
    return fallback;
  }

  if (normalized === "1" || normalized === "true" || normalized === "active" || normalized === "connected") {
    return "connected";
  }

  if (normalized === "0" || normalized === "false" || normalized === "inactive" || normalized === "disconnected") {
    return "disconnected";
  }

  if (normalized === "error" || normalized === "failed") {
    return "error";
  }

  if (normalized === "disabled" || normalized === "suspended" || normalized === "trial_expired") {
    return normalized as ConnectionData["status"];
  }

  return fallback;
};

const getMemberKey = (member: WorkspaceMemberDisplay) =>
  firstString(member.userId, member.id, member.email, member.displayName, member.name);

const getMemberRole = (member: WorkspaceMemberDisplay) =>
  firstString(member.dataRoleName, member.workspaceRole, member.membershipRole, member.role);

const isWorkspaceOwnerRole = (role: unknown) => {
  const normalizedRole = normalizeText(role).replace(/[\s_-]+/g, "");
  return normalizedRole === "owner" || normalizedRole.includes("owner");
};

const getMemberDisplayRole = (member: MemberAccessColumn, t: (key: string) => string) => {
  if (!member.role) return "";
  return formatRoleLabel(String(member.role), t, { titleCaseFallback: true });
};

const normalizeConnectionUserMessage = (message?: string | null, fallback = "Connection failed. Please try again.") => {
  if (!message) {
    return fallback;
  }

  return message
    .replace(/Zernio/gi, "Bustly")
    .replace(/Zerinio/gi, "Bustly")
    .replace(/zerinio/gi, "Bustly")
    .replace(/zernio/gi, "Bustly");
};

const resolveConnectionOwnerKey = (connectedBy: string, members: MemberAccessColumn[]) => {
  const connectedByText = normalizeText(connectedBy);
  if (!connectedByText) return null;
  const exactMatch = members.find((member) =>
    [member.name, member.email, member.key].some((value) => normalizeText(value) === connectedByText),
  );
  if (exactMatch) return exactMatch.key;
  const fuzzyMatch = members.find((member) =>
    [member.name, member.email].some((value) => {
      const normalizedValue = normalizeText(value);
      return normalizedValue && connectedByText.includes(normalizedValue);
    }),
  );
  return fuzzyMatch?.key ?? null;
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const getReadableDisplayValue = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmedValue = value.trim();

  if (!trimmedValue || UUID_PATTERN.test(trimmedValue)) {
    return null;
  }

  return trimmedValue;
};

const getNonEmptyDisplayValue = (value: unknown): string | null => {
  if (value == null) {
    return null;
  }

  const text = String(value).trim();
  return text || null;
};

const FEISHU_INTERNAL_ACCOUNT_PATTERN = /^feishu:[^:]+:.+$/i;
const WORKSPACE_INTEGRATION_SLUG_PATTERN = /^wi_[0-9a-z]{10}$/i;

const getFeishuReadableDisplayValue = (value: unknown): string | null => {
  const text = getNonEmptyDisplayValue(value);
  if (!text || FEISHU_INTERNAL_ACCOUNT_PATTERN.test(text) || WORKSPACE_INTEGRATION_SLUG_PATTERN.test(text)) {
    return null;
  }

  return text;
};

const resolveConnectionUserDisplayName = (mapping?: Record<string, any>, profile: Record<string, any> = {}) => {
  const profileUser = profile.user || profile.connected_by || profile.created_by || {};
  const displayName = [
    mapping?.connected_by_name,
    mapping?.user_name,
    mapping?.created_by_name,
    mapping?.connectedByName,
    mapping?.userName,
    mapping?.createdByName,
    profile.connected_by_name,
    profile.user_name,
    profile.created_by_name,
    profile.name,
    profile.full_name,
    profileUser.name,
    profileUser.full_name,
    mapping?.authorized_account_email,
    mapping?.authorizedAccountEmail,
    mapping?.external_account_email,
    mapping?.externalAccountEmail,
    mapping?.connected_by_email,
    mapping?.user_email,
    mapping?.created_by_email,
    mapping?.email,
    profile.authorized_account_email,
    profile.authorizedAccountEmail,
    profile.external_account_email,
    profile.externalAccountEmail,
    profile.connected_by_email,
    profile.user_email,
    profile.created_by_email,
    profile.email,
    profileUser.authorized_account_email,
    profileUser.authorizedAccountEmail,
    profileUser.external_account_email,
    profileUser.externalAccountEmail,
    profileUser.email,
    mapping?.connected_by,
    mapping?.created_by,
    mapping?.user_id,
  ]
    .map(getReadableDisplayValue)
    .find(Boolean);

  return displayName || "Admin";
};

const resolveFeishuConnectionDisplayName = (
  profile: Record<string, any> = {},
  integration: Record<string, any> = {},
  mapping?: Record<string, any>,
) => {
  const integrationProfile = integration.profile_data || {};
  return (
    [
      mapping?.external_account_name,
      mapping?.externalAccountName,
      integration.external_account_name,
      integration.externalAccountName,
      mapping?.authorized_account_name,
      mapping?.authorizedAccountName,
      profile.authorized_account_name,
      profile.authorizedAccountName,
      profile.external_account_name,
      profile.externalAccountName,
      profile.accountName,
      profile.name,
      integrationProfile.authorized_account_name,
      integrationProfile.authorizedAccountName,
      integrationProfile.external_account_name,
      integrationProfile.externalAccountName,
      integrationProfile.accountName,
      integrationProfile.name,
      mapping?.account_name,
      mapping?.name,
      mapping?.label,
      integration.label,
    ]
      .map(getFeishuReadableDisplayValue)
      .find(Boolean) || ""
  );
};

const resolveGoogleAdsAuthorizedAccountValue = (
  profile: Record<string, any> = {},
  integration: Record<string, any> = {},
  mapping?: Record<string, any>,
) => {
  const integrationProfile = integration.profile_data || {};
  const workspaceExternalAccountName =
    getNonEmptyDisplayValue(mapping?.external_account_name) ||
    getNonEmptyDisplayValue(mapping?.externalAccountName) ||
    getNonEmptyDisplayValue(integration.external_account_name) ||
    getNonEmptyDisplayValue(integration.externalAccountName);

  if (workspaceExternalAccountName) {
    return workspaceExternalAccountName;
  }

  return (
    [
      mapping?.authorized_account_email,
      mapping?.authorizedAccountEmail,
      mapping?.external_account_email,
      mapping?.externalAccountEmail,
      mapping?.authorized_account_name,
      mapping?.authorizedAccountName,
      mapping?.external_account_name,
      mapping?.externalAccountName,
      profile.authorized_account_email,
      profile.authorizedAccountEmail,
      profile.external_account_email,
      profile.externalAccountEmail,
      profile.authorized_account_name,
      profile.authorizedAccountName,
      profile.external_account_name,
      profile.externalAccountName,
      profile.email,
      profile.accountEmail,
      profile.loginEmail,
      profile.accountName,
      integrationProfile.authorized_account_email,
      integrationProfile.authorizedAccountEmail,
      integrationProfile.external_account_email,
      integrationProfile.externalAccountEmail,
      integrationProfile.authorized_account_name,
      integrationProfile.authorizedAccountName,
      integrationProfile.external_account_name,
      integrationProfile.externalAccountName,
      integrationProfile.email,
      integrationProfile.accountEmail,
      integrationProfile.loginEmail,
      integrationProfile.accountName,
      integration.external_account_name,
      integration.externalAccountName,
      mapping?.authorized_account_id,
      mapping?.authorizedAccountId,
      mapping?.external_account_id,
      mapping?.externalAccountId,
      integration.external_account_id,
      integration.externalAccountId,
      mapping?.connection_id,
    ]
      .map(getNonEmptyDisplayValue)
      .find(Boolean) || ""
  );
};

const resolveConnectionOwnerKeyFromMapping = (
  mapping: Record<string, any> | undefined,
  connectedBy: string,
  members: MemberAccessColumn[],
  integrationOwnerId?: unknown,
) => {
  const explicitOwner = [
    mapping?.user_id,
    mapping?.connected_by_user_id,
    mapping?.created_by_user_id,
    mapping?.connectedByUserId,
    mapping?.createdByUserId,
    mapping?.connected_by_email,
    mapping?.created_by_email,
    mapping?.connectedByEmail,
    mapping?.createdByEmail,
    // The workspace-integrations response owns the canonical connection
    // owner. Mapping rows are provider-specific and may omit it entirely;
    // without this fallback an app owner is misclassified as an ordinary
    // member and the assignment picker is incorrectly narrowed to their own
    // groups (BUSTLY-2772).
    integrationOwnerId,
  ]
    .map(getNonEmptyDisplayValue)
    .find(Boolean);

  if (explicitOwner) {
    const explicitMatch = members.find((member) =>
      [member.key, member.userId, member.email].some((value) => normalizeText(value) === normalizeText(explicitOwner)),
    );

    if (explicitMatch) {
      return explicitMatch.key;
    }
  }

  return resolveConnectionOwnerKey(connectedBy, members);
};

const normalizeConnectionBrandIds = (...values: unknown[]) =>
  Array.from(
    new Set(
      values
        .flatMap((value) => (Array.isArray(value) ? value : [value]))
        .map((value) => String(value || "").trim())
        .filter(Boolean),
    ),
  );

const buildConnectionRows = (
  allIntegrations: WorkspaceIntegration[],
  members: MemberAccessColumn[],
  currentMemberKey: string | null,
) => {
  return allIntegrations.flatMap((integration) => {
    const provider = getProviderLabel(integration.provider);
    if (!integration.mappings?.length && !integration.connected) {
      return [];
    }

    return getIntegrationMappings(integration).map((rawMapping, index) => {
      const mapping = rawMapping as Record<string, any> | undefined;
      const profileData = (integration.profile_data ?? {}) as Record<string, any>;
      const integrationRecord = integration as unknown as Record<string, any>;
      const id = String(mapping?.id ?? `${normalizeProvider(integration.provider)}-${index}`);
      const workspaceConnectionStatus: ConnectionData["status"] = integration.connected ? "connected" : "disconnected";
      const normalizedConnectionStatus = normalizeConnectionStatusValue(mapping?.status, workspaceConnectionStatus);
      const availability =
        provider === "Klaviyo" ? resolveKlaviyoIntegrationAvailability(integration) : AVAILABLE_INTEGRATION_STATUS;
      const displayStatus = availability.isUnavailable
        ? availability.status
        : integration.connected && normalizedConnectionStatus === "disconnected"
          ? "connected"
          : normalizedConnectionStatus;
      const isShopifyConnection = provider === "Shopify";
      const isGoogleAdsConnection = provider === "Google Ads";
      const isBigCommerceConnection = provider === "BigCommerce";
      const isFeishuConnection = provider === "Feishu";
      const isDingTalkConnection = provider === "DingTalk";
      const googleAdsAuthorizedAccountValue = isGoogleAdsConnection
        ? resolveGoogleAdsAuthorizedAccountValue(profileData, integrationRecord, mapping)
        : "";
      const feishuAccountDisplayValue = isFeishuConnection
        ? resolveFeishuConnectionDisplayName(profileData, integrationRecord, mapping)
        : "";
      const dingtalkAccountDisplayValue = isDingTalkConnection
        ? profileData.corpName ||
          profileData.corp_name ||
          profileData.accountName ||
          profileData.nick ||
          mapping?.account_name ||
          mapping?.name ||
          "DingTalk Workspace"
        : "";
      const accountValue = isShopifyConnection
        ? resolveShopifyConnectionDomain({
            integration: integrationRecord,
            mapping,
          }) || mapping?.account_name || mapping?.connection_id || ""
        : isGoogleAdsConnection
          ? googleAdsAuthorizedAccountValue
          : isFeishuConnection
            ? feishuAccountDisplayValue
            : isDingTalkConnection
              ? dingtalkAccountDisplayValue
              : isBigCommerceConnection
                ? mapping?.name ||
                  mapping?.account_name ||
                  mapping?.account_id ||
                  profileData.accountName ||
                  profileData.name ||
                  mapping?.connection_id ||
                  ""
                : profileData.accountId ||
                  mapping?.account_name ||
                  mapping?.account_id ||
                  profileData.username ||
                  mapping?.connection_id ||
                  "";
      const connectionLabel = mapping?.label || "";
      const normalizedConnectionId =
        mapping?.external_connection_id ||
        mapping?.externalConnectionId ||
        mapping?.connection_id ||
        mapping?.nango_connection_id ||
        undefined;
      const connectedBy = resolveConnectionUserDisplayName(mapping, profileData);
      const ownerKey = resolveConnectionOwnerKeyFromMapping(
        mapping,
        connectedBy,
        members,
        integrationRecord.connectedBy,
      );
      const isConnectionOwnedByCurrentUser = !!currentMemberKey && ownerKey === currentMemberKey;
      const canManageAccess = Boolean(integrationRecord.canManageAccess ?? mapping?.canManageAccess);
      const currentUserAccess = resolveCurrentUserConnectionAccess(mapping, integrationRecord);
      const connectionBrandIds = normalizeConnectionBrandIds(
        mapping?.brand_id,
        mapping?.brandId,
        mapping?.brand_ids,
        mapping?.brandIds,
        integrationRecord.brand_id,
        integrationRecord.brandId,
        integrationRecord.brand_ids,
        integrationRecord.brandIds,
      );
      const subAccountBrandIds = isGoogleAdsConnection
        ? ((mapping?.sub_accounts || []) as Array<Record<string, unknown>>).reduce<Record<string, string[]>>(
            (brandIdsByCustomer, subAccount: Record<string, unknown>) => {
              const customerId = String(subAccount.customer_id || subAccount.account_id || "");
              if (customerId) {
                brandIdsByCustomer[customerId] = normalizeConnectionBrandIds(
                  subAccount.brand_id,
                  subAccount.brandId,
                  subAccount.brand_ids,
                  subAccount.brandIds,
                );
              }
              return brandIdsByCustomer;
            },
            {},
          )
        : {};
      const connectionMode = resolveMcpDisconnectConnectionMode({
        provider,
        connectionMode:
          mapping?.connectionMode ??
          mapping?.connection_mode ??
          integrationRecord.connectionMode ??
          integrationRecord.connection_mode,
        externalAccountType:
          mapping?.externalAccountType ??
          mapping?.external_account_type ??
          integrationRecord.externalAccountType ??
          integrationRecord.external_account_type,
        externalAccountId:
          mapping?.externalAccountId ??
          mapping?.external_account_id ??
          integrationRecord.externalAccountId ??
          integrationRecord.external_account_id,
      });

      return {
        id,
        connectionId: normalizedConnectionId,
        connectionBackend: mapping?.connection_backend ?? mapping?.connectionBackend ?? undefined,
        workspaceIntegrationId: mapping?.id == null ? undefined : String(mapping.id),
        slugId: mapping?.slugId || profileData.slugId,
        authType: String(mapping?.authType || integrationRecord.authType || ""),
        native: Boolean(mapping?.native ?? integrationRecord.native),
        connectionMode,
        provider,
        integrationType: provider,
        name: isGoogleAdsConnection
          ? googleAdsAuthorizedAccountValue
          : isFeishuConnection
            ? feishuAccountDisplayValue
            : isDingTalkConnection
              ? dingtalkAccountDisplayValue
              : profileData.connection_name ||
                profileData.accountName ||
                mapping?.name ||
                mapping?.account_name ||
                `${provider} Workspace`,
        accountId: String(accountValue || "-"),
        alias: String(connectionLabel || ""),
        status: displayStatus,
        statusLabel: availability.label,
        statusMessage: availability.message,
        isUnavailable: availability.isUnavailable,
        connectedAt: firstString(mapping?.created_at, integration.created_at, "Recent"),
        connectedBy,
        subAccounts: isGoogleAdsConnection
          ? (mapping?.sub_accounts || [])
              .map((subAccount: Record<string, unknown>) => ({
                accountName: String(
                  subAccount.account_name ||
                    subAccount.customer_id ||
                    subAccount.account_id ||
                    "Google Ads sub account",
                ),
                customerId: String(subAccount.customer_id || subAccount.account_id || ""),
                label: typeof subAccount.label === "string" ? subAccount.label : null,
              }))
              .filter((subAccount: { customerId: string }) => subAccount.customerId)
          : undefined,
        connectionOwnerKey: ownerKey,
        isConnectionOwnedByCurrentUser,
        currentUserAccess,
        projectAccessByResource: {},
        canManageAccess,
        isManagedByCurrentUser: canManageAccess,
        brandIds: connectionBrandIds,
        subAccountBrandIds,
      } satisfies ConnectionAccessRow;
    });
  });
};

type BrandScopedResource = IntegrationResource & {
  brandIds?: string[];
  brandAccessCeilings?: Record<string, Exclude<ProjectAssignmentCeiling, null | undefined>>;
  brandLabels?: Record<string, string>;
};

const isSecondaryProjectResource = (resourceType?: string | null) =>
  ['CUSTOMER', 'PROPERTY', 'SITE'].includes(String(resourceType || '').toUpperCase());

const shouldProjectConnectionAsRow = (
  connection: BrandConnection,
  brandId: string,
  assignedConnectionIds: Set<string>,
) => {
  const connectionId = normalizeConnectionIdentity(connection.id);
  const isAssigned = connection.brandIds.includes(brandId) || assignedConnectionIds.has(connectionId);
  if (!isAssigned) return false;

  // Google Ads / Analytics / Search Console are represented by their assigned
  // secondary resources. Inventory-only children must not hide a canonical
  // CONNECTION assignment (for example a Shopify SHOP inventory row).
  const hasAssignedSecondaryResources = hasAssignedCanonicalProjectSecondaryResource(
    connection.provider,
    connection.resources,
    brandId,
  );
  return !hasAssignedSecondaryResources;
};

const buildAssignedProjectConnectionRow = (
  connection: BrandConnection,
  members: MemberAccessColumn[],
  currentMemberKey: string | null,
): ConnectionAccessRow => {
  const provider = getProviderLabel(connection.provider);
  const connectionOwnerKey = resolveConnectionOwnerKey(connection.connectedByUserId, members);
  const connectionOwner = members.find((member) => member.key === connectionOwnerKey);
  const connectedBy =
    connectionOwner?.name ||
    connectionOwner?.email ||
    getReadableDisplayValue(connection.connectedByUserId) ||
    "";

  return {
    id: String(connection.id),
    connectionId: String(connection.id),
    workspaceIntegrationId: String(connection.id),
    authType: "",
    native: false,
    connectionMode: "INTERNAL_APP",
    provider,
    integrationType: provider,
    name: firstString(connection.accountName, connection.accountDetail, `${provider} Workspace`),
    accountId: firstString(connection.accountDetail, connection.accountName, "-"),
    alias: "",
    status: connection.status === "connected" ? "connected" : "disconnected",
    statusLabel: connection.status,
    connectedAt: connection.connectedAt,
    connectedBy,
    connectionOwnerKey,
    isConnectionOwnedByCurrentUser: Boolean(currentMemberKey && connectionOwnerKey === currentMemberKey),
    // The Project contract and persisted Project grant resolve the effective
    // access below. A display-only fallback must never invent Workspace use.
    currentUserAccess: "no_access",
    projectAccessByResource: {},
    isManagedByCurrentUser: false,
    canManageAccess: connection.canManageAccess === true,
    brandIds: connection.brandIds,
    subAccountBrandIds: {},
  };
};

const getProjectResourceGroupKey = (resource: BrandScopedResource) => {
  const provider = getProviderLabel(resource.provider);
  const resourceIdentity = `${resource.connectionId ?? "none"}:${resource.provider}:${resource.resourceType}:${resource.resourceKey}`;
  if (!isCanonicalProjectSecondaryResource(resource.provider, resource.resourceType)) {
    return `resource:${resourceIdentity}`;
  }

  const parentIdentity = resource.connectionId ?? resource.sourceMappingId;
  return parentIdentity == null ? `resource:${resourceIdentity}` : `secondary-resource:${provider}:${parentIdentity}`;
};

const buildBrandResourceRows = (
  resources: BrandScopedResource[],
  members: MemberAccessColumn[],
  currentMemberKey: string | null,
  projectOwnerKey: string | null,
  brandId: string | null,
  connections: BrandConnection[],
  projectResourceGrants: ProjectUserResourceGrant[],
): ConnectionAccessRow[] => {
  const groupedResources = new Map<string, BrandScopedResource[]>();
  getCanonicalProjectSecondaryResources(resources)
    .filter((resource) => !brandId || (resource.brandIds || []).includes(brandId))
    .forEach((resource) => {
      const groupKey = getProjectResourceGroupKey(resource);
      groupedResources.set(groupKey, [...(groupedResources.get(groupKey) || []), resource]);
    });

  return [...groupedResources.values()].map((resources) => {
    const resource = resources[0];
    const provider = getProviderLabel(resource.provider);
    const isGoogleAdsConnection = provider === "Google Ads";
    const isSecondaryResourceConnection = isCanonicalProjectSecondaryResource(
      resource.provider,
      resource.resourceType,
    );
    const resourceKey = String(resource.resourceKey);
    const rowId = isGoogleAdsConnection && resource.connectionId != null
      ? String(resource.connectionId)
      : resource.sourceMappingId == null
      ? `${resource.connectionId ?? resource.provider}:${resource.resourceType}:${resourceKey}`
      : String(resource.sourceMappingId);
    const brandIds = [...new Set(resources.flatMap((resource) => resource.brandIds || []))];
    const parentConnection = connections.find(
      (connection) => normalizeConnectionIdentity(connection.id) === normalizeConnectionIdentity(resource.connectionId),
    );
    const connectedBy = parentConnection?.connectedByUserId || resource.connectedBy || "";
    const connectionOwnerKey = resolveConnectionOwnerKey(connectedBy, members);
    const connectionOwner = members.find((member) => member.key === connectionOwnerKey);
    const connectedByDisplayName =
      connectionOwner?.name || connectionOwner?.email || getReadableDisplayValue(connectedBy) || "";
    const connectionName = parentConnection?.accountName || resource.name || resourceKey;
    const connectionAccountId = parentConnection?.accountName || resourceKey;
    const projectAccessByResource = new Map(
      resources.map((candidate) => [
        getProjectResourceIdentityKey(candidate),
        (() => {
          const persistedAccess = resolveProjectResourceAccess(projectResourceGrants, currentMemberKey, candidate);
          const isProjectOwner = Boolean(
            currentMemberKey && projectOwnerKey && normalizeText(currentMemberKey) === normalizeText(projectOwnerKey),
          );
          const ownerAccess = isProjectOwner
            ? resolveProjectOwnerAccess(
                candidate.brandAccessCeilings?.[brandId || ""],
                Boolean(currentMemberKey && connectionOwnerKey === currentMemberKey),
              )
            : "no_access";
          return resolveProjectResourceContractAccess({
            isUsableInProjectContract: candidate.canUseResource === true,
            ownerAccess,
            persistedAccess,
          });
        })(),
      ]),
    );
    const projectAccessLevels = [...projectAccessByResource.values()];
    const currentUserAccess = projectAccessLevels.includes("action")
      ? "action"
      : projectAccessLevels.includes("read")
        ? "read"
        : "no_access";

    return {
      id: rowId,
      connectionId: resource.connectionId == null ? undefined : String(resource.connectionId),
      workspaceIntegrationId: resource.sourceMappingId == null
        ? resource.connectionId == null
          ? undefined
          : String(resource.connectionId)
        : String(resource.sourceMappingId),
      authType: "",
      native: false,
      connectionMode: "INTERNAL_APP",
      provider,
      integrationType: provider,
      name: connectionName,
      accountId: connectionAccountId,
      alias: isGoogleAdsConnection ? "" : brandId ? resource.brandLabels?.[brandId] || "" : "",
      status: String(resource.status).toUpperCase() === "ACTIVE" ? "connected" : "disconnected",
      statusLabel: String(resource.status),
      connectedAt: "",
      connectedBy: connectedByDisplayName,
      subAccounts: isGoogleAdsConnection
        ? resources.map((resource) => ({
            accountName: resource.name || resource.resourceKey,
            customerId: String(resource.resourceKey),
            label: brandId ? resource.brandLabels?.[brandId] || null : null,
            projectAccess: projectAccessByResource.get(getProjectResourceIdentityKey(resource)) || "no_access",
          }))
        : undefined,
      resourceType: isGoogleAdsConnection ? undefined : resource.resourceType,
      resourceKey: isGoogleAdsConnection || isSecondaryResourceConnection ? undefined : resource.resourceKey,
      isProjectResourceRow: true,
      connectionOwnerKey,
      isConnectionOwnedByCurrentUser: Boolean(currentMemberKey && connectionOwnerKey === currentMemberKey),
      currentUserAccess,
      projectAccessByResource: Object.fromEntries(projectAccessByResource.entries()),
      isManagedByCurrentUser: false,
      canManageAccess: resources.some((resource) => resource.canManageResourceAccess === true),
      brandIds,
      subAccountBrandIds: isGoogleAdsConnection
        ? Object.fromEntries(resources.map((resource) => [String(resource.resourceKey), [...new Set(resource.brandIds || [])]]))
        : {},
    };
  });
};

const buildMemberColumns = (members: WorkspaceMemberDisplay[], currentUserId?: string | null) =>
  members
    .map<MemberAccessColumn | null>((member) => {
      const key = getMemberKey(member);
      if (!key) return null;
      const role = getMemberRole(member);
      return {
        key,
        userId: firstString(member.userId, member.id),
        name: firstString(member.displayName, member.name, member.email, "Member"),
        email: firstString(member.email),
        role,
        avatar: member.avatar,
        isCurrentUser: Boolean(member.isCurrentUser) || member.userId === currentUserId || member.id === currentUserId,
        isWorkspaceOwner:
          isWorkspaceOwnerRole(member.membershipRole) ||
          isWorkspaceOwnerRole(member.workspaceRole) ||
          isWorkspaceOwnerRole(role),
      };
    })
    .filter((member): member is MemberAccessColumn => Boolean(member));

const getColon = (locale: string) => (locale?.startsWith("zh") ? "：" : ":");

const formatConnectionTimestamp = (locale: string, connectedAt: string) => {
  if (!connectedAt || connectedAt === "Recent") {
    return null;
  }

  const parsedDate = new Date(connectedAt);
  if (Number.isNaN(parsedDate.getTime())) {
    return connectedAt;
  }

  const includesExplicitTime = connectedAt.includes("T") || /\d:\d{2}/.test(connectedAt);
  return new Intl.DateTimeFormat(
    locale === "zh-CN" ? "zh-CN" : "en-US",
    includesExplicitTime
      ? {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: false,
        }
      : {
          month: "short",
          day: "numeric",
          year: "numeric",
        },
  ).format(parsedDate);
};

const formatConnectionMetaLine = (
  t: (key: string, values?: Record<string, string>) => string,
  locale: string,
  connection: Pick<ConnectionAccessRow, "connectedAt" | "connectedBy">,
) => {
  const formattedTimestamp = formatConnectionTimestamp(locale, connection.connectedAt);

  if (!formattedTimestamp) {
    return connection.connectedBy
      ? t("website.integrationsSettings.connectedRecentlyBy", {
          user: connection.connectedBy,
        })
      : t("website.integrationsSettings.connectedRecently");
  }

  return connection.connectedBy
    ? t("website.integrationsSettings.connectedByOn", {
        user: connection.connectedBy,
        date: formattedTimestamp,
      })
    : t("website.integrationsSettings.connectedOn", {
        date: formattedTimestamp,
      });
};

const getConnectionOpenTimestamp = (row: Pick<ConnectionAccessRow, "connectedAt">) => {
  if (!row.connectedAt || row.connectedAt === "Recent") {
    return Number.MAX_SAFE_INTEGER;
  }

  const parsedTimestamp = new Date(row.connectedAt).getTime();
  return Number.isNaN(parsedTimestamp) ? 0 : parsedTimestamp;
};

const findRequestedAccessRow = (rows: ConnectionAccessRow[], provider: string | null) => {
  const providerId = normalizeProvider(provider);
  if (!providerId) {
    return null;
  }

  return (
    [...rows]
      .filter(
        (row) =>
          getConnectionGroupKey(row.provider) === providerId &&
          row.isManagedByCurrentUser &&
          isIntegrationConnectionActive(row),
      )
      .sort((left, right) => getConnectionOpenTimestamp(right) - getConnectionOpenTimestamp(left))[0] ?? null
  );
};

export type ConnectionAccessFilter = "all" | "owned_by_me";
const CONNECTION_ACCESS_FILTERS: ConnectionAccessFilter[] = ["all", "owned_by_me"];
type ConnectionAccessViewState = "loading" | "empty" | "ready";

const getConnectionAccessFilterLabelKey = (filter: ConnectionAccessFilter, brandScoped: boolean) =>
  brandScoped
    ? filter === "owned_by_me"
      ? "website.integrationsSettings.onlyMyConnections"
      : "website.integrationsSettings.allConnectors"
    : filter === "owned_by_me"
      ? "website.integrationsSettings.ownedByMe"
      : "website.integrationsSettings.allConnections";

interface ConnectionScopeFilterProps {
  value: ConnectionAccessFilter;
  onChange: (value: ConnectionAccessFilter) => void;
  t: (key: string) => string;
  brandScoped?: boolean;
}

export const ConnectionScopeFilter = ({ value, onChange, t, brandScoped = false }: ConnectionScopeFilterProps) => (
  <div className="flex justify-end">
    <div
      role="group"
      aria-label={t("website.integrationsSettings.connectionOwnerFilter")}
      className="inline-flex items-center rounded-full bg-[#F4F5F8] p-1"
    >
      {CONNECTION_ACCESS_FILTERS.map((scope) => {
        const isSelected = scope === value;

        return (
          <button
            key={scope}
            type="button"
            aria-pressed={isSelected}
            onClick={() => onChange(scope)}
            className={clsx(
              "inline-flex h-7 items-center rounded-full px-3 text-xs font-medium outline-none transition-[background-color,color,box-shadow] duration-200 focus-visible:ring-2 focus-visible:ring-[#1A162F]/20",
              isSelected
                ? "bg-white text-[#1A162F] shadow-[0_1px_2px_rgba(26,22,47,0.08)]"
                : "text-[#666F8D] hover:text-[#1A162F]",
            )}
          >
            {t(getConnectionAccessFilterLabelKey(scope, brandScoped))}
          </button>
        );
      })}
    </div>
  </div>
);

const getConnectionSearchText = (row: ConnectionAccessRow) =>
  [
    row.provider,
    row.integrationType,
    row.name,
    row.accountId,
    row.alias,
    row.connectedBy,
    row.status,
    row.statusLabel,
    row.connectedAt,
    ...(row.subAccounts ?? []).flatMap((subAccount) => [
      subAccount.accountName,
      subAccount.customerId,
      subAccount.label,
    ]),
  ]
    .map(normalizeText)
    .join(" ");

const filterConnectionAccessRows = (
  rows: ConnectionAccessRow[],
  filter: ConnectionAccessFilter,
  searchQuery: string,
) => {
  const normalizedQuery = normalizeText(searchQuery);
  const filterRows = filter === "owned_by_me" ? getOwnedConnectionRows(rows) : rows;

  return filterRows.filter((row) => {
    if (!normalizedQuery) {
      return true;
    }

    return getConnectionSearchText(row).includes(normalizedQuery);
  });
};

const getAssignableMembersForRow = (members: MemberAccessColumn[], row: ConnectionAccessRow) => {
  return [...members].sort((a, b) => {
    const aIsOwner = a.isWorkspaceOwner || isWorkspaceOwnerRole(a.role) || a.key === row.connectionOwnerKey;
    const bIsOwner = b.isWorkspaceOwner || isWorkspaceOwnerRole(b.role) || b.key === row.connectionOwnerKey;
    if (aIsOwner && !bIsOwner) return -1;
    if (!aIsOwner && bIsOwner) return 1;
    return 0;
  });
};

const getConnectionDisconnectTrackingId = (row: ConnectionAccessRow) =>
  row.provider === "Feishu" || row.provider === "DingTalk"
    ? row.slugId || row.connectionId || row.id
    : row.connectionId || row.id;

const getGoogleAdsCustomerDisconnectOperationKey = (row: Pick<ConnectionAccessRow, "id">, customerId: string) =>
  `${row.id}:${customerId}`;

type GoogleResourceDisconnectProvider = "google-ads" | "google-analytics" | "google-search-console";

const getGoogleResourceDisconnectProvider = (provider: string): GoogleResourceDisconnectProvider | null => {
  const normalizedProvider = normalizeProvider(provider);
  return normalizedProvider === "google-ads" || normalizedProvider === "google-analytics" || normalizedProvider === "google-search-console"
    ? normalizedProvider
    : null;
};

const getGoogleResourceDisconnectOperationKey = (row: ConnectionAccessRow, resourceKey: string) =>
  `${row.id}:${resourceKey}`;

const getDisconnectPlatformValue = (provider: string) => provider.toLowerCase().replace(/\s+/g, "_");

// ─── Status Text ────────────────────────────────────────────────────────────

const getConnectionStatusTextClassName = (status: ConnectionData["status"]) => {
  if (isIntegrationConnectionActive({ status })) {
    return "text-emerald-700";
  }
  if (status === "error") {
    return "text-red-700";
  }
  if (status === "disabled" || status === "suspended" || status === "trial_expired") {
    return "text-amber-700";
  }
  return "text-slate-500";
};

const ConnectionStatusText = ({
  status,
  statusLabel,
  isHighlighted = false,
}: Pick<ConnectionAccessRow, "status" | "statusLabel"> & { isHighlighted?: boolean }) => {
  const { t } = useTranslation();
  const statusMeta = getIntegrationConnectionStatusMeta({
    status,
    statusLabel,
  });
  const localizedStatusLabel = isIntegrationConnectionActive({ status })
    ? t("website.integrationsSettings.connected")
    : statusMeta.label;
  return (
    <span
      className={clsx(
        "inline-flex max-w-full items-center gap-1.5 text-xs font-medium",
        getConnectionStatusTextClassName(status),
        isHighlighted && "connection-status-highlight",
      )}
    >
      <span className={clsx("h-1.5 w-1.5 shrink-0 rounded-full", statusMeta.dotClassName)} />
      <span className="truncate">{localizedStatusLabel}</span>
    </span>
  );
};

const useIsTextTruncated = (textRef: { current: HTMLElement | null }, text: string) => {
  const [isTruncated, setIsTruncated] = useState(false);

  const updateTruncationState = useCallback(() => {
    const element = textRef.current;
    if (!element) {
      setIsTruncated(false);
      return false;
    }

    const nextIsTruncated = element.scrollWidth > element.clientWidth + 1;
    setIsTruncated((previous) => (previous === nextIsTruncated ? previous : nextIsTruncated));
    return nextIsTruncated;
  }, [textRef]);

  useEffect(() => {
    const element = textRef.current;
    if (!element) {
      setIsTruncated(false);
      return;
    }

    updateTruncationState();
    const frameId = window.requestAnimationFrame(updateTruncationState);
    void document.fonts?.ready.then(updateTruncationState);

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateTruncationState);
      observer.observe(element);
      if (element.parentElement) {
        observer.observe(element.parentElement);
      }
      return () => {
        window.cancelAnimationFrame(frameId);
        observer.disconnect();
      };
    }

    window.addEventListener("resize", updateTruncationState);
    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", updateTruncationState);
    };
  }, [text, textRef, updateTruncationState]);

  return { isTruncated, updateTruncationState };
};

const TruncatedTooltipText = ({
  text,
  className,
  tooltipContent,
  tooltipClassName,
}: {
  text: string;
  className?: string;
  tooltipContent?: ReactNode;
  tooltipClassName?: string;
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const { isTruncated, updateTruncationState } = useIsTextTruncated(textRef, text);
  const shouldShowTooltip = Boolean(tooltipContent) || isTruncated;
  const textNode = (
    <span
      ref={textRef}
      onMouseEnter={updateTruncationState}
      className={clsx(className, shouldShowTooltip && "cursor-help")}
    >
      {text}
    </span>
  );

  return (
    <Tooltip
      content={shouldShowTooltip ? tooltipContent || text : undefined}
      contentClassName={tooltipClassName}
      side="top"
      align="start"
    >
      {textNode}
    </Tooltip>
  );
};

const HoverEditLabel = ({
  text,
  editLabelText,
  editLabelTooltip,
  onEdit,
}: {
  text: string;
  editLabelText: string;
  editLabelTooltip: string;
  onEdit?: () => void;
}) => (
  <div className="flex min-w-0 max-w-full items-center gap-0.5">
    <TruncatedTooltipText
      text={text}
      className="block min-w-0 flex-1 truncate text-xs text-[#3D394D]"
    />
    {onEdit ? (
      <Tooltip content={editLabelTooltip || editLabelText} side="top">
        <button
          type="button"
          onClick={onEdit}
          aria-label={editLabelText}
          className="pointer-events-none inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-[#8A93B2] opacity-0 outline-none transition-[color,background-color,opacity] duration-200 group-hover/label:pointer-events-auto group-hover/label:opacity-100 group-focus-within/label:pointer-events-auto group-focus-within/label:opacity-100 hover:bg-[#F4F5F8] hover:text-[#1A162F] focus-visible:pointer-events-auto focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-[#1A162F]/20"
        >
          <PencilSimple size={14} weight="regular" />
        </button>
      </Tooltip>
    ) : null}
  </div>
);

const hasSameBrandAssignmentIds = (currentBrandIds: string[], nextBrandIds: string[]) => {
  const currentIds = new Set(currentBrandIds);
  const nextIds = new Set(nextBrandIds);

  return currentIds.size === nextIds.size && [...currentIds].every((brandId) => nextIds.has(brandId));
};

const getConnectionLabelOverrideKey = (connectionRowId: string, resourceKey?: string | null) =>
  resourceKey ? `${connectionRowId}::resource::${resourceKey}` : connectionRowId;

type BrandAssignmentUnit = {
  key: string;
  row: ConnectionAccessRow;
  subAccount?: GoogleAdsCustomerSubAccount;
  resourceKey?: string;
  name: string;
  detail: string;
};

const getBrandAssignmentUnitResourceKey = (unit: BrandAssignmentUnit) =>
  unit.resourceKey || unit.subAccount?.customerId;

interface ConnectionAccessSettingsProps {
  isRefreshingConnections?: boolean;
  showHeader?: boolean;
  showScopeSwitcher?: boolean;
  connectionFilter?: ConnectionAccessFilter;
  onConnectionFilterChange?: (filter: ConnectionAccessFilter) => void;
  /** Reports active connection counts so the parent filter rail matches this list. */
  onConnectionScopeCountsChange?: (counts: ConnectionScopeCounts) => void;
  onBrowseIntegrations?: () => void;
  searchQuery?: string;
  providerFilter?: string | null;
  usePluginWorkspaceContext?: boolean;
  highlightedConnectionKey?: string | null;
  highlightedConnectionProvider?: string | null;
  onHighlightComplete?: () => void;
  requestedAccessProvider?: string | null;
  onAccessRequestComplete?: () => void;
  onConnectionDisconnected?: () => void | Promise<void>;
  connectionScene?: ConnectionScene;
  accessRefreshKey?: number;
  brandId?: string | null;
  brandConnectionIds?: string[];
  brandResources?: BrandScopedResource[];
  projectBootstrap?: ProjectIntegrationsBootstrapResponse | null;
  canManageBrand?: boolean;
  canRemoveConnectionsFromBrand?: boolean;
  /** Child resources currently waiting for a post-authorization Business-group assignment. */
  pendingBusinessGroupAssignmentResourceKeys?: ReadonlySet<string>;
  /** DOM slot used by the Workspace integrations header for the bulk assignment action. */
  brandAssignmentActionTargetId?: string;
  /** Opens member access for one exact Business-group application account. */
  onSetMemberAccess?: (target: BusinessGroupMemberAccessTarget) => void;
  /** Disables an action that cannot target an assignable member. */
  canSetMemberAccess?: (target: BusinessGroupMemberAccessTarget) => boolean;
  /** Explains why a visible member access action is disabled. */
  getSetMemberAccessDisabledReason?: (target: BusinessGroupMemberAccessTarget) => string | undefined;
}

// ─── Main Component ─────────────────────────────────────────────────────────

export const MEMBERS_PER_PAGE = 5;
const MAX_VISIBLE_PAGE_BUTTONS = 5;

export const ConnectionAccessSettings = ({
  isRefreshingConnections = false,
  showHeader = true,
  showScopeSwitcher = false,
  connectionFilter = "all",
  onConnectionFilterChange,
  onConnectionScopeCountsChange,
  onBrowseIntegrations,
  searchQuery = "",
  providerFilter = null,
  usePluginWorkspaceContext = false,
  highlightedConnectionKey = null,
  highlightedConnectionProvider = null,
  onHighlightComplete,
  requestedAccessProvider = null,
  onAccessRequestComplete,
  onConnectionDisconnected,
  connectionScene = "admin",
  accessRefreshKey = 0,
  brandId = null,
  brandConnectionIds,
  brandResources,
  projectBootstrap = null,
  canManageBrand = false,
  canRemoveConnectionsFromBrand = false,
  pendingBusinessGroupAssignmentResourceKeys,
  brandAssignmentActionTargetId,
  onSetMemberAccess,
  canSetMemberAccess,
  getSetMemberAccessDisabledReason,
}: ConnectionAccessSettingsProps) => {
  const { t, locale } = useTranslation();
  const metadataValueGapClass = locale?.startsWith("zh") ? "gap-1" : "gap-[3px]";
  const toast = useToast();
  const { showDialog } = useDialog();
  const { user, currentWorkspace, integrations, workspaceMembers } = useAuth();
  const {
    data: brandGroupData,
    assignIntegrationToBrand,
    removeIntegrationFromBrand,
    syncResourceAssignmentsToBrand,
    removeResourceFromBrand,
    refresh: refreshBrandGroup,
  } = useBrandGroup();
  const activeBrand = brandId
    ? brandGroupData.brands.find((brand) => brand.id === brandId)
    : undefined;
  const activeBrandName = activeBrand ? getBusinessGroupDisplayName(activeBrand) : "";
  const isPluginAdmin = usePluginWorkspaceContext || isPluginProduct(getCurrentProductCode());
  const brandManagementEnabled = getAdminProductFeatures().brandManagement;
  const showBrandAssignmentControls = brandManagementEnabled && !isPluginAdmin && !brandId;
  const showBusinessGroupMetadata = brandManagementEnabled && !isPluginAdmin && !brandId;
  const showBusinessGroupMemberAccessActions = Boolean(
    brandId && onSetMemberAccess,
  );
  const businessGroupApplicationGridClass =
    "w-full min-w-0 md:grid-cols-[minmax(0,1fr)_180px_180px_188px]";
  const workspaceAccountTableGridClass = brandId
    ? businessGroupApplicationGridClass
    : isPluginAdmin
      ? "w-full min-w-0 md:grid-cols-[minmax(0,1.4fr)_minmax(0,0.7fr)_minmax(0,0.8fr)_220px]"
      : "w-full min-w-0 md:grid-cols-[minmax(0,1.4fr)_minmax(0,0.7fr)_minmax(0,0.9fr)_220px]";
  const workspaceAccountTableHorizontalPaddingClass = brandId ? "pl-4 pr-0" : "px-4";
  const workspaceAccountActionHeaderAlignment = brandId || isPluginAdmin ? "text-left" : "text-right";
  const workspaceAccountActionAlignment = brandId || isPluginAdmin ? "md:justify-start" : "md:justify-end";
  const [connectionAccessById, setConnectionAccessById] = useState<
    Record<string, Record<string, ConnectionAccessLevel>>
  >({});
  const [connectionManageAccessById, setConnectionManageAccessById] = useState<Record<string, Record<string, boolean>>>(
    {},
  );
  const [googleAdsCustomerAccessById, setGoogleAdsCustomerAccessById] = useState<
    Record<string, Record<string, string[]>>
  >({});
  const [googleAdsCustomerAccessLevelsById, setGoogleAdsCustomerAccessLevelsById] = useState<
    Record<string, Record<string, Record<string, ConnectionAccessLevel>>>
  >({});
  const [loadingAccessConnectionIds, setLoadingAccessConnectionIds] = useState<Set<string>>(new Set());
  const [savingAccessConnectionIds, setSavingAccessConnectionIds] = useState<Set<string>>(new Set());
  const [connectionAccessErrors, setConnectionAccessErrors] = useState<Record<string, string>>({});
  const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null);
  const lastHighlightRequestRef = useRef<string | null>(null);

  const [modalAccessDraft, setModalAccessDraft] = useState<Record<string, ConnectionAccessLevel>>({});
  const [modalManageAccessDraft, setModalManageAccessDraft] = useState<Record<string, boolean>>({});
  const [modalGoogleAdsCustomerDraft, setModalGoogleAdsCustomerDraft] = useState<Record<string, string[]>>({});
  const [activeAccessConnectionId, setActiveAccessConnectionId] = useState<string | null>(null);
  const [activeAccessCustomerId, setActiveAccessCustomerId] = useState<string | null>(null);
  const [connectionAliasOverrides, setConnectionAliasOverrides] = useState<Record<string, string>>({});
  const [editingLabelConnection, setEditingLabelConnection] = useState<ConnectionAccessRow | null>(null);
  const [editingLabelResource, setEditingLabelResource] = useState<WorkspaceResourceLabelTarget | null>(null);
  const [editingLabelValue, setEditingLabelValue] = useState("");
  const [isSavingConnectionLabel, setIsSavingConnectionLabel] = useState(false);
  const [editingBrandConnection, setEditingBrandConnection] = useState<ConnectionAccessRow | null>(null);
  const [editingBrandIds, setEditingBrandIds] = useState<string[]>([]);
  const [editingBrandMaxAccessLevels, setEditingBrandMaxAccessLevels] = useState<Record<string, 'READ' | 'WRITE'>>({});
  const [isSavingBrandAssignments, setIsSavingBrandAssignments] = useState(false);
  // State disables the controls after React renders; this ref also blocks
  // repeated clicks that arrive before that render completes.
  const brandAssignmentSaveInFlightRef = useRef(false);
  const [isBulkBrandAssignmentOpen, setIsBulkBrandAssignmentOpen] = useState(false);
  const [bulkBrandAssignmentUnitKeys, setBulkBrandAssignmentUnitKeys] = useState<string[]>([]);
  const [bulkBrandAssignmentBrandId, setBulkBrandAssignmentBrandId] = useState<string | null>(null);
  const [bulkBrandAssignmentAccessLevels, setBulkBrandAssignmentAccessLevels] =
    useState<Record<string, 'READ' | 'WRITE'>>({});
  const [brandAssignmentActionTarget, setBrandAssignmentActionTarget] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!brandAssignmentActionTargetId) {
      setBrandAssignmentActionTarget(null);
      return;
    }

    setBrandAssignmentActionTarget(document.getElementById(brandAssignmentActionTargetId));
  }, [brandAssignmentActionTargetId]);
  const [editingGoogleAdsCustomerBrand, setEditingGoogleAdsCustomerBrand] = useState<{
    connection: ConnectionAccessRow;
    subAccount: GoogleAdsCustomerSubAccount;
  } | null>(null);
  const [editingGoogleAdsCustomerBrandIds, setEditingGoogleAdsCustomerBrandIds] = useState<string[]>([]);
  const [editingGoogleAdsCustomerBrandMaxAccessLevels, setEditingGoogleAdsCustomerBrandMaxAccessLevels] =
    useState<Record<string, 'READ' | 'WRITE'>>({});
  const [editingSecondaryResourceBrand, setEditingSecondaryResourceBrand] = useState<{
    connection: ConnectionAccessRow;
    resource: BrandScopedResource;
  } | null>(null);
  const [editingSecondaryResourceBrandIds, setEditingSecondaryResourceBrandIds] = useState<string[]>([]);
  const [editingSecondaryResourceBrandMaxAccessLevels, setEditingSecondaryResourceBrandMaxAccessLevels] =
    useState<Record<string, 'READ' | 'WRITE'>>({});
  const [membersPage, setMembersPage] = useState(1);
  const [disconnectingConnectionIds, setDisconnectingConnectionIds] = useState<Set<string>>(new Set());
  const [disconnectingGoogleAdsCustomerKeys, setDisconnectingGoogleAdsCustomerKeys] = useState<Set<string>>(new Set());
  const [disconnectingGoogleResourceKeys, setDisconnectingGoogleResourceKeys] = useState<Set<string>>(new Set());
  const [projectResourceGrants, setProjectResourceGrants] = useState<ProjectUserResourceGrant[]>([]);
  const [isProjectResourceGrantsLoading, setIsProjectResourceGrantsLoading] = useState(false);
  const projectIntegrationContractKey = brandId && currentWorkspace?.id
    ? `${currentWorkspace.id}:${brandId}:${accessRefreshKey}`
    : null;
  const [projectIntegrationContractState, setProjectIntegrationContractState] = useState<{
    key: string;
    contract: IntegrationContractResult | null;
  } | null>(null);
  const [isProjectIntegrationContractRequestLoading, setIsProjectIntegrationContractRequestLoading] = useState(false);
  const projectIntegrationContract = projectIntegrationContractState?.key === projectIntegrationContractKey
    ? projectIntegrationContractState.contract
    : null;
  const isProjectIntegrationContractLoading = Boolean(projectIntegrationContractKey) && (
    isProjectIntegrationContractRequestLoading ||
    projectIntegrationContractState?.key !== projectIntegrationContractKey
  );
  const hasProjectIntegrationContractError = Boolean(
    projectIntegrationContractKey &&
    !isProjectIntegrationContractLoading &&
    projectIntegrationContractState?.key === projectIntegrationContractKey &&
    projectIntegrationContractState.contract === null,
  );

  useEffect(() => {
    setConnectionAccessById({});
    setConnectionManageAccessById({});
    setGoogleAdsCustomerAccessById({});
    setGoogleAdsCustomerAccessLevelsById({});
    setConnectionAccessErrors({});
  }, [accessRefreshKey]);

  useEffect(() => {
    if (!brandId || !currentWorkspace?.id) {
      setProjectResourceGrants([]);
      setIsProjectResourceGrantsLoading(false);
      return;
    }
    if (projectBootstrap) {
      setProjectResourceGrants(projectBootstrap.resourceGrants || []);
      setIsProjectResourceGrantsLoading(false);
      return;
    }
    let cancelled = false;
    setIsProjectResourceGrantsLoading(true);
    void projectService
      .listProjectResourceGrants(currentWorkspace.id, brandId)
      .then((grants) => {
        if (!cancelled) setProjectResourceGrants(grants || []);
      })
      .catch(() => {
        // A failed Project grant read must fail closed in the permission badge.
        if (!cancelled) setProjectResourceGrants([]);
      })
      .finally(() => {
        if (!cancelled) setIsProjectResourceGrantsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    accessRefreshKey,
    brandId,
    currentWorkspace?.id,
    projectBootstrap,
  ]);

  useEffect(() => {
    if (!brandId || !currentWorkspace?.id || !projectIntegrationContractKey) {
      setIsProjectIntegrationContractRequestLoading(false);
      return;
    }
    if (projectBootstrap) {
      setProjectIntegrationContractState({
        key: projectIntegrationContractKey,
        // A partial contract is a valid fail-closed state: keep rendering the
        // assigned connection, but do not mistake incomplete configuration
        // (for example, a Google Ads connection without a selected account)
        // for a request failure.
        contract: projectBootstrap.resourceContract,
      });
      setIsProjectIntegrationContractRequestLoading(false);
      return;
    }
    let cancelled = false;
    setIsProjectIntegrationContractRequestLoading(true);
    void projectService
      .getProjectIntegrationContract(currentWorkspace.id, brandId)
      .then((contract) => {
        if (!cancelled) {
          setProjectIntegrationContractState({
            key: projectIntegrationContractKey,
            contract,
          });
        }
      })
      .catch(() => {
        // Preserve the assigned rows, but fail their access state closed when
        // the Project authorization contract is unavailable.
        if (!cancelled) {
          setProjectIntegrationContractState({
            key: projectIntegrationContractKey,
            contract: null,
          });
        }
      })
      .finally(() => {
        if (!cancelled) setIsProjectIntegrationContractRequestLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    accessRefreshKey,
    brandId,
    currentWorkspace?.id,
    projectBootstrap,
    projectIntegrationContractKey,
  ]);
  const disconnectOperationKeysRef = useRef<Set<string>>(new Set());
  const googleAdsCustomerDisconnectOperationKeysRef = useRef<Set<string>>(new Set());
  const googleResourceDisconnectOperationKeysRef = useRef<Set<string>>(new Set());

  const members = useMemo(
    () => buildMemberColumns(workspaceMembers.members ?? [], user?.id),
    [user?.id, workspaceMembers.members],
  );
  const currentMemberKey = useMemo(() => members.find((member) => member.isCurrentUser)?.key ?? null, [members]);
  const isCurrentMemberWorkspaceOwner = useMemo(
    () => members.some((member) => member.isCurrentUser && member.isWorkspaceOwner),
    [members],
  );
  const manageableBusinessGroups = useMemo(
    () => getManageableBusinessGroups(
      brandGroupData.assignmentBrands,
      [currentMemberKey, user?.id, user?.email].filter((value): value is string => Boolean(value)),
      isCurrentMemberWorkspaceOwner,
    ),
    [brandGroupData.assignmentBrands, brandGroupData.brands, currentMemberKey, isCurrentMemberWorkspaceOwner, user?.email, user?.id],
  );
  const businessGroupAssignmentOptions = useMemo(
    () =>
      getApplicationOwnerAssignableBusinessGroups(
        brandGroupData.assignmentBrands,
        manageableBusinessGroups,
        true,
      ).map((businessGroup) => ({
        ...businessGroup,
        name: getBusinessGroupDisplayName(businessGroup),
        creatorName: getBusinessGroupCreatorName(businessGroup, brandGroupData.users),
      })),
    [brandGroupData.assignmentBrands, brandGroupData.brands, brandGroupData.users, manageableBusinessGroups, t],
  );
  const canAssignBusinessGroups = businessGroupAssignmentOptions.length > 0;
  const manageableBusinessGroupIds = useMemo(
    () => manageableBusinessGroups.map((brand) => brand.id),
    [manageableBusinessGroups],
  );
  const canCurrentMemberAssignBusinessGroupsForRow = useCallback(
    (row: ConnectionAccessRow) => canAssignConnectionToBusinessGroups({
      isConnectionOwner: row.isConnectionOwnedByCurrentUser,
      isWorkspaceOwner: isCurrentMemberWorkspaceOwner,
    }),
    [isCurrentMemberWorkspaceOwner],
  );
  const getAssignableBusinessGroupsForRow = useCallback(
    (row: ConnectionAccessRow) => {
      if (!canCurrentMemberAssignBusinessGroupsForRow(row)) {
        return [];
      }

      return getApplicationOwnerAssignableBusinessGroups(
        brandGroupData.assignmentBrands,
        manageableBusinessGroups,
        true,
      ).map((businessGroup) => ({
        ...businessGroup,
        creatorName: getBusinessGroupCreatorName(businessGroup, brandGroupData.users),
      }));
    },
    [
      brandGroupData.assignmentBrands,
      brandGroupData.brands,
      brandGroupData.users,
      canCurrentMemberAssignBusinessGroupsForRow,
      manageableBusinessGroups,
    ],
  );
  const canDelegatePositiveProjectAccess = useCallback(
    (row: ConnectionAccessRow) => row.isConnectionOwnedByCurrentUser,
    [],
  );
  const normalizeProjectAssignmentCeilings = useCallback(
    (
      row: ConnectionAccessRow,
      accessLevels: Record<string, Exclude<ProjectAssignmentCeiling, null | undefined>>,
      existingProjectIds: readonly string[] = [],
      currentAccessLevels: Readonly<Record<string, Exclude<ProjectAssignmentCeiling, null | undefined>>> = {},
    ): Record<string, Exclude<ProjectAssignmentCeiling, null | undefined>> => {
      if (canDelegatePositiveProjectAccess(row)) return accessLevels;
      const existing = new Set(existingProjectIds);
      return Object.fromEntries(
        Object.entries(accessLevels).map(([projectId, accessLevel]) => [
          projectId,
          existing.has(projectId) ? currentAccessLevels[projectId] || 'DISABLED' : 'DISABLED',
        ]),
      ) as Record<string, Exclude<ProjectAssignmentCeiling, null | undefined>>;
    },
    [canDelegatePositiveProjectAccess],
  );
  const canAssignBusinessGroupsForRow = useCallback(
    (row: ConnectionAccessRow) => getAssignableBusinessGroupsForRow(row).length > 0,
    [getAssignableBusinessGroupsForRow],
  );
  const editingBrandAssignableBusinessGroups = useMemo(
    () => editingBrandConnection
      ? getAssignableBusinessGroupsForRow(editingBrandConnection)
      : [],
    [editingBrandConnection, getAssignableBusinessGroupsForRow],
  );
  const editingGoogleAdsAssignableBusinessGroups = useMemo(
    () => editingGoogleAdsCustomerBrand
      ? getAssignableBusinessGroupsForRow(editingGoogleAdsCustomerBrand.connection)
      : [],
    [editingGoogleAdsCustomerBrand, getAssignableBusinessGroupsForRow],
  );
  const editingSecondaryAssignableBusinessGroups = useMemo(
    () => editingSecondaryResourceBrand
      ? getAssignableBusinessGroupsForRow(editingSecondaryResourceBrand.connection)
      : [],
    [editingSecondaryResourceBrand, getAssignableBusinessGroupsForRow],
  );
  const defaultBulkBrandAssignmentBrandId = useMemo(
    () =>
      businessGroupAssignmentOptions.find((brand) => brand.isDefault)?.id ??
      businessGroupAssignmentOptions[0]?.id ??
      null,
    [businessGroupAssignmentOptions],
  );
  const canManageConnectionLifecycleForRow = useCallback(
    (row: ConnectionAccessRow) =>
      row.isConnectionOwnedByCurrentUser || isCurrentMemberWorkspaceOwner,
    [isCurrentMemberWorkspaceOwner],
  );
  const isConnectionAccessLoading = integrations.isLoading || workspaceMembers.isLoading || isRefreshingConnections
    || Boolean(brandId && (isProjectResourceGrantsLoading || isProjectIntegrationContractLoading));
  const canManageConnectionAccessForRow = useCallback(
    (row: ConnectionAccessRow) =>
      canCurrentMemberManageConnectionAccess(row, currentMemberKey, connectionManageAccessById[row.id]),
    [connectionManageAccessById, currentMemberKey],
  );

  const buildWorkspaceRequestConfig = useCallback(() => {
    if (!currentWorkspace?.id) {
      throw new Error("Workspace ID is required");
    }

    const baseWorkspaceRequestConfig = {
      headers: {
        "X-Workspace-Id": currentWorkspace.id,
      },
    };
    const shouldUsePluginProductHeaders = usePluginWorkspaceContext || isPluginProduct(getCurrentProductCode());
    return shouldUsePluginProductHeaders
      ? withPluginProductHeaders(baseWorkspaceRequestConfig)
      : baseWorkspaceRequestConfig;
  }, [currentWorkspace?.id, usePluginWorkspaceContext]);

  const projectScopedBrandResources = useMemo(
    () => brandId && projectIntegrationContract
      ? applyProjectContractResourceAccess(
          brandResources ?? [],
          projectIntegrationContract.resources,
        )
      : brandId
        ? applyProjectContractResourceAccess(brandResources ?? [], [])
        : brandResources ?? [],
    [brandId, brandResources, projectIntegrationContract],
  );

  const rows = useMemo(() => {
    const allWorkspaceRows = buildConnectionRows(
      integrations.allIntegrations ?? [],
      members,
      currentMemberKey,
    );
    const resourceRows = brandId
      ? buildBrandResourceRows(
          projectScopedBrandResources,
          members,
          currentMemberKey,
          (() => {
            const activeBrand = brandGroupData.brands.find((brand) => brand.id === brandId);
            return activeBrand?.primaryOwnerUserId || activeBrand?.createdByUserId || null;
          })(),
          brandId,
          brandGroupData.connections,
          projectResourceGrants,
        )
      : [];
    const assignedConnectionIds = new Set(
      (brandConnectionIds || []).map((connectionId) => normalizeConnectionIdentity(connectionId)),
    );
    const integrationOnlyRows = brandId
      ? allWorkspaceRows.filter((row) =>
          brandGroupData.connections.some((connection) => {
            if (!shouldProjectConnectionAsRow(connection, brandId, assignedConnectionIds)) return false;
            const connectionKeys = new Set([normalizeConnectionIdentity(connection.id)]);
            return [...getConnectionRowIdentityKeys(row)].some((key) => connectionKeys.has(key));
          }),
        )
      : [];
    const assignedProjectConnectionRows = brandId
      ? brandGroupData.connections.flatMap((connection) => {
          if (!shouldProjectConnectionAsRow(connection, brandId, assignedConnectionIds)) return [];
          const connectionId = normalizeConnectionIdentity(connection.id);
          const hasWorkspaceRow = allWorkspaceRows.some((row) =>
            getConnectionRowIdentityKeys(row).has(connectionId),
          );
          return hasWorkspaceRow
            ? []
            : [buildAssignedProjectConnectionRow(connection, members, currentMemberKey)];
        })
      : [];
    const sourceRows: ConnectionAccessRow[] = brandId
      ? [...resourceRows, ...integrationOnlyRows, ...assignedProjectConnectionRows]
      : allWorkspaceRows;
    const projectScopedRows = brandId
      ? sourceRows.map((row) => {
          if (row.isProjectResourceRow) return row;
          const projectConnection = brandGroupData.connections.find((connection) =>
            shouldProjectConnectionAsRow(connection, brandId, assignedConnectionIds) &&
            [...getConnectionRowIdentityKeys(row)].includes(normalizeConnectionIdentity(connection.id)),
          );
          if (!projectConnection) return row;
          const connectionId = String(projectConnection.id);
          const activeBrand = brandGroupData.brands.find((brand) => brand.id === brandId);
          const projectOwnerKey = activeBrand?.primaryOwnerUserId || activeBrand?.createdByUserId || null;
          const isProjectOwner = Boolean(
            currentMemberKey && projectOwnerKey && normalizeText(currentMemberKey) === normalizeText(projectOwnerKey),
          );
          const persistedAccess = resolveProjectResourceAccess(projectResourceGrants, currentMemberKey, {
              connectionId,
              resourceType: "CONNECTION",
              resourceKey: connectionId,
            });
          const ownerAccess = isProjectOwner
            ? resolveProjectOwnerAccess(
                projectConnection.brandAccessCeilings?.[brandId],
                row.isConnectionOwnedByCurrentUser,
              )
            : "no_access";
          const projectIntegrationUsable = Boolean(projectIntegrationContract
            && isProjectContractIntegrationUsable(
              projectIntegrationContract.integrations,
              connectionId,
            ));
          return {
            ...row,
            currentUserAccess: resolveProjectConnectionAccess({
              isAssignedToProject: Boolean(projectConnection),
              isUsableInProjectContract: projectIntegrationUsable,
              ownerAccess,
              persistedAccess,
            }),
          };
        })
      : sourceRows;
    const workspaceRows = projectScopedRows.map(
      (row) => {
        const overriddenAlias = connectionAliasOverrides[getConnectionLabelOverrideKey(row.id)];
        return typeof overriddenAlias === "undefined"
          ? row
          : {
              ...row,
              alias: overriddenAlias,
            };
      },
    );
    return workspaceRows;
  }, [
    brandConnectionIds,
    brandGroupData.connections,
    brandGroupData.brands,
    brandId,
    connectionAliasOverrides,
    currentMemberKey,
    integrations.allIntegrations,
    members,
    projectIntegrationContract,
    projectResourceGrants,
    projectScopedBrandResources,
  ]);

  const rowsWithResolvedManageAccess = useMemo(
    () =>
      rows.map((row) => {
        // The Project contract's resource access is the authority in a Business-group view.
        // Legacy workspace grant data is still loaded for the workspace connection-management UI,
        // but must not override a Project resource's unavailable state or no-access badge.
        const resolvedCurrentUserAccess = brandId || !currentMemberKey
          ? row.currentUserAccess
          : (connectionAccessById[row.id]?.[currentMemberKey] ?? row.currentUserAccess);
        return {
          ...row,
          currentUserAccess: resolvedCurrentUserAccess,
          isManagedByCurrentUser: canManageConnectionAccessForRow(row),
        };
      }),
    [brandId, canManageConnectionAccessForRow, connectionAccessById, currentMemberKey, rows],
  );

  const getProjectConnectionForRow = useCallback(
    (row: ConnectionAccessRow) => {
      const rowIdentityKeys = getConnectionRowIdentityKeys(row);
      return brandGroupData.connections.find((connection) =>
        rowIdentityKeys.has(normalizeConnectionIdentity(connection.id)) ||
        connection.resources.some((resource) =>
          [resource.connectionId, resource.sourceMappingId].some((identity) =>
            rowIdentityKeys.has(normalizeConnectionIdentity(identity)),
          ),
        ),
      );
    },
    [brandGroupData.connections],
  );

  const getProjectResourcesForRow = useCallback(
    (row: ConnectionAccessRow, resourceKey?: string) => {
      const matchingResources = getProjectConnectionForRow(row)?.resources || [];
      const scopedResources = resourceKey
        ? matchingResources.filter(
            (resource) => normalizeConnectionIdentity(resource.resourceKey) === normalizeConnectionIdentity(resourceKey),
          )
        : matchingResources;
      const uniqueResources = new Map<string, BrandScopedResource>();
      scopedResources.forEach((resource) => {
        uniqueResources.set(
          `${resource.connectionId ?? "none"}:${resource.provider}:${resource.resourceType}:${resource.resourceKey}`,
          resource,
        );
      });
      return [...uniqueResources.values()];
    },
    [getProjectConnectionForRow],
  );

  const getBusinessGroupAccessCeilingForRow = useCallback(
    (row: ConnectionAccessRow, resourceKey?: string) => {
      if (!brandId) return "no_access";

      if (resourceKey) {
        const assignedResource = getProjectResourcesForRow(row, resourceKey).find(
          (resource) => (resource.brandIds || []).includes(brandId),
        );
        return mapBackendConnectionAccessToUi(
          assignedResource?.brandAccessCeilings?.[brandId],
        );
      }

      return mapBackendConnectionAccessToUi(
        getProjectConnectionForRow(row)?.brandAccessCeilings?.[brandId],
      );
    },
    [brandId, getProjectConnectionForRow, getProjectResourcesForRow],
  );

  const getProjectIdsForRow = useCallback(
    (row: ConnectionAccessRow, resourceKey?: string) => {
      if (!resourceKey) {
        return getProjectConnectionForRow(row)?.brandIds || row.brandIds;
      }
      const resources = getProjectResourcesForRow(row, resourceKey);
      if (resources.length > 0) {
        return [...new Set(resources.flatMap((resource) => resource.brandIds || []))];
      }
      return row.subAccountBrandIds[resourceKey] || [];
    },
    [getProjectConnectionForRow, getProjectResourcesForRow],
  );

  const editingBrandAssignmentHasChanges = useMemo(() => {
    if (!editingBrandConnection) return false;
    const effectiveAccessLevels = normalizeProjectAssignmentCeilings(
      editingBrandConnection,
      editingBrandMaxAccessLevels,
      getProjectIdsForRow(editingBrandConnection),
      getProjectConnectionForRow(editingBrandConnection)?.brandAccessCeilings,
    );
    const connection = getProjectConnectionForRow(editingBrandConnection);
    const changes = getManageableProjectAssignmentChanges({
      currentProjectIds: getProjectIdsForRow(editingBrandConnection),
      nextProjectIds: editingBrandIds,
      currentAccessLevels: connection?.brandAccessCeilings,
      nextAccessLevels: effectiveAccessLevels,
      manageableProjectIds: editingBrandAssignableBusinessGroups.map(({ id }) => id),
      currentDefaultAccessLevel: 'WRITE',
      nextDefaultAccessLevel: 'WRITE',
    });
    return changes.added.length > 0 || changes.removed.length > 0 || changes.changed.length > 0;
  }, [editingBrandAssignableBusinessGroups, editingBrandConnection, editingBrandIds, editingBrandMaxAccessLevels, getProjectConnectionForRow, getProjectIdsForRow, normalizeProjectAssignmentCeilings]);

  const editingGoogleAdsCustomerBrandAssignmentHasChanges = useMemo(() => {
    if (!editingGoogleAdsCustomerBrand) return false;

    const resources = getProjectResourcesForRow(
      editingGoogleAdsCustomerBrand.connection,
      editingGoogleAdsCustomerBrand.subAccount.customerId,
    );
    return resources.some((resource) => {
      const changes = getManageableProjectAssignmentChanges({
        currentProjectIds: resource.brandIds || [],
        nextProjectIds: editingGoogleAdsCustomerBrandIds,
        currentAccessLevels: resource.brandAccessCeilings,
        nextAccessLevels: normalizeProjectAssignmentCeilings(
          editingGoogleAdsCustomerBrand.connection,
          editingGoogleAdsCustomerBrandMaxAccessLevels,
          resource.brandIds || [],
          resource.brandAccessCeilings,
        ),
        manageableProjectIds: editingGoogleAdsAssignableBusinessGroups.map(({ id }) => id),
      });
      return changes.added.length > 0 || changes.removed.length > 0 || changes.changed.length > 0;
    });
  }, [
    editingGoogleAdsCustomerBrand,
    editingGoogleAdsCustomerBrandIds,
    editingGoogleAdsCustomerBrandMaxAccessLevels,
    editingGoogleAdsAssignableBusinessGroups,
    getProjectResourcesForRow,
    normalizeProjectAssignmentCeilings,
    manageableBusinessGroupIds,
  ]);

  const editingSecondaryResourceBrandAssignmentHasChanges = useMemo(() => {
    if (!editingSecondaryResourceBrand) return false;
    const { resource } = editingSecondaryResourceBrand;
    const effectiveAccessLevels = normalizeProjectAssignmentCeilings(
      editingSecondaryResourceBrand.connection,
      editingSecondaryResourceBrandMaxAccessLevels,
      resource.brandIds || [],
      resource.brandAccessCeilings,
    );
    const changes = getManageableProjectAssignmentChanges({
      currentProjectIds: resource.brandIds || [],
      nextProjectIds: editingSecondaryResourceBrandIds,
      currentAccessLevels: resource.brandAccessCeilings,
      nextAccessLevels: effectiveAccessLevels,
      manageableProjectIds: editingSecondaryAssignableBusinessGroups.map(({ id }) => id),
    });
    return changes.added.length > 0 || changes.removed.length > 0 || changes.changed.length > 0;
  }, [
    editingSecondaryResourceBrand,
    editingSecondaryResourceBrandIds,
    editingSecondaryResourceBrandMaxAccessLevels,
    editingSecondaryAssignableBusinessGroups,
    normalizeProjectAssignmentCeilings,
  ]);

  const syncIntegrationProjectAssignments = useCallback(
    async (
      row: ConnectionAccessRow,
      nextProjectIds: string[],
      maxAccessLevels: Record<string, Exclude<ProjectAssignmentCeiling, null | undefined>>,
    ) => {
      const assignableProjectIds = getAssignableBusinessGroupsForRow(row).map(({ id }) => id);
      const connection = getProjectConnectionForRow(row);
      if (!currentWorkspace?.id) {
        throw new Error("workspace_required");
      }
      const connectionId = await resolveCanonicalProjectConnectionIdWithRetry(
        () => projectService.getIntegrationContract(currentWorkspace.id),
        [...getConnectionRowIdentityKeys(row)],
        {
          attempts: 3,
          wait: () => new Promise((resolve) => window.setTimeout(resolve, 500)),
        },
      );
      const effectiveMaxAccessLevels = normalizeProjectAssignmentCeilings(
        row,
        maxAccessLevels,
        getProjectIdsForRow(row),
        connection?.brandAccessCeilings,
      );
      const { added, removed, changed } = getManageableProjectAssignmentChanges({
        currentProjectIds: connection?.brandIds || [],
        nextProjectIds,
        currentAccessLevels: connection?.brandAccessCeilings,
        nextAccessLevels: effectiveMaxAccessLevels,
        manageableProjectIds: assignableProjectIds,
        currentDefaultAccessLevel: 'WRITE',
        nextDefaultAccessLevel: 'WRITE',
      });
      await projectService.applyProjectAssignmentBatch(currentWorkspace.id, {
        integrationAssignments: [...added, ...changed].map((projectId) => ({
          projectId,
          connectionId,
          maxAccessLevel: effectiveMaxAccessLevels[projectId] || 'DISABLED',
        })),
        integrationRemovals: removed.map((projectId) => ({ projectId, connectionId })),
      });
    },
    [currentWorkspace?.id, getAssignableBusinessGroupsForRow, getProjectConnectionForRow, getProjectIdsForRow, normalizeProjectAssignmentCeilings],
  );

  const syncResourceProjectAssignments = useCallback(
    async (
      resources: BrandScopedResource[],
      nextProjectIds: string[],
      maxAccessLevels: Record<string, Exclude<ProjectAssignmentCeiling, null | undefined>> = {},
      assignmentRow?: ConnectionAccessRow,
    ) => {
      if (!currentWorkspace?.id) {
        throw new Error("workspace_required");
      }
      const assignableProjectIds = assignmentRow
        ? getAssignableBusinessGroupsForRow(assignmentRow).map(({ id }) => id)
        : manageableBusinessGroupIds;
      const canDelegatePositiveAccess = assignmentRow
        ? canDelegatePositiveProjectAccess(assignmentRow)
        : true;
      const upsertsByProject = new Map<string, ProjectResourceAssignmentInput[]>();
      const removalsByProject = new Map<string, BrandResource[]>();

      resources.forEach((resource) => {
        const ownsIndependentCeiling = isSecondaryProjectResource(resource.resourceType);
        const effectiveMaxAccessLevels = assignmentRow
          ? normalizeProjectAssignmentCeilings(
              assignmentRow,
              maxAccessLevels,
              resource.brandIds || [],
              resource.brandAccessCeilings,
            )
          : maxAccessLevels;
        const { added, removed, changed } = getManageableProjectAssignmentChanges({
          currentProjectIds: resource.brandIds || [],
          nextProjectIds,
          currentAccessLevels: ownsIndependentCeiling ? resource.brandAccessCeilings : {},
          nextAccessLevels: ownsIndependentCeiling ? effectiveMaxAccessLevels : {},
          manageableProjectIds: assignableProjectIds,
        });
        [...added, ...changed].forEach((projectId) => {
          const assignments = upsertsByProject.get(projectId) || [];
          assignments.push({
            resource: { ...resource, brandIds: resource.brandIds || [] },
            ...(ownsIndependentCeiling
              ? { maxAccessLevel: effectiveMaxAccessLevels[projectId] || (canDelegatePositiveAccess ? "WRITE" : "DISABLED") }
              : {}),
          });
          upsertsByProject.set(projectId, assignments);
        });
        removed.forEach((projectId) => {
          const removals = removalsByProject.get(projectId) || [];
          removals.push({ ...resource, brandIds: resource.brandIds || [] });
          removalsByProject.set(projectId, removals);
        });
      });
      const affectedProjectIds = new Set([
        ...upsertsByProject.keys(),
        ...removalsByProject.keys(),
      ]);
      if (affectedProjectIds.size === 0) {
        return;
      }
      await projectService.applyProjectAssignmentBatch(currentWorkspace.id, {
        resourceSyncs: [...affectedProjectIds].map((projectId) => ({
          projectId,
          resources: (upsertsByProject.get(projectId) || []).map(({ resource, maxAccessLevel }) => ({
            provider: resource.provider,
            resourceType: resource.resourceType,
            resourceKey: resource.resourceKey,
            connectionId: resource.connectionId,
            maxAccessLevel,
          })),
          removals: (removalsByProject.get(projectId) || []).map((resource) => ({
            provider: resource.provider,
            resourceType: resource.resourceType,
            resourceKey: resource.resourceKey,
            connectionId: resource.connectionId,
          })),
        })),
      });
    },
    [canDelegatePositiveProjectAccess, currentWorkspace?.id, getAssignableBusinessGroupsForRow, manageableBusinessGroupIds, normalizeProjectAssignmentCeilings],
  );

  const workspaceVisibleRows = useMemo(
    () => getVisibleConnectionRows(rowsWithResolvedManageAccess),
    [rowsWithResolvedManageAccess],
  );

  const brandVisibleRows = useMemo(() => {
    if (!brandId) {
      return workspaceVisibleRows;
    }

    const assignedConnectionIds = new Set(
      (brandConnectionIds || []).map((connectionId) => normalizeConnectionIdentity(connectionId)),
    );

    return workspaceVisibleRows.flatMap((row) => {
      const assignedToProject = [...getConnectionRowIdentityKeys(row)].some((key) =>
        assignedConnectionIds.has(key),
      );
      if (!assignedToProject) return [];

      if (row.subAccounts?.length) {
        const scopedSubAccounts = row.subAccounts.filter(
          (subAccount: GoogleAdsCustomerSubAccount) =>
            getProjectIdsForRow(row, subAccount.customerId).includes(brandId),
        );

        return [{ ...row, subAccounts: scopedSubAccounts }];
      }

      return [row];
    });
  }, [
    brandId,
    brandConnectionIds,
    getProjectIdsForRow,
    workspaceVisibleRows,
  ]);

  const ownedConnectionRows = useMemo(() => getOwnedConnectionRows(brandVisibleRows), [brandVisibleRows]);
  const activeConnectionScopeCounts = useMemo(
    () => getConnectionScopeCounts(brandVisibleRows.filter(isIntegrationConnectionActive)),
    [brandVisibleRows],
  );

  useEffect(() => {
    if (isConnectionAccessLoading) {
      return;
    }

    onConnectionScopeCountsChange?.(activeConnectionScopeCounts);
  }, [
    activeConnectionScopeCounts,
    isConnectionAccessLoading,
    onConnectionScopeCountsChange,
  ]);

  const rowsInCurrentScope = connectionFilter === "owned_by_me" ? ownedConnectionRows : brandVisibleRows;

  const visibleRows = useMemo(
    () => filterConnectionAccessRows(brandVisibleRows, connectionFilter, searchQuery),
    [brandVisibleRows, connectionFilter, searchQuery],
  );

  const scopedVisibleRows = useMemo(() => {
    if (!providerFilter) {
      return visibleRows;
    }

    const providerKey = normalizeProvider(providerFilter);
    return visibleRows.filter((row) => normalizeProvider(row.provider) === providerKey);
  }, [providerFilter, visibleRows]);
  const orderedScopedVisibleRows = useMemo(
    () => brandId ? sortBusinessGroupApplicationsNewestFirst(scopedVisibleRows) : scopedVisibleRows,
    [brandId, scopedVisibleRows],
  );
  const getBusinessGroupMemberAccessTarget = (
    row: ConnectionAccessRow,
    resource?: { resourceType?: string; resourceKey?: string },
  ): BusinessGroupMemberAccessTarget => ({
    connectionIdentities: [...getConnectionRowIdentityKeys(row)],
    ...(resource?.resourceType && resource.resourceKey
      ? { resourceType: resource.resourceType, resourceKey: resource.resourceKey }
      : isSecondaryProjectResource(row.resourceType) && row.resourceKey
        ? { resourceType: row.resourceType, resourceKey: row.resourceKey }
        : {}),
  });
  const renderBusinessGroupMemberAccessAction = (
    row: ConnectionAccessRow,
    resource?: { resourceType?: string; resourceKey?: string },
  ) => {
    const target = getBusinessGroupMemberAccessTarget(row, resource);
    if (!showBusinessGroupMemberAccessActions || !onSetMemberAccess) {
      return null;
    }
    const canManageMembersForTarget = canSetMemberAccess
      ? canSetMemberAccess(target)
      : true;
    const disabledReason = !canManageMembersForTarget
      ? getSetMemberAccessDisabledReason?.(target)
      : undefined;

    return (
      <Tooltip content={disabledReason} side="top">
        <span
          className="inline-flex max-w-full shrink-0 rounded-[8px] outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/20"
          tabIndex={disabledReason ? 0 : undefined}
          aria-label={disabledReason}
        >
          <button
            type="button"
            onClick={() => onSetMemberAccess(target)}
            disabled={!canManageMembersForTarget}
            className="max-w-full rounded-[8px] border border-[#E8EBF3] bg-white px-3 py-2 text-center text-xs font-medium leading-4 text-[#1A162F] outline-none transition-colors hover:border-[#C8D0E2] hover:bg-[#F8F9FC] focus-visible:ring-2 focus-visible:ring-[#1A162F]/20 disabled:cursor-not-allowed disabled:border-[#E8EBF3] disabled:bg-[#F8F9FC] disabled:text-[#A4ABC0]"
          >
            {t("website.brandsSettings.simple.batchAssignPermissions")}
          </button>
        </span>
      </Tooltip>
    );
  };
  const brandAssignmentUnits = useMemo<BrandAssignmentUnit[]>(
    () =>
      rowsInCurrentScope.flatMap((row) => {
        if (
          !isIntegrationConnectionActive(row) ||
          !canManageConnectionLifecycleForRow(row)
        ) {
          return [];
        }

        if (row.subAccounts?.length) {
          return row.subAccounts.map((subAccount: GoogleAdsCustomerSubAccount) => ({
            key: getGoogleAdsCustomerBrandKey(row.id, subAccount.customerId),
            row,
            subAccount,
            name: subAccount.accountName || subAccount.customerId,
            detail: subAccount.customerId,
          }));
        }

        const secondaryResources = getProjectResourcesForRow(row).filter((resource) =>
          isSecondaryProjectResource(resource.resourceType),
        );
        if (secondaryResources.length > 0) {
          return secondaryResources.map((resource) => ({
            key: `${row.id}:${resource.resourceKey}`,
            row,
            resourceKey: resource.resourceKey,
            name: resource.name || resource.resourceKey,
            detail: resource.resourceKey,
          }));
        }

        return [
          {
            key: row.id,
            row,
            name: row.accountId && row.accountId !== "-" ? row.accountId : row.name || row.provider,
            detail: row.name || "",
          },
        ];
      }),
    [canManageConnectionLifecycleForRow, getProjectResourcesForRow, rowsInCurrentScope],
  );
  const assignedBulkBrandAssignmentUnitKeys = useMemo(() => {
    if (!bulkBrandAssignmentBrandId) return new Set<string>();

    return new Set(
      brandAssignmentUnits.flatMap((unit) => {
        const resourceKey = getBrandAssignmentUnitResourceKey(unit);
        return getProjectIdsForRow(unit.row, resourceKey).includes(
          bulkBrandAssignmentBrandId,
        )
          ? [unit.key]
          : [];
      }),
    );
  }, [brandAssignmentUnits, bulkBrandAssignmentBrandId, getProjectIdsForRow]);
  const getBulkBrandAssignmentConnectionKey = useCallback(
    (unit: BrandAssignmentUnit) =>
      normalizeConnectionIdentity(
        getProjectConnectionForRow(unit.row)?.id ||
          unit.row.workspaceIntegrationId ||
          unit.row.id,
      ),
    [getProjectConnectionForRow],
  );
  const orderedBrandAssignmentUnits = useMemo(
    () =>
      prioritizePersistedAssignedUnits(
        brandAssignmentUnits,
        assignedBulkBrandAssignmentUnitKeys,
      ),
    [assignedBulkBrandAssignmentUnitKeys, brandAssignmentUnits],
  );
  const getBulkBrandAssignmentUnitAccessLevel = useCallback(
    (unit: BrandAssignmentUnit, businessGroupId: string) => {
      const resourceKey = getBrandAssignmentUnitResourceKey(unit);
      if (resourceKey) {
        const assignedResource = getProjectResourcesForRow(
          unit.row,
          resourceKey,
        ).find((resource) => resource.brandIds?.includes(businessGroupId));
        const ceiling = assignedResource?.brandAccessCeilings?.[businessGroupId];
        if (ceiling) return ceiling;
        return canDelegatePositiveProjectAccess(unit.row) ? "WRITE" : "DISABLED";
      }

      const ceiling = getProjectConnectionForRow(unit.row)?.brandAccessCeilings?.[
        businessGroupId
      ];
      if (ceiling) return ceiling;
      return canDelegatePositiveProjectAccess(unit.row) ? "WRITE" : "DISABLED";
    },
    [canDelegatePositiveProjectAccess, getProjectConnectionForRow, getProjectResourcesForRow],
  );
  const createBulkBrandAssignmentDraft = useCallback(
    (businessGroupId: string | null) => {
      if (!businessGroupId) {
        return {
          unitKeys: [] as string[],
          accessLevels: {} as Record<string, "READ" | "WRITE">,
        };
      }

      const assignedUnits = brandAssignmentUnits.filter((unit) =>
        getProjectIdsForRow(
          unit.row,
          getBrandAssignmentUnitResourceKey(unit),
        ).includes(
          businessGroupId,
        ),
      );
      return {
        unitKeys: assignedUnits.map((unit) => unit.key),
        accessLevels: Object.fromEntries(
          assignedUnits.map((unit) => [
            unit.key,
            getBulkBrandAssignmentUnitAccessLevel(unit, businessGroupId) === "WRITE" ? "WRITE" : "READ",
          ]),
        ) as Record<string, "READ" | "WRITE">,
      };
    },
    [
      brandAssignmentUnits,
      getBulkBrandAssignmentUnitAccessLevel,
      getProjectIdsForRow,
    ],
  );
  const selectedBulkBrandAssignmentUnitCount = useMemo(
    () => bulkBrandAssignmentUnitKeys.length,
    [bulkBrandAssignmentUnitKeys],
  );
  const bulkBrandAssignmentParentCleanupRows = useMemo(() => {
    if (!bulkBrandAssignmentBrandId) return [];

    const rowsByConnectionKey = new Map<string, ConnectionAccessRow>();
    const units = brandAssignmentUnits.map((unit) => {
      const connectionKey = getBulkBrandAssignmentConnectionKey(unit);
      rowsByConnectionKey.set(connectionKey, unit.row);
      return {
        unitKey: unit.key,
        connectionKey,
        isSecondaryResource: Boolean(getBrandAssignmentUnitResourceKey(unit)),
        isParentAssigned: getProjectIdsForRow(unit.row).includes(
          bulkBrandAssignmentBrandId,
        ),
      };
    });
    const cleanupConnectionKeys = getParentConnectionKeysWithoutSelectedResources(
      units,
      bulkBrandAssignmentUnitKeys,
    );

    return cleanupConnectionKeys.flatMap((connectionKey) => {
      const row = rowsByConnectionKey.get(connectionKey);
      return row ? [row] : [];
    });
  }, [
    brandAssignmentUnits,
    bulkBrandAssignmentBrandId,
    bulkBrandAssignmentUnitKeys,
    getBulkBrandAssignmentConnectionKey,
    getProjectIdsForRow,
  ]);
  const canSetBulkUnitPositiveAccessForUnit = useCallback(
    (unit: BrandAssignmentUnit) => canDelegatePositiveProjectAccess(unit.row),
    [canDelegatePositiveProjectAccess],
  );
  const bulkBrandAssignmentEligibleUnitKeys = useMemo(
    () =>
      brandAssignmentUnits
        .filter(canSetBulkUnitPositiveAccessForUnit)
        .map((unit) => unit.key),
    [brandAssignmentUnits, canSetBulkUnitPositiveAccessForUnit],
  );
  const canApplyBulkBrandAssignmentAccess =
    bulkBrandAssignmentEligibleUnitKeys.length > 0;
  const bulkBrandAssignmentCanSetPositiveAccess = useMemo(
    () =>
      bulkBrandAssignmentUnitKeys.length > 0 &&
      bulkBrandAssignmentUnitKeys.every((unitKey) => {
        const unit = brandAssignmentUnits.find((candidate) => candidate.key === unitKey);
        return Boolean(unit && canSetBulkUnitPositiveAccessForUnit(unit));
      }),
    [
      brandAssignmentUnits,
      bulkBrandAssignmentUnitKeys,
      canSetBulkUnitPositiveAccessForUnit,
    ],
  );
  const bulkBrandAssignmentAccessLevel = useMemo(() => {
    if (selectedBulkBrandAssignmentUnitCount === 0) return undefined;

    const selectedAccessLevels = new Set(
      bulkBrandAssignmentUnitKeys.map(
        (unitKey) => bulkBrandAssignmentAccessLevels[unitKey] || "WRITE",
      ),
    );
    if (selectedAccessLevels.size !== 1) return undefined;

    return selectedAccessLevels.has("WRITE") ? "action" : "read";
  }, [
    bulkBrandAssignmentAccessLevels,
    bulkBrandAssignmentUnitKeys,
    selectedBulkBrandAssignmentUnitCount,
  ]);
  const bulkBrandAssignmentHasChanges = useMemo(() => {
    if (!bulkBrandAssignmentBrandId) return false;
    if (bulkBrandAssignmentParentCleanupRows.length > 0) return true;
    if (
      !hasSameBrandAssignmentIds(
        [...assignedBulkBrandAssignmentUnitKeys],
        bulkBrandAssignmentUnitKeys,
      )
    ) {
      return true;
    }

    return bulkBrandAssignmentUnitKeys.some((unitKey) => {
      const unit = brandAssignmentUnits.find(
        (candidate) => candidate.key === unitKey,
      );
      if (!unit) return false;
      const isCurrentlyAssigned = assignedBulkBrandAssignmentUnitKeys.has(unitKey);
      const currentAccessLevel = getBulkBrandAssignmentUnitAccessLevel(
        unit,
        bulkBrandAssignmentBrandId,
      );
      const canSetPositiveAccess = canDelegatePositiveProjectAccess(unit.row);
      const desiredAccessLevel = !canSetPositiveAccess && isCurrentlyAssigned
        ? currentAccessLevel
        : !canSetPositiveAccess
          ? "DISABLED"
          : (bulkBrandAssignmentAccessLevels[unitKey]
            || getBulkBrandAssignmentUnitAccessLevel(unit, bulkBrandAssignmentBrandId));
      return (
        currentAccessLevel !== desiredAccessLevel
      );
    });
  }, [
    assignedBulkBrandAssignmentUnitKeys,
    brandAssignmentUnits,
    bulkBrandAssignmentAccessLevels,
    bulkBrandAssignmentBrandId,
    bulkBrandAssignmentParentCleanupRows,
    bulkBrandAssignmentUnitKeys,
    canDelegatePositiveProjectAccess,
    getBulkBrandAssignmentUnitAccessLevel,
  ]);
  const hasConnectionRows = scopedVisibleRows.length > 0;
  const hasRowsInCurrentScope = providerFilter
    ? rowsInCurrentScope.some((row) => normalizeProvider(row.provider) === normalizeProvider(providerFilter))
    : rowsInCurrentScope.length > 0;
  const hasConnectionSearchQuery = Boolean(searchQuery.trim());
  const isConnectionSearchEmpty = hasConnectionSearchQuery && hasRowsInCurrentScope && !hasConnectionRows;
  const isProviderScopedView = Boolean(providerFilter);
  const showProviderIcon = !isProviderScopedView;

  const providerAnchorRowIds = useMemo(() => {
    const anchorRowIds = new Map<string, string>();

    scopedVisibleRows.forEach((row) => {
      const providerKey = getConnectionGroupKey(row.provider);
      if (!anchorRowIds.has(providerKey)) {
        anchorRowIds.set(providerKey, row.id);
      }
    });

    return anchorRowIds;
  }, [scopedVisibleRows]);

  const activeAccessRow = useMemo(
    () => scopedVisibleRows.find((row) => row.id === activeAccessConnectionId) ?? null,
    [activeAccessConnectionId, scopedVisibleRows],
  );

  const brandMembers = useMemo(() => {
    if (!brandId) {
      return members;
    }

    const brandMemberIds = new Set(
      brandGroupData.memberships
        .filter((membership) => membership.brandId === brandId)
        .map((membership) => membership.userId),
    );

    return members.filter((member) =>
      [member.userId, member.key, member.email].some(
        (memberId) => Boolean(memberId) && brandMemberIds.has(String(memberId)),
      ),
    );
  }, [brandGroupData.memberships, brandId, members]);

  const activeAssignableMembers = useMemo(
    () => (activeAccessRow ? getAssignableMembersForRow(members, activeAccessRow) : []),
    [activeAccessRow, members],
  );

  const activeAssignableMemberKeys = useMemo(
    () => activeAssignableMembers.map((member) => member.key).join("\u0000"),
    [activeAssignableMembers],
  );

  const isActiveAccessLoading = activeAccessRow ? loadingAccessConnectionIds.has(activeAccessRow.id) : false;
  const isActiveAccessGoogleAds = activeAccessRow
    ? normalizeProvider(activeAccessRow.provider) === "google-ads"
    : false;
  const activeAccessSubAccount = activeAccessCustomerId
    ? (activeAccessRow?.subAccounts?.find((subAccount) => subAccount.customerId === activeAccessCustomerId) ?? null)
    : null;
  const activeAccessMinimumUnitName = activeAccessSubAccount
    ? activeAccessSubAccount.accountName || activeAccessSubAccount.customerId
    : "";
  const isSavingActiveAccess = activeAccessRow ? savingAccessConnectionIds.has(activeAccessRow.id) : false;
  const activeAccessError = activeAccessRow ? connectionAccessErrors[activeAccessRow.id] : undefined;
  const canEditActiveConnectionMemberAccess = activeAccessRow
    ? canEditConnectionMemberAccess({
        isPluginAdmin,
        isConnectionOwnedByCurrentUser: activeAccessRow.isConnectionOwnedByCurrentUser,
        isManagedByCurrentUser: activeAccessRow.isManagedByCurrentUser,
      })
    : false;
  const activeEditableAssignableMembers = useMemo(() => {
    if (!activeAccessRow) return [];

    return activeAssignableMembers.filter((member) =>
      canEditConnectionMemberAccessTarget({
        isPluginAdmin,
        canEditMemberAccess: canEditActiveConnectionMemberAccess,
        isConnectionOwnedByCurrentUser: activeAccessRow.isConnectionOwnedByCurrentUser,
        isTargetConnectionOwner: member.key === activeAccessRow.connectionOwnerKey,
      }),
    );
  }, [
    activeAccessRow,
    activeAssignableMembers,
    canEditActiveConnectionMemberAccess,
    isPluginAdmin,
  ]);
  const activeEditableAssignableMemberKeys = useMemo(
    () => new Set(activeEditableAssignableMembers.map((member) => member.key)),
    [activeEditableAssignableMembers],
  );

  const loadConnectionAccessGrants = useCallback(
    async (row: ConnectionAccessRow, forceRefresh = false, options: LoadConnectionAccessGrantsOptions = {}) => {
      if (!currentWorkspace?.id) {
        return;
      }
      if (!forceRefresh && connectionAccessById[row.id] && connectionManageAccessById[row.id]) {
        return;
      }
      if (loadingAccessConnectionIds.has(row.id)) {
        return;
      }

      const integrationId = getConnectionAccessBackendId(row);
      if (!integrationId) {
        setConnectionAccessErrors((previous) => ({
          ...previous,
          [row.id]: t("website.integrationsSettings.connectionAccessUnavailable"),
        }));
        return;
      }

      setLoadingAccessConnectionIds((previous) => new Set(previous).add(row.id));
      setConnectionAccessErrors((previous) => {
        const next = { ...previous };
        delete next[row.id];
        return next;
      });

      try {
        const response = await authService.getWorkspaceIntegrationAccessGrants(
          integrationId,
          buildWorkspaceRequestConfig(),
        );
        const accessGrants = response.accessGrants ?? [];
        setConnectionAccessById((previous) => ({
          ...previous,
          [row.id]: buildAccessMatrixFromBackendGrants(accessGrants, members),
        }));
        setConnectionManageAccessById((previous) => ({
          ...previous,
          [row.id]: buildManageAccessMatrixFromBackendGrants(accessGrants, members),
        }));
        setGoogleAdsCustomerAccessById((previous) => ({
          ...previous,
          [row.id]: buildGoogleAdsCustomerAccessMatrixFromBackendGrants(response.googleAdsCustomerGrants, members),
        }));
        setGoogleAdsCustomerAccessLevelsById((previous) => ({
          ...previous,
          [row.id]: buildGoogleAdsCustomerAccessLevelMatrixFromBackendGrants(response.googleAdsCustomerGrants, members),
        }));
      } catch (error: any) {
        const message = normalizeConnectionUserMessage(
          error?.message,
          t("website.integrationsSettings.failedToLoadConnectionAccess"),
        );
        setConnectionAccessErrors((previous) => ({
          ...previous,
          [row.id]: message,
        }));
        if (options.silent) {
          setConnectionManageAccessById((previous) => ({
            ...previous,
            [row.id]: buildManageAccessMatrixFromBackendGrants([], members),
          }));
        } else {
          toast.showToast(message, "error");
        }
      } finally {
        setLoadingAccessConnectionIds((previous) => {
          const next = new Set(previous);
          next.delete(row.id);
          return next;
        });
      }
    },
    [
      connectionAccessById,
      connectionManageAccessById,
      buildWorkspaceRequestConfig,
      currentWorkspace?.id,
      loadingAccessConnectionIds,
      members,
      t,
      toast,
    ],
  );

  useEffect(() => {
    if (!currentWorkspace?.id || isConnectionAccessLoading) {
      return;
    }

    scopedVisibleRows.forEach((row) => {
      // Business-group permission badges are resolved from the Project
      // contract/grants. Do not query Workspace grants for display-only rows
      // that the current user cannot otherwise see in their integration list.
      if (brandId || !shouldPrefetchConnectionAccess(row)) {
        return;
      }
      if (connectionManageAccessById[row.id]) {
        return;
      }

      void loadConnectionAccessGrants(row, false, { silent: true });
    });
  }, [
    connectionManageAccessById,
    brandId,
    currentWorkspace?.id,
    isConnectionAccessLoading,
    loadConnectionAccessGrants,
    scopedVisibleRows,
  ]);

  const connectionAccessViewState = useMemo<ConnectionAccessViewState>(() => {
    // Project resource grants are the authority for the Business-group
    // permission column. Do not render the assigned resource rows with an
    // interim fallback while that authority is still resolving.
    if (isConnectionAccessLoading && (!hasConnectionRows || Boolean(brandId))) {
      return "loading";
    }

    return hasConnectionRows ? "ready" : "empty";
  }, [hasConnectionRows, isConnectionAccessLoading]);

  useEffect(() => {
    if (!activeAccessConnectionId) {
      return;
    }

    if (!activeAccessRow) {
      setActiveAccessConnectionId(null);
    }
  }, [activeAccessConnectionId, activeAccessRow]);

  useEffect(() => {
    if (!activeAccessRow) {
      setModalAccessDraft({});
      setModalManageAccessDraft({});
      setModalGoogleAdsCustomerDraft({});
      setMembersPage(1);
      return;
    }

    setMembersPage(1);
    void loadConnectionAccessGrants(activeAccessRow);
    const savedAccess = connectionAccessById[activeAccessRow.id] ?? {};
    const savedGoogleAdsCustomerAccess = googleAdsCustomerAccessById[activeAccessRow.id] ?? {};
    const savedGoogleAdsCustomerAccessLevels = googleAdsCustomerAccessLevelsById[activeAccessRow.id] ?? {};
    const savedManageAccess = connectionManageAccessById[activeAccessRow.id] ?? {};
    setModalAccessDraft(
      activeAssignableMembers.reduce<Record<string, ConnectionAccessLevel>>((next, member) => {
        const hasSelectedCustomerAccess = activeAccessCustomerId
          ? (savedGoogleAdsCustomerAccess[member.key] ?? []).includes(activeAccessCustomerId)
          : false;
        next[member.key] = activeAccessCustomerId
          ? (savedGoogleAdsCustomerAccessLevels[member.key]?.[activeAccessCustomerId] ??
            (hasSelectedCustomerAccess ? (savedAccess[member.key] ?? "read") : "no_access"))
          : (savedAccess[member.key] ?? "no_access");
        return next;
      }, {}),
    );
    setModalManageAccessDraft(
      activeAssignableMembers.reduce<Record<string, boolean>>((next, member) => {
        next[member.key] = Boolean(
          member.key === activeAccessRow.connectionOwnerKey ||
            savedManageAccess[member.key] ||
            (member.isCurrentUser && activeAccessRow.canManageAccess),
        );
        return next;
      }, {}),
    );
    setModalGoogleAdsCustomerDraft(
      activeAssignableMembers.reduce<Record<string, string[]>>((next, member) => {
        next[member.key] = savedGoogleAdsCustomerAccess[member.key] ?? [];
        return next;
      }, {}),
    );
  }, [
    activeAccessRow,
    activeAccessCustomerId,
    activeAssignableMemberKeys,
    activeAssignableMembers,
    connectionAccessById,
    connectionManageAccessById,
    googleAdsCustomerAccessById,
    googleAdsCustomerAccessLevelsById,
    loadConnectionAccessGrants,
  ]);

  const accessLabel = useCallback(
    (level: ConnectionAccessLevel) => {
      if (level === "action") return t("website.integrationsSettings.viewAndTakeAction");
      if (level === "read") return t("website.integrationsSettings.viewOnly");
      return t("website.integrationsSettings.noAccess");
    },
    [t],
  );

  const openUserAccessModal = useCallback((row: ConnectionAccessRow, customerId: string | null = null) => {
    if (!row.isManagedByCurrentUser || !isIntegrationConnectionActive(row)) {
      return;
    }

    setActiveAccessConnectionId(row.id);
    setActiveAccessCustomerId(customerId);
  }, []);

  useEffect(() => {
    if (!requestedAccessProvider || isConnectionAccessLoading) {
      return;
    }

    const targetRow = findRequestedAccessRow(scopedVisibleRows, requestedAccessProvider);
    if (targetRow) {
      openUserAccessModal(targetRow);
    }

    onAccessRequestComplete?.();
  }, [
    isConnectionAccessLoading,
    onAccessRequestComplete,
    openUserAccessModal,
    requestedAccessProvider,
    scopedVisibleRows,
  ]);

  useEffect(() => {
    if (!highlightedConnectionKey && !highlightedConnectionProvider) {
      lastHighlightRequestRef.current = null;
      return;
    }

    const highlightedKey = normalizeConnectionIdentity(highlightedConnectionKey);
    const providerId = normalizeProvider(highlightedConnectionProvider);
    const highlightRequestId = highlightedKey ? `row:${highlightedKey}` : providerId ? `provider:${providerId}` : "";
    if (!highlightRequestId) {
      lastHighlightRequestRef.current = null;
      return;
    }

    if (lastHighlightRequestRef.current === highlightRequestId) {
      return;
    }

    if (isConnectionAccessLoading) {
      return;
    }

    const targetRow = highlightedKey
      ? scopedVisibleRows.find((row) => getConnectionRowIdentityKeys(row).has(highlightedKey))
      : [...scopedVisibleRows]
          .filter((row) => getConnectionGroupKey(row.provider) === providerId)
          .sort((left, right) => getConnectionOpenTimestamp(right) - getConnectionOpenTimestamp(left))[0];
    if (!targetRow) {
      return;
    }

    const rowElementKey = getConnectionRowElementKey(targetRow);
    const getTargetElement = () =>
      Array.from(document.querySelectorAll<HTMLElement>("[data-connection-row-key]")).find(
        (candidate) => candidate.dataset.connectionRowKey === rowElementKey,
      ) ?? document.getElementById(getConnectionGroupElementId(targetRow.provider));
    const element = getTargetElement();

    if (element) {
      lastHighlightRequestRef.current = highlightRequestId;
      setHighlightedRowId(targetRow.id);

      let scrollFrameId = 0;
      let nestedScrollFrameId = 0;

      scrollFrameId = window.requestAnimationFrame(() => {
        nestedScrollFrameId = window.requestAnimationFrame(() => {
          const targetElement = getTargetElement();
          if (!(targetElement instanceof HTMLElement)) {
            return;
          }

          scrollConnectionGroupIntoView(targetElement);
          targetElement.focus({ preventScroll: true });
        });
      });

      const highlightTimeout = setTimeout(() => {
        setHighlightedRowId(null);
        onHighlightComplete?.();
      }, 5000);

      return () => {
        window.cancelAnimationFrame(scrollFrameId);
        window.cancelAnimationFrame(nestedScrollFrameId);
        clearTimeout(highlightTimeout);
      };
    }
  }, [
    highlightedConnectionKey,
    highlightedConnectionProvider,
    isConnectionAccessLoading,
    onHighlightComplete,
    scopedVisibleRows,
  ]);

  const closeUserAccessModal = useCallback(() => {
    setActiveAccessConnectionId(null);
    setActiveAccessCustomerId(null);
    setMemberSearchQuery("");
    setModalAccessDraft({});
    setModalManageAccessDraft({});
    setModalGoogleAdsCustomerDraft({});
  }, []);

  const openEditConnectionLabelModal = useCallback((row: ConnectionAccessRow, resource?: WorkspaceResourceLabelTarget) => {
    const canEditLabel = !brandId && (
      row.isConnectionOwnedByCurrentUser || isCurrentMemberWorkspaceOwner
    );
    if (!canEditLabel) {
      return;
    }

    setEditingLabelConnection(row);
    setEditingLabelResource(resource || null);
    const resourceLabel = resource && normalizeProvider(row.provider) === "google-ads"
      ? row.subAccounts?.find((subAccount) => subAccount.customerId === resource.resourceKey)?.label
      : resource?.label;
    setEditingLabelValue(resourceLabel || row.alias || "");
  }, [brandId, isCurrentMemberWorkspaceOwner]);

  const closeEditConnectionLabelModal = useCallback(() => {
    if (isSavingConnectionLabel) {
      return;
    }

    setEditingLabelConnection(null);
    setEditingLabelResource(null);
    setEditingLabelValue("");
  }, [isSavingConnectionLabel]);

  const handleSaveConnectionLabel = useCallback(async () => {
    if (!editingLabelConnection || isSavingConnectionLabel) {
      return;
    }

    const nextLabel = editingLabelValue.trim();
    if (brandId) {
      return;
    }

    const canEditLabel = editingLabelConnection.isConnectionOwnedByCurrentUser || isCurrentMemberWorkspaceOwner;
    const integrationId = getConnectionAccessBackendId(editingLabelConnection);
    if (!canEditLabel || !integrationId) {
      return;
    }

    setIsSavingConnectionLabel(true);
    try {
      if (editingLabelResource && normalizeProvider(editingLabelConnection.provider) === "google-ads") {
        await authService.updateGoogleAdsCustomerLabel(integrationId, editingLabelResource.resourceKey, {
          label: nextLabel || null,
          axiosRequestConfig: buildWorkspaceRequestConfig(),
        });
      } else if (editingLabelResource) {
        await authService.updateWorkspaceIntegrationResourceLabel(integrationId, {
          resourceType: editingLabelResource.resourceType,
          resourceKey: editingLabelResource.resourceKey,
          label: nextLabel || null,
          axiosRequestConfig: buildWorkspaceRequestConfig(),
        });
      } else {
        await authService.updateWorkspaceIntegrationLabel(integrationId, {
          label: nextLabel || null,
          axiosRequestConfig: buildWorkspaceRequestConfig(),
        });
      }

      setConnectionAliasOverrides((previous) => ({
        ...previous,
        [getConnectionLabelOverrideKey(editingLabelConnection.id, editingLabelResource?.resourceKey)]: nextLabel,
      }));
      setEditingLabelConnection(null);
      setEditingLabelResource(null);
      setEditingLabelValue("");
      toast.showToast(t("website.integrationsSettings.labelUpdated"), "success");
      void integrations.refresh(false, { forceRefresh: true }).catch((refreshError) => {
        console.warn("[ConnectionAccessSettings] Failed to refresh integrations after label update", refreshError);
      });
    } catch (error: any) {
      toast.showToast(
        `${t("website.integrationsSettings.labelUpdateFailed")}: ${error.message || t("website.integrationsSettings.failedToUpdateIntegrationLabel")}`,
        "error",
      );
    } finally {
      setIsSavingConnectionLabel(false);
    }
  }, [
    editingLabelConnection,
    editingLabelResource,
    editingLabelValue,
    brandId,
    buildWorkspaceRequestConfig,
    integrations,
    isCurrentMemberWorkspaceOwner,
    isSavingConnectionLabel,
    t,
    toast,
  ]);

  const openEditConnectionBrandsModal = useCallback(
    (row: ConnectionAccessRow) => {
      if (!canAssignBusinessGroupsForRow(row)) {
        return;
      }

      setEditingBrandConnection(row);
      const assignedIds = getProjectIdsForRow(row);
      const connection = getProjectConnectionForRow(row);
      setEditingBrandIds(assignedIds);
      setEditingBrandMaxAccessLevels(Object.fromEntries(
        assignedIds.map((projectId) => [
          projectId,
          toDelegatedProjectAccessLevel(
            connection?.brandAccessCeilings?.[projectId],
            "WRITE",
          ),
        ]),
      ));
    },
    [canAssignBusinessGroupsForRow, getProjectConnectionForRow, getProjectIdsForRow],
  );

  const closeEditConnectionBrandsModal = useCallback(() => {
    setEditingBrandConnection(null);
    setEditingBrandIds([]);
    setEditingBrandMaxAccessLevels({});
  }, []);

  const toggleEditingBrand = useCallback((brandId: string) => {
    setEditingBrandIds((previous) =>
      previous.includes(brandId) ? previous.filter((id) => id !== brandId) : [...previous, brandId],
    );
    setEditingBrandMaxAccessLevels((previous) => ({ ...previous, [brandId]: previous[brandId] || 'WRITE' }));
  }, []);

  const saveConnectionBrands = useCallback(async () => {
    if (
      !editingBrandConnection ||
      editingBrandAssignableBusinessGroups.length === 0 ||
      !editingBrandAssignmentHasChanges ||
      isSavingBrandAssignments ||
      brandAssignmentSaveInFlightRef.current
    ) {
      return;
    }

    const resources = getProjectResourcesForRow(editingBrandConnection);
    if (resources.some((resource) => isSecondaryProjectResource(resource.resourceType))) {
      return;
    }

    brandAssignmentSaveInFlightRef.current = true;
    setIsSavingBrandAssignments(true);
    try {
      await syncIntegrationProjectAssignments(editingBrandConnection, editingBrandIds, editingBrandMaxAccessLevels);
      await syncResourceProjectAssignments(
        resources,
        editingBrandIds,
        {},
        editingBrandConnection,
      );
      await refreshBrandGroup();
      closeEditConnectionBrandsModal();
      toast.showToast(t("website.brandsSettings.simple.brandAssignmentsUpdated"), "success");
    } catch {
      toast.showToast(t("website.integrationsSettings.failedToSaveConnectionAccess"), "error");
    } finally {
      brandAssignmentSaveInFlightRef.current = false;
      setIsSavingBrandAssignments(false);
    }
  }, [
    closeEditConnectionBrandsModal,
    editingBrandConnection,
    editingBrandIds,
    editingBrandMaxAccessLevels,
    editingBrandAssignmentHasChanges,
    editingBrandAssignableBusinessGroups.length,
    getProjectResourcesForRow,
    syncIntegrationProjectAssignments,
    syncResourceProjectAssignments,
    refreshBrandGroup,
    t,
    toast,
    isSavingBrandAssignments,
  ]);

  const openEditGoogleAdsCustomerBrandsModal = useCallback(
    (connection: ConnectionAccessRow, subAccount: GoogleAdsCustomerSubAccount) => {
      if (!canAssignBusinessGroupsForRow(connection)) {
        return;
      }

      setEditingGoogleAdsCustomerBrand({ connection, subAccount });
      const projectIds = getProjectIdsForRow(connection, subAccount.customerId);
      const resources = getProjectResourcesForRow(connection, subAccount.customerId);
      setEditingGoogleAdsCustomerBrandIds(projectIds);
      setEditingGoogleAdsCustomerBrandMaxAccessLevels(Object.fromEntries(projectIds.map((projectId) => [
        projectId,
        toDelegatedProjectAccessLevel(
          resources.find((resource) => resource.brandAccessCeilings?.[projectId])
            ?.brandAccessCeilings?.[projectId],
          "WRITE",
        ),
      ])));
    },
    [canAssignBusinessGroupsForRow, getProjectIdsForRow, getProjectResourcesForRow],
  );

  const closeEditGoogleAdsCustomerBrandsModal = useCallback(() => {
    setEditingGoogleAdsCustomerBrand(null);
    setEditingGoogleAdsCustomerBrandIds([]);
    setEditingGoogleAdsCustomerBrandMaxAccessLevels({});
  }, []);

  const toggleEditingGoogleAdsCustomerBrand = useCallback((brandId: string) => {
    setEditingGoogleAdsCustomerBrandIds((previous) =>
      previous.includes(brandId) ? previous.filter((id) => id !== brandId) : [...previous, brandId],
    );
    setEditingGoogleAdsCustomerBrandMaxAccessLevels((previous) => ({ ...previous, [brandId]: previous[brandId] || 'WRITE' }));
  }, []);

  const openEditSecondaryResourceBrandsModal = useCallback(
    (connection: ConnectionAccessRow, resourceKey = connection.resourceKey) => {
      if (!canAssignBusinessGroupsForRow(connection) || !resourceKey) return;
      const resource = getProjectResourcesForRow(connection, resourceKey)
        .find((candidate) => isSecondaryProjectResource(candidate.resourceType));
      if (!resource) return;
      const projectIds = resource.brandIds || [];
      setEditingSecondaryResourceBrand({ connection, resource });
      setEditingSecondaryResourceBrandIds(projectIds);
      setEditingSecondaryResourceBrandMaxAccessLevels(Object.fromEntries(projectIds.map((projectId) => [
        projectId,
        toDelegatedProjectAccessLevel(resource.brandAccessCeilings?.[projectId], "WRITE"),
      ])));
    },
    [canAssignBusinessGroupsForRow, getProjectResourcesForRow],
  );

  const closeEditSecondaryResourceBrandsModal = useCallback(() => {
    setEditingSecondaryResourceBrand(null);
    setEditingSecondaryResourceBrandIds([]);
    setEditingSecondaryResourceBrandMaxAccessLevels({});
  }, []);

  const toggleEditingSecondaryResourceBrand = useCallback((brandId: string) => {
    setEditingSecondaryResourceBrandIds((previous) =>
      previous.includes(brandId) ? previous.filter((id) => id !== brandId) : [...previous, brandId],
    );
    setEditingSecondaryResourceBrandMaxAccessLevels((previous) => ({ ...previous, [brandId]: previous[brandId] || 'WRITE' }));
  }, []);

  const saveSecondaryResourceBrands = useCallback(async () => {
    if (
      !editingSecondaryResourceBrand ||
      editingSecondaryAssignableBusinessGroups.length === 0 ||
      !editingSecondaryResourceBrandAssignmentHasChanges ||
      isSavingBrandAssignments ||
      brandAssignmentSaveInFlightRef.current
    ) return;
    brandAssignmentSaveInFlightRef.current = true;
    setIsSavingBrandAssignments(true);
    try {
      const { connection, resource } = editingSecondaryResourceBrand;
      await syncResourceProjectAssignments(
        [resource],
        editingSecondaryResourceBrandIds,
        editingSecondaryResourceBrandMaxAccessLevels,
        connection,
      );
      await refreshBrandGroup();
      closeEditSecondaryResourceBrandsModal();
      toast.showToast(t("website.brandsSettings.simple.brandAssignmentsUpdated"), "success");
    } catch {
      toast.showToast(t("website.integrationsSettings.failedToSaveConnectionAccess"), "error");
    } finally {
      brandAssignmentSaveInFlightRef.current = false;
      setIsSavingBrandAssignments(false);
    }
  }, [
    closeEditSecondaryResourceBrandsModal,
    editingSecondaryResourceBrand,
    editingSecondaryResourceBrandAssignmentHasChanges,
    editingSecondaryResourceBrandIds,
    editingSecondaryResourceBrandMaxAccessLevels,
    editingSecondaryAssignableBusinessGroups.length,
    isSavingBrandAssignments,
    refreshBrandGroup,
    syncResourceProjectAssignments,
    t,
    toast,
  ]);

  const saveGoogleAdsCustomerBrands = useCallback(async () => {
    if (
      !editingGoogleAdsCustomerBrand ||
      editingGoogleAdsAssignableBusinessGroups.length === 0 ||
      !editingGoogleAdsCustomerBrandAssignmentHasChanges ||
      isSavingBrandAssignments ||
      brandAssignmentSaveInFlightRef.current
    ) {
      return;
    }

    brandAssignmentSaveInFlightRef.current = true;
    setIsSavingBrandAssignments(true);
    try {
      const resources = getProjectResourcesForRow(
        editingGoogleAdsCustomerBrand.connection,
        editingGoogleAdsCustomerBrand.subAccount.customerId,
      );
      if (resources.length === 0) {
        throw new Error("google_ads_customer_resource_unavailable");
      }
      await syncResourceProjectAssignments(
        resources,
        editingGoogleAdsCustomerBrandIds,
        editingGoogleAdsCustomerBrandMaxAccessLevels,
        editingGoogleAdsCustomerBrand.connection,
      );
      await refreshBrandGroup();
      closeEditGoogleAdsCustomerBrandsModal();
      toast.showToast(t("website.brandsSettings.simple.brandAssignmentsUpdated"), "success");
    } catch {
      toast.showToast(t("website.integrationsSettings.failedToSaveConnectionAccess"), "error");
    } finally {
      brandAssignmentSaveInFlightRef.current = false;
      setIsSavingBrandAssignments(false);
    }
  }, [
    closeEditGoogleAdsCustomerBrandsModal,
    editingGoogleAdsCustomerBrand,
    editingGoogleAdsCustomerBrandIds,
    editingGoogleAdsCustomerBrandMaxAccessLevels,
    editingGoogleAdsCustomerBrandAssignmentHasChanges,
    editingGoogleAdsAssignableBusinessGroups.length,
    getProjectResourcesForRow,
    syncResourceProjectAssignments,
    refreshBrandGroup,
    t,
    toast,
    isSavingBrandAssignments,
  ]);

  const openBulkBrandAssignmentModal = useCallback(() => {
    if (!canAssignBusinessGroups || !defaultBulkBrandAssignmentBrandId) return;
    const draft = createBulkBrandAssignmentDraft(
      defaultBulkBrandAssignmentBrandId,
    );
    setBulkBrandAssignmentUnitKeys(draft.unitKeys);
    setBulkBrandAssignmentBrandId(defaultBulkBrandAssignmentBrandId);
    setBulkBrandAssignmentAccessLevels(draft.accessLevels);
    setIsBulkBrandAssignmentOpen(true);
  }, [canAssignBusinessGroups, createBulkBrandAssignmentDraft, defaultBulkBrandAssignmentBrandId]);

  const closeBulkBrandAssignmentModal = useCallback(() => {
    setIsBulkBrandAssignmentOpen(false);
    setBulkBrandAssignmentUnitKeys([]);
    setBulkBrandAssignmentBrandId(null);
    setBulkBrandAssignmentAccessLevels({});
  }, []);

  const handleBulkBrandAssignmentBrandChange = useCallback((brandId: string) => {
    const nextBrandId = brandId || null;
    const draft = createBulkBrandAssignmentDraft(nextBrandId);
    setBulkBrandAssignmentBrandId(nextBrandId);
    setBulkBrandAssignmentUnitKeys(draft.unitKeys);
    setBulkBrandAssignmentAccessLevels(draft.accessLevels);
  }, [createBulkBrandAssignmentDraft]);

  const toggleBulkBrandAssignmentUnit = useCallback((unitKey: string) => {
    const selectedUnit = brandAssignmentUnits.find((unit) => unit.key === unitKey);
    const defaultAccessLevel = selectedUnit
      ? getNewBulkAssignmentAccessLevel({
          isSecondaryResource: Boolean(getBrandAssignmentUnitResourceKey(selectedUnit)),
          canDelegatePositiveAccess: canDelegatePositiveProjectAccess(selectedUnit.row),
        })
      : "READ";
    setBulkBrandAssignmentUnitKeys((previous) =>
      previous.includes(unitKey) ? previous.filter((key) => key !== unitKey) : [...previous, unitKey],
    );
    setBulkBrandAssignmentAccessLevels((accessLevels) => {
      if (!bulkBrandAssignmentUnitKeys.includes(unitKey)) {
        return {
          ...accessLevels,
          [unitKey]: accessLevels[unitKey] || defaultAccessLevel,
        };
      }
      return accessLevels;
    });
  }, [brandAssignmentUnits, bulkBrandAssignmentUnitKeys, canDelegatePositiveProjectAccess]);

  const updateAllBulkBrandAssignmentAccessLevels = useCallback((accessLevel: "READ" | "WRITE") => {
    const unitKeys = bulkBrandAssignmentEligibleUnitKeys;
    setBulkBrandAssignmentUnitKeys(unitKeys);
    setBulkBrandAssignmentAccessLevels((previous) =>
      unitKeys.reduce<Record<string, "READ" | "WRITE">>(
        (next, unitKey) => {
          next[unitKey] = accessLevel;
          return next;
        },
        { ...previous },
      ),
    );
  }, [bulkBrandAssignmentEligibleUnitKeys]);

  const updateBulkBrandAssignmentAccessLevel = useCallback((unitKey: string, accessLevel: 'READ' | 'WRITE') => {
    setBulkBrandAssignmentUnitKeys((previous) =>
      previous.includes(unitKey) ? previous : [...previous, unitKey],
    );
    setBulkBrandAssignmentAccessLevels((previous) => ({
      ...previous,
      [unitKey]: accessLevel,
    }));
  }, []);

  const saveBulkBrandAssignment = useCallback(async () => {
    if (
      !bulkBrandAssignmentBrandId ||
      !bulkBrandAssignmentHasChanges ||
      isSavingBrandAssignments ||
      brandAssignmentSaveInFlightRef.current
    ) {
      return;
    }
    if (!currentWorkspace?.id) {
      throw new Error("workspace_required");
    }

    brandAssignmentSaveInFlightRef.current = true;
    setIsSavingBrandAssignments(true);
    try {
      const batchRequest: ProjectAssignmentBatchPayload = {
        integrationAssignments: [],
        integrationRemovals: [],
        resourceSyncs: [],
      };
      const resourceSyncsByProjectId = new Map<
        string,
        {
          resources: Map<string, ProjectAssignmentBatchResourceUpsert>;
          removals: Map<string, ProjectAssignmentBatchResourceRemoval>;
        }
      >();
      const getResourceSyncGroup = (projectId: string) => {
        const existing = resourceSyncsByProjectId.get(projectId);
        if (existing) return existing;
        const created = {
          resources: new Map<string, ProjectAssignmentBatchResourceUpsert>(),
          removals: new Map<string, ProjectAssignmentBatchResourceRemoval>(),
        };
        resourceSyncsByProjectId.set(projectId, created);
        return created;
      };
      const resourceSyncKey = (
        resource: Pick<
          BrandScopedResource,
          "provider" | "resourceType" | "resourceKey" | "connectionId"
        >,
      ) =>
        [
          normalizeConnectionIdentity(resource.connectionId),
          normalizeConnectionIdentity(resource.provider),
          normalizeConnectionIdentity(resource.resourceType),
          normalizeConnectionIdentity(resource.resourceKey),
        ].join(":");
      const queueResourceUpsert = (
        projectId: string,
        resource: BrandScopedResource,
        maxAccessLevel?: Exclude<ProjectAssignmentCeiling, null | undefined>,
      ) => {
        const group = getResourceSyncGroup(projectId);
        const key = resourceSyncKey(resource);
        group.resources.set(key, {
          provider: resource.provider,
          resourceType: resource.resourceType,
          resourceKey: resource.resourceKey,
          connectionId: resource.connectionId,
          ...(maxAccessLevel ? { maxAccessLevel } : {}),
        });
        group.removals.delete(key);
      };
      const queueResourceRemoval = (
        projectId: string,
        resource: BrandScopedResource,
      ) => {
        const group = getResourceSyncGroup(projectId);
        const key = resourceSyncKey(resource);
        group.removals.set(key, {
          provider: resource.provider,
          resourceType: resource.resourceType,
          resourceKey: resource.resourceKey,
          connectionId: resource.connectionId,
        });
        group.resources.delete(key);
      };

      const changedBulkBrandAssignmentUnits = brandAssignmentUnits.filter((unit) => {
        const isCurrentlyAssigned = assignedBulkBrandAssignmentUnitKeys.has(unit.key);
        const shouldBeAssigned = bulkBrandAssignmentUnitKeys.includes(unit.key);
        if (isCurrentlyAssigned !== shouldBeAssigned) return true;
        if (!shouldBeAssigned) return false;
        const currentAccessLevel = getBulkBrandAssignmentUnitAccessLevel(unit, bulkBrandAssignmentBrandId);
        const canSetPositiveAccess = canDelegatePositiveProjectAccess(unit.row);
        const desiredAccessLevel = !canSetPositiveAccess && isCurrentlyAssigned
          ? currentAccessLevel
          : !canSetPositiveAccess
            ? "DISABLED"
            : (bulkBrandAssignmentAccessLevels[unit.key]
              || getBulkBrandAssignmentUnitAccessLevel(unit, bulkBrandAssignmentBrandId));
        return currentAccessLevel !== desiredAccessLevel;
      });
      if (
        !changedBulkBrandAssignmentUnits.length &&
        !bulkBrandAssignmentParentCleanupRows.length
      ) return;

      for (const unit of changedBulkBrandAssignmentUnits) {
        const shouldBeAssigned = bulkBrandAssignmentUnitKeys.includes(unit.key);
        const resourceKey = getBrandAssignmentUnitResourceKey(unit);
        const connectionProjectIds = getProjectIdsForRow(unit.row);
        const existingProjectIds = getProjectIdsForRow(unit.row, resourceKey);
        const resources = getProjectResourcesForRow(unit.row, resourceKey);
        const nextConnectionProjectIds = resourceKey
          ? shouldBeAssigned
            ? Array.from(new Set([...connectionProjectIds, bulkBrandAssignmentBrandId]))
            : connectionProjectIds
          : shouldBeAssigned
            ? Array.from(new Set([...connectionProjectIds, bulkBrandAssignmentBrandId]))
            : connectionProjectIds.filter((projectId) => projectId !== bulkBrandAssignmentBrandId);
        const nextConnectionAccessCeilings = Object.fromEntries(
          nextConnectionProjectIds.map((projectId) => [
            projectId,
            projectId === bulkBrandAssignmentBrandId && shouldBeAssigned
              && (!resourceKey || !connectionProjectIds.includes(bulkBrandAssignmentBrandId))
              ? bulkBrandAssignmentAccessLevels[unit.key] || "WRITE"
              : getProjectConnectionForRow(unit.row)?.brandAccessCeilings?.[projectId] || "READ",
          ]),
        );
        const nextResourceProjectIds = shouldBeAssigned
          ? Array.from(new Set([...existingProjectIds, bulkBrandAssignmentBrandId]))
          : existingProjectIds.filter((projectId) => projectId !== bulkBrandAssignmentBrandId);
        const assignableProjectIds = getAssignableBusinessGroupsForRow(unit.row).map(({ id }) => id);
        const canDelegatePositiveAccess = canDelegatePositiveProjectAccess(unit.row);
        const nextResourceAccessCeilings = Object.fromEntries(
          nextResourceProjectIds.map((projectId) => [
            projectId,
            projectId === bulkBrandAssignmentBrandId && shouldBeAssigned
              ? bulkBrandAssignmentAccessLevels[unit.key] || "WRITE"
              : resources.find((resource) => resource.brandAccessCeilings?.[projectId])
                  ?.brandAccessCeilings?.[projectId]
                || (canDelegatePositiveAccess ? "WRITE" : "DISABLED"),
          ]),
        );

        if (!resourceKey) {
          const connection = getProjectConnectionForRow(unit.row);
          const connectionId = await resolveCanonicalProjectConnectionIdWithRetry(
            () => projectService.getIntegrationContract(currentWorkspace.id),
            [...getConnectionRowIdentityKeys(unit.row)],
            {
              attempts: 3,
              wait: () => new Promise((resolve) => window.setTimeout(resolve, 500)),
            },
          );
          const { added, removed, changed } = getManageableProjectAssignmentChanges({
            currentProjectIds: connection?.brandIds || [],
            nextProjectIds: nextConnectionProjectIds,
            currentAccessLevels: connection?.brandAccessCeilings,
            nextAccessLevels: nextConnectionAccessCeilings,
            manageableProjectIds: assignableProjectIds,
            currentDefaultAccessLevel: "WRITE",
            nextDefaultAccessLevel: "WRITE",
          });
          batchRequest.integrationAssignments!.push(
            ...[...added, ...changed].map((projectId) => ({
              projectId,
              connectionId,
              maxAccessLevel: nextConnectionAccessCeilings[projectId] || "DISABLED",
            })),
          );
          batchRequest.integrationRemovals!.push(
            ...removed.map((projectId) => ({ projectId, connectionId })),
          );
        }
        resources.forEach((resource) => {
          const ownsIndependentCeiling = isSecondaryProjectResource(resource.resourceType);
          const effectiveMaxAccessLevels = ownsIndependentCeiling
            ? normalizeProjectAssignmentCeilings(
                unit.row,
                nextResourceAccessCeilings,
                resource.brandIds || [],
                resource.brandAccessCeilings,
              )
            : {};
          const { added, removed, changed } = getManageableProjectAssignmentChanges({
            currentProjectIds: resource.brandIds || [],
            nextProjectIds: nextResourceProjectIds,
            currentAccessLevels: ownsIndependentCeiling ? resource.brandAccessCeilings : {},
            nextAccessLevels: ownsIndependentCeiling ? effectiveMaxAccessLevels : {},
            manageableProjectIds: assignableProjectIds,
          });
          [...added, ...changed].forEach((projectId) => {
            queueResourceUpsert(
              projectId,
              resource,
              ownsIndependentCeiling
                ? (effectiveMaxAccessLevels[projectId] || (canDelegatePositiveAccess ? "WRITE" : "DISABLED"))
                : undefined,
            );
          });
          removed.forEach((projectId) => {
            queueResourceRemoval(projectId, resource);
          });
        });
      }

      for (const row of bulkBrandAssignmentParentCleanupRows) {
        const connectionId = await resolveCanonicalProjectConnectionIdWithRetry(
          () => projectService.getIntegrationContract(currentWorkspace.id),
          [...getConnectionRowIdentityKeys(row)],
          {
            attempts: 3,
            wait: () => new Promise((resolve) => window.setTimeout(resolve, 500)),
          },
        );
        batchRequest.integrationRemovals!.push({
          projectId: bulkBrandAssignmentBrandId,
          connectionId,
        });
      }

      batchRequest.resourceSyncs = [...resourceSyncsByProjectId.entries()].map(
        ([projectId, group]) => ({
          projectId,
          resources: [...group.resources.values()],
          removals: [...group.removals.values()],
        }),
      ).filter(({ resources, removals }) => resources.length > 0 || removals.length > 0);
      if (
        !batchRequest.integrationAssignments.length &&
        !batchRequest.integrationRemovals.length &&
        !batchRequest.resourceSyncs.length
      ) {
        return;
      }
      await projectService.applyProjectAssignmentBatch(currentWorkspace.id, batchRequest);

      closeBulkBrandAssignmentModal();
      toast.showToast(t("website.brandsSettings.simple.brandAssignmentsUpdated"), "success");
      void refreshBrandGroupAfterAssignmentSave(refreshBrandGroup);
    } catch {
      toast.showToast(t("website.integrationsSettings.failedToSaveConnectionAccess"), "error");
    } finally {
      brandAssignmentSaveInFlightRef.current = false;
      setIsSavingBrandAssignments(false);
    }
  }, [
    assignedBulkBrandAssignmentUnitKeys,
    brandAssignmentUnits,
    bulkBrandAssignmentHasChanges,
    bulkBrandAssignmentBrandId,
    bulkBrandAssignmentParentCleanupRows,
    bulkBrandAssignmentUnitKeys,
    bulkBrandAssignmentAccessLevels,
    canDelegatePositiveProjectAccess,
    closeBulkBrandAssignmentModal,
    getBulkBrandAssignmentUnitAccessLevel,
    getProjectConnectionForRow,
    getProjectIdsForRow,
    getProjectResourcesForRow,
    refreshBrandGroup,
    t,
    toast,
    isSavingBrandAssignments,
  ]);

  const handleConnectionDisconnect = useCallback(
    async (row: ConnectionAccessRow) => {
      const canDisconnectConnection =
        row.isConnectionOwnedByCurrentUser || isCurrentMemberWorkspaceOwner;
      if (!canDisconnectConnection) {
        if (connectionScene === "admin") {
          logAdminWrite("blocked", {
            area: "integration",
            action: "disconnect",
            workspace_id: currentWorkspace?.id,
            entity_id: getConnectionAccessBackendId(row) || undefined,
            block_reason: "permission",
          });
        }
        toast.showToast(t("website.integrationsSettings.disconnectPermissionDenied"), "error");
        return;
      }

      const connectionId = getConnectionDisconnectTrackingId(row);
      const disconnectOperationKey = `${row.provider}:${connectionId || row.id}`;
      const hasGoogleAdsSubAccounts = row.provider === "Google Ads" && Boolean(row.subAccounts?.length);
      if (disconnectOperationKeysRef.current.has(disconnectOperationKey)) {
        return;
      }

      disconnectOperationKeysRef.current.add(disconnectOperationKey);
      const connectionName = row.accountId || row.name || row.provider;
      const confirmed = await showDialog({
        title: hasGoogleAdsSubAccounts
          ? t("website.integrationsSettings.disconnectGoogleAdsConnectionTitle")
          : t("website.integrationsSettings.disconnectTitle", {
              target: connectionName,
            }),
        subtitle: hasGoogleAdsSubAccounts
          ? t(isPluginAdmin
              ? "website.integrationsSettings.pluginDisconnectGoogleAdsConnectionSubtitle"
              : "website.integrationsSettings.disconnectGoogleAdsConnectionSubtitle", {
              count: row.subAccounts?.length || 0,
            })
          : t(isPluginAdmin
              ? "website.integrationsSettings.pluginDisconnectSubtitle"
              : "website.integrationsSettings.disconnectSubtitle"),
        confirmText: t("website.integrationsSettings.disconnect"),
        cancelText: t("common.cancel"),
        destructive: true,
      });

      if (!confirmed) {
        if (connectionScene === "admin") {
          logAdminWrite("cancelled", {
            area: "integration",
            action: "disconnect",
            workspace_id: currentWorkspace?.id,
            entity_id: getConnectionAccessBackendId(row) || undefined,
          });
        }
        disconnectOperationKeysRef.current.delete(disconnectOperationKey);
        return;
      }

      if (connectionId) {
        setDisconnectingConnectionIds((previous) => new Set(previous).add(connectionId));
      }

      const tracking = connectionScene === "admin"
        ? startAdminWrite({
            area: "integration",
            action: "disconnect",
            workspace_id: currentWorkspace?.id,
            entity_id: getConnectionAccessBackendId(row) || undefined,
          })
        : null;
      let mutationSucceeded = false;
      try {
        if (isMcpBackedDisconnectConnection(row)) {
          const workspaceId = currentWorkspace?.id;
          if (!workspaceId) {
            throw new Error("Workspace ID is required");
          }

          const connectors = await mcpConnectorService.list(workspaceId);
          const connector = resolveMcpConnectorForDisconnect(connectors, row);
          if (!connector) {
            throw new Error("MCP connector not found");
          }
          await mcpConnectorService.disconnect(workspaceId, connector.connectorId);
          mutationSucceeded = true;
        } else {
          await integrations.disconnect(getDisconnectPlatformValue(row.provider), {
            connectionId,
            connectionBackend: row.connectionBackend,
            workspaceIntegrationId: row.workspaceIntegrationId || row.id,
            shopDomain: row.provider === "Shopify" ? row.accountId || row.name : undefined,
            authType: row.authType,
            native: row.native,
            connectionScene,
            onMutationSucceeded: () => {
              mutationSucceeded = true;
            },
            onPostWriteFailed: (refreshError) => {
              tracking?.postWriteFailed(refreshError, { failure_stage: "refresh" });
            },
          });
        }
        if (activeAccessConnectionId === row.id) {
          setActiveAccessConnectionId(null);
        }
        if (editingLabelConnection?.id === row.id) {
          setEditingLabelConnection(null);
          setEditingLabelValue("");
        }
        try {
          await onConnectionDisconnected?.();
        } catch (refreshError) {
          tracking?.postWriteFailed(refreshError, { failure_stage: "connector_refresh" });
          throw refreshError;
        }
        toast.showToast(
          t("website.integrationsSettings.disconnectedSuccess", {
            tool: row.provider,
          }),
          "success",
        );
        void integrations.refresh(false, { forceRefresh: true, throwOnError: true }).then(
          () => tracking?.succeeded(),
          (error) => {
            tracking?.postWriteFailed(error, { failure_stage: "refresh" });
            console.warn("[ConnectionAccessSettings] Failed to refresh integrations after disconnect", error);
          },
        );
      } catch (error: any) {
        if (mutationSucceeded) {
          tracking?.postWriteFailed(error, { failure_stage: "refresh" });
        } else {
          tracking?.failed(error);
        }
        toast.showToast(
          `${t("website.integrationsSettings.disconnectFailed")}: ${normalizeConnectionUserMessage(error.message, t("website.integrationsSettings.failedToDisconnectFromTool", { tool: row.provider }))}`,
          "error",
        );
      } finally {
        disconnectOperationKeysRef.current.delete(disconnectOperationKey);
        if (connectionId) {
          setDisconnectingConnectionIds((previous) => {
            const next = new Set(previous);
            next.delete(connectionId);
            return next;
          });
        }
      }
    },
    [
      activeAccessConnectionId,
      connectionScene,
      currentWorkspace?.id,
      editingLabelConnection?.id,
      integrations,
      isCurrentMemberWorkspaceOwner,
      isPluginAdmin,
      onConnectionDisconnected,
      showDialog,
      t,
      toast,
    ],
  );

  const handleRemoveConnectionFromBrand = useCallback(
    async (row: ConnectionAccessRow, selectedResourceKey?: string) => {
      if (!brandId || !canRemoveConnectionsFromBrand) {
        return;
      }

      const connectionName = row.accountId || row.name || row.provider;
      const confirmed = await showDialog({
        title: t("website.integrationsSettings.removeConnectionFromBrandTitle", {
          account: connectionName,
          brand: activeBrandName,
        }),
        subtitle: t("website.integrationsSettings.removeConnectionFromBrandSubtitle", {
          brand: activeBrandName,
        }),
        confirmText: t("website.integrationsSettings.removeConnectionFromBrand"),
        cancelText: t("common.cancel"),
        destructive: true,
      });

      if (!confirmed) {
        return;
      }

      const resourceKey = selectedResourceKey || (isSecondaryProjectResource(row.resourceType)
        ? row.resourceKey
        : undefined);

      if (row.provider === "Google Ads" && row.subAccounts?.length) {
        for (const subAccount of row.subAccounts) {
          const resources = getProjectResourcesForRow(row, subAccount.customerId);
          const nextProjectIds = getProjectIdsForRow(row, subAccount.customerId).filter(
            (projectId) => projectId !== brandId,
          );
          await syncResourceProjectAssignments(resources, nextProjectIds, {}, row);
        }
      } else {
        const resources = getProjectResourcesForRow(row, resourceKey);
        const nextProjectIds = getProjectIdsForRow(row, resourceKey).filter(
          (projectId) => projectId !== brandId,
        );
        await syncResourceProjectAssignments(resources, nextProjectIds, {}, row);
      }
      const remainingResources = Boolean(resourceKey) &&
        getProjectResourcesForRow(row).some(
          (resource) =>
            resource.resourceKey !== resourceKey && (resource.brandIds || []).includes(brandId),
        );
      if (!remainingResources) {
        await removeIntegrationFromBrand(
          brandId,
          Number(getProjectConnectionForRow(row)?.id || row.workspaceIntegrationId || row.id),
          { refresh: false },
        );
      }

      await refreshBrandGroup();
      await onConnectionDisconnected?.();
      toast.showToast(
        t("website.integrationsSettings.removedConnectionFromBrandSuccess", {
          account: connectionName,
          brand: activeBrandName,
        }),
        "success",
      );
    },
    [
      activeBrandName,
      brandId,
      canRemoveConnectionsFromBrand,
      getProjectIdsForRow,
      getProjectConnectionForRow,
      getProjectResourcesForRow,
      onConnectionDisconnected,
      showDialog,
      removeIntegrationFromBrand,
      refreshBrandGroup,
      syncResourceProjectAssignments,
      t,
      toast,
    ],
  );

  const handleGoogleAdsCustomerDisconnect = useCallback(
    async (row: ConnectionAccessRow, subAccount: GoogleAdsCustomerSubAccount) => {
      if (!row.isManagedByCurrentUser) {
        if (connectionScene === "admin") {
          logAdminWrite("blocked", {
            area: "integration",
            action: "disconnect",
            workspace_id: currentWorkspace?.id,
            entity_id: getConnectionAccessBackendId(row) || undefined,
            resource_kind: "google_ads_customer",
            block_reason: "permission",
          });
        }
        toast.showToast(t("website.integrationsSettings.disconnectPermissionDenied"), "error");
        return;
      }

      const connectionId = getConnectionDisconnectTrackingId(row);
      const customerOperationKey = getGoogleAdsCustomerDisconnectOperationKey(row, subAccount.customerId);
      if (!connectionId || googleAdsCustomerDisconnectOperationKeysRef.current.has(customerOperationKey)) {
        return;
      }

      const customerName = subAccount.accountName || subAccount.customerId;
      const confirmed = await showDialog({
        title: t("website.integrationsSettings.disconnectGoogleAdsCustomerTitle", {
          account: customerName,
        }),
        subtitle: t(
          isPluginAdmin
            ? "website.integrationsSettings.pluginDisconnectGoogleAdsCustomerSubtitle"
            : "website.integrationsSettings.disconnectGoogleAdsCustomerSubtitle",
        ),
        confirmText: t("website.integrationsSettings.disconnectGoogleAdsCustomer"),
        cancelText: t("common.cancel"),
        destructive: true,
      });

      if (!confirmed) {
        if (connectionScene === "admin") {
          logAdminWrite("cancelled", {
            area: "integration",
            action: "disconnect",
            workspace_id: currentWorkspace?.id,
            entity_id: getConnectionAccessBackendId(row) || undefined,
            resource_kind: "google_ads_customer",
          });
        }
        return;
      }

      googleAdsCustomerDisconnectOperationKeysRef.current.add(customerOperationKey);
      setDisconnectingGoogleAdsCustomerKeys((previous) => new Set(previous).add(customerOperationKey));

      const tracking = connectionScene === "admin"
        ? startAdminWrite({
            area: "integration",
            action: "disconnect",
            workspace_id: currentWorkspace?.id,
            entity_id: getConnectionAccessBackendId(row) || undefined,
            resource_kind: "google_ads_customer",
          })
        : null;
      let mutationSucceeded = false;
      try {
        await authService.disconnectNangoGoogleResource({
          provider: 'google-ads',
          connectionId,
          resourceKey: subAccount.customerId,
          axiosRequestConfig: buildWorkspaceRequestConfig(),
        });
        mutationSucceeded = true;

        if (activeAccessConnectionId === row.id && activeAccessCustomerId === subAccount.customerId) {
          setActiveAccessConnectionId(null);
          setActiveAccessCustomerId(null);
        }
        await refreshBrandGroup();
        await onConnectionDisconnected?.();
        toast.showToast(
          t("website.integrationsSettings.googleAdsCustomerDisconnectedSuccess", {
            account: customerName,
          }),
          "success",
        );
        try {
          await integrations.refresh(false, { forceRefresh: true, throwOnError: true });
        } catch (error) {
          tracking?.postWriteFailed(error, { failure_stage: "refresh" });
        }
        tracking?.succeeded();
      } catch (error: any) {
        if (mutationSucceeded) {
          tracking?.postWriteFailed(error, { failure_stage: "refresh" });
        } else {
          tracking?.failed(error);
        }
        toast.showToast(
          `${t("website.integrationsSettings.disconnectGoogleAdsCustomerFailed")}: ${normalizeConnectionUserMessage(
            error.message,
            t("website.integrationsSettings.disconnectGoogleAdsCustomerFailed"),
          )}`,
          "error",
        );
      } finally {
        googleAdsCustomerDisconnectOperationKeysRef.current.delete(customerOperationKey);
        setDisconnectingGoogleAdsCustomerKeys((previous) => {
          const next = new Set(previous);
          next.delete(customerOperationKey);
          return next;
        });
      }
    },
    [
      activeAccessConnectionId,
      activeAccessCustomerId,
      buildWorkspaceRequestConfig,
      connectionScene,
      currentWorkspace?.id,
      integrations,
      isPluginAdmin,
      onConnectionDisconnected,
      refreshBrandGroup,
      showDialog,
      t,
      toast,
    ],
  );

  const handleGoogleResourceDisconnect = useCallback(
    async (row: ConnectionAccessRow, resource: BrandScopedResource) => {
      if (!row.isManagedByCurrentUser) {
        if (connectionScene === "admin") {
          logAdminWrite("blocked", {
            area: "integration",
            action: "disconnect",
            workspace_id: currentWorkspace?.id,
            entity_id: getConnectionAccessBackendId(row) || undefined,
            resource_kind: "google_resource",
            block_reason: "permission",
          });
        }
        toast.showToast(t("website.integrationsSettings.disconnectPermissionDenied"), "error");
        return;
      }

      const provider = getGoogleResourceDisconnectProvider(row.provider);
      const connectionId = getConnectionDisconnectTrackingId(row);
      const operationKey = getGoogleResourceDisconnectOperationKey(row, resource.resourceKey);
      if (!provider || !connectionId || googleResourceDisconnectOperationKeysRef.current.has(operationKey)) {
        return;
      }

      const resourceName = resource.name || resource.resourceKey;
      const confirmed = await showDialog({
        title: t("website.integrationsSettings.disconnectGoogleResourceTitle", { account: resourceName }),
        subtitle: t(
          isPluginAdmin
            ? "website.integrationsSettings.pluginDisconnectGoogleResourceSubtitle"
            : "website.integrationsSettings.disconnectGoogleResourceSubtitle",
        ),
        confirmText: t("website.integrationsSettings.disconnect"),
        cancelText: t("common.cancel"),
        destructive: true,
      });
      if (!confirmed) {
        if (connectionScene === "admin") {
          logAdminWrite("cancelled", {
            area: "integration",
            action: "disconnect",
            workspace_id: currentWorkspace?.id,
            entity_id: getConnectionAccessBackendId(row) || undefined,
            resource_kind: "google_resource",
          });
        }
        return;
      }

      googleResourceDisconnectOperationKeysRef.current.add(operationKey);
      setDisconnectingGoogleResourceKeys((previous) => new Set(previous).add(operationKey));
      const tracking = connectionScene === "admin"
        ? startAdminWrite({
            area: "integration",
            action: "disconnect",
            workspace_id: currentWorkspace?.id,
            entity_id: getConnectionAccessBackendId(row) || undefined,
            resource_kind: "google_resource",
          })
        : null;
      let mutationSucceeded = false;
      try {
        await authService.disconnectNangoGoogleResource({
          provider,
          connectionId,
          resourceKey: resource.resourceKey,
          axiosRequestConfig: buildWorkspaceRequestConfig(),
        });
        mutationSucceeded = true;
        if (activeAccessConnectionId === row.id) {
          setActiveAccessConnectionId(null);
        }
        await refreshBrandGroup();
        await onConnectionDisconnected?.();
        toast.showToast(
          t("website.integrationsSettings.googleResourceDisconnectedSuccess", { account: resourceName }),
          "success",
        );
        try {
          await integrations.refresh(false, { forceRefresh: true, throwOnError: true });
        } catch (error) {
          tracking?.postWriteFailed(error, { failure_stage: "refresh" });
        }
        tracking?.succeeded();
      } catch (error: any) {
        if (mutationSucceeded) {
          tracking?.postWriteFailed(error, { failure_stage: "refresh" });
        } else {
          tracking?.failed(error);
        }
        toast.showToast(
          `${t("website.integrationsSettings.disconnectGoogleResourceFailed")}: ${normalizeConnectionUserMessage(
            error.message,
            t("website.integrationsSettings.disconnectGoogleResourceFailed"),
          )}`,
          "error",
        );
      } finally {
        googleResourceDisconnectOperationKeysRef.current.delete(operationKey);
        setDisconnectingGoogleResourceKeys((previous) => {
          const next = new Set(previous);
          next.delete(operationKey);
          return next;
        });
      }
    },
    [
      activeAccessConnectionId,
      buildWorkspaceRequestConfig,
      connectionScene,
      currentWorkspace?.id,
      integrations,
      isPluginAdmin,
      onConnectionDisconnected,
      refreshBrandGroup,
      showDialog,
      t,
      toast,
    ],
  );

  const handleRemoveGoogleAdsCustomerFromBrand = useCallback(
    async (row: ConnectionAccessRow, subAccount: GoogleAdsCustomerSubAccount) => {
      if (!brandId || !canRemoveConnectionsFromBrand) {
        return;
      }

      const customerName = subAccount.accountName || subAccount.customerId;
      const confirmed = await showDialog({
        title: t("website.integrationsSettings.removeConnectionFromBrandTitle", {
          account: customerName,
          brand: activeBrandName,
        }),
        subtitle: t("website.integrationsSettings.removeConnectionFromBrandSubtitle", {
          brand: activeBrandName,
        }),
        confirmText: t("website.integrationsSettings.removeConnectionFromBrand"),
        cancelText: t("common.cancel"),
        destructive: true,
      });

      if (!confirmed) {
        return;
      }

      const resources = getProjectResourcesForRow(row, subAccount.customerId);
      const nextProjectIds = getProjectIdsForRow(row, subAccount.customerId).filter(
        (projectId) => projectId !== brandId,
      );
      await syncResourceProjectAssignments(resources, nextProjectIds, {}, row);
      await refreshBrandGroup();
      if (activeAccessConnectionId === row.id && activeAccessCustomerId === subAccount.customerId) {
        setActiveAccessConnectionId(null);
        setActiveAccessCustomerId(null);
      }
      await onConnectionDisconnected?.();
      toast.showToast(
        t("website.integrationsSettings.removedConnectionFromBrandSuccess", {
          account: customerName,
          brand: activeBrandName,
        }),
        "success",
      );
    },
    [
      activeAccessConnectionId,
      activeAccessCustomerId,
      activeBrandName,
      brandId,
      canRemoveConnectionsFromBrand,
      getProjectIdsForRow,
      getProjectResourcesForRow,
      onConnectionDisconnected,
      showDialog,
      syncResourceProjectAssignments,
      refreshBrandGroup,
      t,
      toast,
    ],
  );

  const handleAccessDraftChange = (memberKey: string, level: ConnectionAccessLevel) => {
    if (!canEditActiveConnectionMemberAccess || !activeEditableAssignableMemberKeys.has(memberKey)) {
      return;
    }

    setModalAccessDraft((previous) => ({
      ...previous,
      [memberKey]: level,
    }));

    if (activeAccessCustomerId) {
      setModalGoogleAdsCustomerDraft((previous) => {
        const current = new Set(previous[memberKey] ?? []);
        if (level === "no_access") {
          current.delete(activeAccessCustomerId);
        } else {
          current.add(activeAccessCustomerId);
        }
        return {
          ...previous,
          [memberKey]: Array.from(current),
        };
      });
    }
  };

  const handleManageAccessDraftChange = (memberKey: string, checked: boolean) => {
    if (
      !isPluginAdmin ||
      !canEditActiveConnectionMemberAccess ||
      !activeAccessRow ||
      !activeAccessRow.isConnectionOwnedByCurrentUser
    ) {
      return;
    }

    setModalManageAccessDraft((previous) => ({
      ...previous,
      [memberKey]: checked,
    }));
  };

  const handleSaveUserAccess = useCallback(async () => {
    if (!activeAccessRow || !currentWorkspace?.id || isSavingActiveAccess || !canEditActiveConnectionMemberAccess) {
      return;
    }

    const integrationId = getConnectionAccessBackendId(activeAccessRow);
    if (!integrationId) {
      toast.showToast(t("website.integrationsSettings.connectionAccessUnavailable"), "error");
      return;
    }

    const savedAccess = connectionAccessById[activeAccessRow.id] ?? {};
    const savedManageAccess = connectionManageAccessById[activeAccessRow.id] ?? {};
    const grants = buildConnectionAccessUpdateGrants(
      activeAssignableMembers,
      modalAccessDraft,
      savedAccess,
      modalManageAccessDraft,
      savedManageAccess,
    ).map((grant) => {
      if (!isActiveAccessGoogleAds) {
        return grant;
      }
      const member = activeAssignableMembers.find((candidate) => candidate.userId === grant.userId);
      const savedGoogleAdsCustomerAccess = googleAdsCustomerAccessById[activeAccessRow.id] ?? {};
      const googleAdsCustomerIds = member
        ? (modalGoogleAdsCustomerDraft[member.key] ?? savedGoogleAdsCustomerAccess[member.key] ?? [])
        : [];
      const selectedMinimumUnitAccess = member
        ? (modalAccessDraft[member.key] ?? savedAccess[member.key] ?? "no_access")
        : "no_access";
      const accessLevel =
        selectedMinimumUnitAccess === "no_access" && googleAdsCustomerIds.length > 0
          ? mapUiConnectionAccessToBackend(member ? (savedAccess[member.key] ?? "read") : "read")
          : mapUiConnectionAccessToBackend(selectedMinimumUnitAccess);
      return {
        ...grant,
        accessLevel,
        googleAdsCustomerIds,
      };
    });
    if (grants.length === 0) {
      closeUserAccessModal();
      return;
    }

    setSavingAccessConnectionIds((previous) => new Set(previous).add(activeAccessRow.id));
    let tracking: ReturnType<typeof startAdminWrite> | null = null;
    let mutationSucceeded = false;
    let postWriteStage = "client_state";
    try {
      const accessUpdateRequestConfig = buildWorkspaceRequestConfig();
      tracking = connectionScene === "admin"
        ? startAdminWrite({
            area: "integration",
            action: "update_access",
            workspace_id: currentWorkspace.id,
            entity_id: String(integrationId),
            grant_count: grants.length,
          })
        : null;
      const response = await authService.updateWorkspaceIntegrationAccessGrants(integrationId, {
        grants,
        axiosRequestConfig: accessUpdateRequestConfig,
      });
      mutationSucceeded = true;
      const nextAccess = buildAccessMatrixFromBackendGrants(response.accessGrants ?? [], members);
      const nextManageAccess = buildManageAccessMatrixFromBackendGrants(response.accessGrants ?? [], members);
      setConnectionAccessById((previous) => ({
        ...previous,
        [activeAccessRow.id]: nextAccess,
      }));
      setConnectionManageAccessById((previous) => ({
        ...previous,
        [activeAccessRow.id]: nextManageAccess,
      }));
      if (isActiveAccessGoogleAds) {
        setGoogleAdsCustomerAccessById((previous) => ({
          ...previous,
          [activeAccessRow.id]: buildGoogleAdsCustomerAccessMatrixFromBackendGrants(
            response.googleAdsCustomerGrants,
            members,
          ),
        }));
        setGoogleAdsCustomerAccessLevelsById((previous) => ({
          ...previous,
          [activeAccessRow.id]: buildGoogleAdsCustomerAccessLevelMatrixFromBackendGrants(
            response.googleAdsCustomerGrants,
            members,
          ),
        }));
      }
      toast.showToast(t("website.integrationsSettings.connectionAccessUpdated"), "success");
      void integrations.refresh(false, { forceRefresh: true, throwOnError: true }).then(
        () => tracking?.succeeded(),
        (error) => {
          tracking?.postWriteFailed(error, { failure_stage: "integrations_refresh" });
          console.warn("[ConnectionAccessSettings] Failed to refresh integrations after access update", error);
        },
      );
      closeUserAccessModal();
    } catch (error: any) {
      if (mutationSucceeded) {
        tracking?.postWriteFailed(error, { failure_stage: postWriteStage });
      } else {
        tracking?.failed(error);
      }
      const message = normalizeConnectionUserMessage(
        error?.message,
        t("website.integrationsSettings.failedToSaveConnectionAccess"),
      );
      toast.showToast(message, "error");
    } finally {
      setSavingAccessConnectionIds((previous) => {
        const next = new Set(previous);
        next.delete(activeAccessRow.id);
        return next;
      });
    }
  }, [
    activeAccessRow,
    activeAssignableMembers,
    buildWorkspaceRequestConfig,
    canEditActiveConnectionMemberAccess,
    closeUserAccessModal,
    connectionScene,
    connectionAccessById,
    connectionManageAccessById,
    currentWorkspace?.id,
    googleAdsCustomerAccessById,
    integrations,
    isSavingActiveAccess,
    members,
    modalAccessDraft,
    modalGoogleAdsCustomerDraft,
    modalManageAccessDraft,
    t,
    toast,
    isActiveAccessGoogleAds,
  ]);

  const handleSelectAllAccess = useCallback(
    (level: ConnectionAccessLevel) => {
      if (!canEditActiveConnectionMemberAccess) return;

      setModalAccessDraft((prev) => {
        const next = { ...prev };
        activeEditableAssignableMembers.forEach((member) => {
          next[member.key] = level;
        });
        return next;
      });

      if (activeAccessCustomerId) {
        setModalGoogleAdsCustomerDraft((previous) => {
          const next = { ...previous };
          activeEditableAssignableMembers.forEach((member) => {
            const customerIds = new Set(previous[member.key] ?? []);
            if (level === "no_access") {
              customerIds.delete(activeAccessCustomerId);
            } else {
              customerIds.add(activeAccessCustomerId);
            }
            next[member.key] = Array.from(customerIds);
          });
          return next;
        });
      }
    },
    [activeAccessCustomerId, activeEditableAssignableMembers, canEditActiveConnectionMemberAccess],
  );

  const selectAllState = useMemo(() => {
    if (!activeAccessRow) return { no_access: false, read: false, action: false };

    let noAccessCount = 0;
    let viewOnlyCount = 0;
    let actionCount = 0;
    const assignableCount = activeEditableAssignableMembers.length;

    activeEditableAssignableMembers.forEach((member) => {
      const savedAccess = connectionAccessById[activeAccessRow.id]?.[member.key] ?? "no_access";
      const access = modalAccessDraft[member.key] ?? savedAccess;
      if (access === "no_access") noAccessCount++;
      else if (access === "read") viewOnlyCount++;
      else if (access === "action") actionCount++;
    });

    return {
      no_access: assignableCount > 0 && noAccessCount === assignableCount,
      read: assignableCount > 0 && viewOnlyCount === assignableCount,
      action: assignableCount > 0 && actionCount === assignableCount,
    };
  }, [activeAccessRow, activeEditableAssignableMembers, modalAccessDraft, connectionAccessById]);

  const [memberSearchQuery, setMemberSearchQuery] = useState("");

  const filteredAssignableMembers = useMemo(() => {
    const query = memberSearchQuery.trim().toLowerCase();
    if (!query) return activeAssignableMembers;
    return activeAssignableMembers.filter(
      (member) =>
        member.name.toLowerCase().includes(query) ||
        member.email.toLowerCase().includes(query) ||
        member.role.toLowerCase().includes(query),
    );
  }, [activeAssignableMembers, memberSearchQuery]);

  const totalMembers = filteredAssignableMembers.length;
  const totalMembersPages = Math.max(1, Math.ceil(totalMembers / MEMBERS_PER_PAGE));
  const visibleMembersPages = useMemo(() => {
    if (totalMembersPages <= MAX_VISIBLE_PAGE_BUTTONS) {
      return Array.from({ length: totalMembersPages }, (_, idx) => idx + 1);
    }
    const half = Math.floor(MAX_VISIBLE_PAGE_BUTTONS / 2);
    let start = Math.max(1, membersPage - half);
    let end = start + MAX_VISIBLE_PAGE_BUTTONS - 1;
    if (end > totalMembersPages) {
      end = totalMembersPages;
      start = end - MAX_VISIBLE_PAGE_BUTTONS + 1;
    }
    return Array.from({ length: end - start + 1 }, (_, idx) => start + idx);
  }, [membersPage, totalMembersPages]);

  const paginatedMembers = useMemo(() => {
    const startIndex = (membersPage - 1) * MEMBERS_PER_PAGE;
    return filteredAssignableMembers.slice(startIndex, startIndex + MEMBERS_PER_PAGE);
  }, [filteredAssignableMembers, membersPage]);

  const canShowBulkBrandAssignmentAction =
    showBrandAssignmentControls &&
    canAssignBusinessGroups &&
    !providerFilter &&
    connectionFilter === "owned_by_me";

  const bulkBrandAssignmentAction =
    canShowBulkBrandAssignmentAction && brandAssignmentUnits.length > 0 ? (
      <button
        type="button"
        onClick={openBulkBrandAssignmentModal}
        disabled={!brandAssignmentUnits.length}
        className="inline-flex items-center gap-2 rounded-lg border border-[#C8D0E2] bg-white px-3.5 py-2 text-sm font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC] disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Briefcase size={16} weight="regular" />
        {t("website.integrationsSettings.bulkAssignAppsToBrands")}
      </button>
    ) : null;

  return (
    <div className={showHeader ? "space-y-6" : "space-y-3"}>
      {showHeader ? (
        <div className="max-w-3xl space-y-2">
          <h1 className="text-[20px] font-medium tracking-[-0.01em] text-[#1A162F]">
            {t("website.integrationsSettings.myConnectionsTitle")}
          </h1>
          <p className="max-w-2xl text-sm leading-6 text-slate-600">
            {t("website.integrationsSettings.connectionAccessSubtitle")}
          </p>
        </div>
      ) : null}

      {showScopeSwitcher && onConnectionFilterChange ? (
        <ConnectionScopeFilter
          value={connectionFilter}
          onChange={onConnectionFilterChange}
          t={t}
          brandScoped={Boolean(brandId)}
        />
      ) : null}

      {bulkBrandAssignmentAction
        ? brandAssignmentActionTargetId
          ? brandAssignmentActionTarget
            ? createPortal(bulkBrandAssignmentAction, brandAssignmentActionTarget)
            : null
          : <div className="flex justify-end">{bulkBrandAssignmentAction}</div>
        : null}

      {hasProjectIntegrationContractError ? (
        <div
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {t("website.integrationsSettings.failedToLoadConnectionAccess")}
        </div>
      ) : null}

      {connectionAccessViewState === "loading" ? (
        <div className="min-h-[520px] overflow-hidden rounded-xl border border-slate-200 bg-white [overflow-anchor:none]">
          <div className="grid grid-cols-[32%_18%_20%_30%] border-b border-slate-100 bg-slate-50/70 px-5 py-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="h-3 w-20 animate-pulse rounded bg-slate-100" />
            ))}
          </div>
          <div className="divide-y divide-slate-100">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="grid grid-cols-[32%_18%_20%_30%] items-center px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 shrink-0 animate-pulse rounded-lg bg-slate-100" />
                  <div className="space-y-2">
                    <div className="h-3 w-40 animate-pulse rounded bg-slate-100" />
                    <div className="h-3 w-52 animate-pulse rounded bg-slate-50" />
                  </div>
                </div>
                <div className="h-3 w-16 animate-pulse rounded bg-slate-100" />
                <div className="h-6 w-24 animate-pulse rounded-full bg-slate-100" />
                <div className="h-7 w-40 animate-pulse rounded-md bg-slate-100" />
              </div>
            ))}
          </div>
        </div>
      ) : connectionAccessViewState === "empty" ? (
        <div className="flex min-h-[520px] flex-col items-center justify-center px-6 pb-20 text-center [overflow-anchor:none] md:pb-24">
          <CirclesFour className="h-10 w-10 text-slate-300" />
          <h3 className="mt-4 text-base font-semibold text-slate-950">
            {t(
              isConnectionSearchEmpty
                ? "website.integrationsSettings.noConnectionMatches"
                : brandId
                  ? "website.brandsSettings.simple.noConnectionsForBrand"
                  : connectionFilter === "owned_by_me"
                    ? "website.integrationsSettings.noMyConnections"
                    : "website.integrationsSettings.noWorkspaceConnections",
            )}
          </h3>
          <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">
            {t(
              isConnectionSearchEmpty
                ? "website.integrationsSettings.noConnectionMatchesDescription"
                : brandId
                  ? "website.brandsSettings.simple.noIntegrations"
                  : connectionFilter === "owned_by_me"
                    ? "website.integrationsSettings.noMyConnectionsDescription"
                    : "website.integrationsSettings.noWorkspaceConnectionsDescription",
            )}
          </p>
          {!hasConnectionSearchQuery && onBrowseIntegrations ? (
            <button
              type="button"
              onClick={onBrowseIntegrations}
              className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-[#1A162F] px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#2A2446] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/20"
            >
              <span>
                {t(
                  brandId
                    ? "website.brandsSettings.simple.goToIntegrations"
                    : "website.integrationsSettings.browseIntegrations",
                )}
              </span>
              <CaretRight size={14} weight="bold" />
            </button>
          ) : null}
        </div>
      ) : (
        <section className="min-h-[520px] space-y-3 [overflow-anchor:none]">
          {orderedScopedVisibleRows.map((row) => {
            const providerKey = getConnectionGroupKey(row.provider);
            const brandIcon = showProviderIcon ? getBrandIcon(row.provider) : "";
            const isProviderAnchor = providerAnchorRowIds.get(providerKey) === row.id;
            const rowElementKey = getConnectionRowElementKey(row);
            const isHighlightedRow = highlightedRowId === row.id;
            const connectionMetaLine = formatConnectionMetaLine(t, locale, row);
            const connectionPrimaryIdentity = row.accountId && row.accountId !== "-" ? row.accountId : row.name || row.provider;
            const connectionProvider = row.integrationType || row.provider;
            const currentUserAccessLabel = accessLabel(row.currentUserAccess);
            const businessGroupAccessCeilingLabel = accessLabel(
              getBusinessGroupAccessCeilingForRow(row, row.resourceKey),
            );
            const isConnectionActive = isIntegrationConnectionActive(row);
            const hasSubAccounts = row.subAccounts && row.subAccounts.length > 0;
            const isSecondaryResourceRow = isSecondaryProjectResource(row.resourceType);
            const secondaryProjectResources = getProjectResourcesForRow(row).filter(
              (resource) =>
                isSecondaryProjectResource(resource.resourceType) &&
                (brandId || resource.isInResourceContract !== false) &&
                (!brandId || getProjectIdsForRow(row, resource.resourceKey).includes(brandId)),
            );
            const hasSecondaryProjectResources = secondaryProjectResources.length > 0;
            const hasNestedProjectResources = hasSubAccounts || hasSecondaryProjectResources;
            // In a Business-group view, connection status belongs to the
            // workspace integration manager. The group page focuses on the
            // accounts and access granted to this group; showing a transient
            // "Connected" badge here makes the access-loading transition
            // read like a second, contradictory state.
            const shouldShowConnectionStatus = !brandId;
            const canManageConnectionLifecycle =
              row.isConnectionOwnedByCurrentUser || isCurrentMemberWorkspaceOwner;
            const canRemoveConnectionFromBrand = Boolean(
              brandId && canRemoveConnectionsFromBrand,
            );
            const showPluginConnectionAccessAction = shouldShowPluginConnectionAccessAction({
              isPluginAdmin,
              isBusinessGroupView: Boolean(brandId),
              isConnectionActive,
              canManageConnectionAccess: row.isManagedByCurrentUser,
            });
            const canShowConnectionActions =
              canManageConnectionLifecycle ||
              canRemoveConnectionFromBrand ||
              (canAssignBusinessGroupsForRow(row) && showBrandAssignmentControls) ||
              showPluginConnectionAccessAction;
            const canEditConnectionLabel = Boolean(
              !brandId && canManageConnectionLifecycle && !hasNestedProjectResources,
            );
            const disconnectTrackingId = getConnectionDisconnectTrackingId(row);
            const isConnectionDisconnecting = disconnectTrackingId
              ? disconnectingConnectionIds.has(disconnectTrackingId)
              : false;
            const assignedBrands = getProjectIdsForRow(row, isSecondaryResourceRow ? row.resourceKey : undefined)
              .map((brandId) => brandGroupData.brands.find((brand) => brand.id === brandId))
              .filter((brand): brand is NonNullable<typeof brand> => Boolean(brand));
            const assignedBrandSummary = assignedBrands
              .slice(0, MAX_INLINE_ASSIGNED_BUSINESS_GROUPS)
              .map((brand) => brand.name)
              .join(" · ");
            const assignedBrandDisplay =
              assignedBrands.length > MAX_INLINE_ASSIGNED_BUSINESS_GROUPS
                ? `${assignedBrandSummary} · +${assignedBrands.length - MAX_INLINE_ASSIGNED_BUSINESS_GROUPS}`
                : assignedBrandSummary;
            const hasAssignedBusinessGroups = !isPluginAdmin && Boolean(assignedBrandDisplay);
            // Keep independently manageable accounts and project resources
            // visible without adding a table for simple connections.
            const shouldRenderAccountTable = hasNestedProjectResources;
            const shouldAlignSimpleBrandAccess = Boolean(
              brandId && !hasNestedProjectResources,
            );
            const connectionAccountName = row.accountId || row.name || row.provider;
            const connectionActionControls = (
              <div className="flex flex-wrap items-center justify-end gap-2">
                <ConnectionActionsMenu
                  manageAccessText={t("website.integrationsSettings.manageConnectionAccess")}
                  assignBrandsText={t("website.integrationsSettings.assignBrands")}
                  disconnectText={t("website.integrationsSettings.disconnect")}
                  disconnectTooltip={t(
                    isPluginAdmin
                      ? "website.integrationsSettings.pluginDisconnectTooltip"
                      : "website.integrationsSettings.disconnectTooltip",
                  )}
                  removeFromBrandText={t("website.integrationsSettings.removeConnectionFromBrand")}
                  removeFromBrandTooltip={t("website.integrationsSettings.removeConnectionFromBrandTooltip")}
                  onManageAccess={
                    showPluginConnectionAccessAction && !hasSubAccounts
                      ? () => openUserAccessModal(row)
                      : undefined
                  }
                  onAssignBrands={
                    canAssignBusinessGroupsForRow(row) && !hasNestedProjectResources && showBrandAssignmentControls
                      ? isSecondaryResourceRow
                        ? () => openEditSecondaryResourceBrandsModal(row)
                        : !hasSecondaryProjectResources
                          ? () => openEditConnectionBrandsModal(row)
                          : undefined
                      : undefined
                  }
                  onDisconnect={
                    !brandId && canManageConnectionLifecycle
                      ? () => handleConnectionDisconnect(row)
                      : undefined
                  }
                  onRemoveFromBrand={
                    canRemoveConnectionFromBrand && (!hasSecondaryProjectResources || hasSubAccounts)
                      ? () => handleRemoveConnectionFromBrand(row)
                      : undefined
                  }
                  isDisconnecting={isConnectionDisconnecting}
                />
              </div>
            );
            return (
              <article
                key={row.id}
                id={isProviderAnchor ? getConnectionGroupElementId(row.provider) : undefined}
                data-connection-row-key={rowElementKey}
                tabIndex={isProviderAnchor ? -1 : undefined}
                className={clsx(
                  "rounded-xl border border-slate-200 bg-white px-5 py-4 transition-colors hover:border-[#C8D0E2] hover:shadow-sm outline-none focus:outline-none",
                  isHighlightedRow && "connection-row-highlight",
                )}
              >
                {/* Header: connection identity, status, meta line 2, and actions */}
                <div
                  className={clsx(
                    "items-center gap-4",
                    shouldAlignSimpleBrandAccess
                      ? clsx("grid", businessGroupApplicationGridClass)
                      : "flex justify-between",
                  )}
                >
                  <div className="flex min-w-0 flex-1 items-center gap-3.5">
                    {showProviderIcon ? (
                      <div
                        className={clsx(
                          "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
                          getBrandIconFrameClassName(row.provider),
                        )}
                      >
                        <img
                          src={brandIcon}
                          alt=""
                          title={row.provider}
                          className={getBrandIconImageClassName(row.provider, "h-5 w-5")}
                          onError={(event) => {
                            event.currentTarget.style.display = "none";
                          }}
                        />
                      </div>
                    ) : null}

                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      {/* Line 1: Provider Name + Status */}
                      <div className="flex max-w-full min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
                        <TruncatedTooltipText
                          text={connectionProvider}
                          className="block min-w-0 max-w-full truncate text-sm font-medium text-[#1A162F]"
                        />
                        {shouldShowConnectionStatus ? (
                          <ConnectionStatusText
                            status={row.status}
                            statusLabel={row.statusLabel}
                            isHighlighted={isHighlightedRow}
                          />
                        ) : null}
                      </div>

                      {/* Line 2: Account Identifier · Meta Line */}
                      <div className="flex max-w-full min-w-0 flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px] text-[#8A93B2]">
                        <TruncatedTooltipText
                          text={connectionPrimaryIdentity}
                          className="block max-w-full truncate text-xs text-[#475467]"
                        />
                        <span className="text-[#D1D5DB]">·</span>
                        <TruncatedTooltipText text={connectionMetaLine} className="block min-w-0 truncate" />
                      </div>
                      {!hasNestedProjectResources && !brandId ? (
                        <div className="flex max-w-full min-w-0 flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px] text-[#8A93B2]">
                          <div className={`group/label flex min-w-0 max-w-[220px] items-center ${metadataValueGapClass}`}>
                            <span className="shrink-0 text-[11px] font-medium text-[#71717A]">
                              {t("website.integrationsSettings.label")}{getColon(locale)}
                            </span>
                            <HoverEditLabel
                              text={row.alias || "-"}
                              editLabelText={t("website.integrationsSettings.editLabelAction")}
                              editLabelTooltip={t("website.integrationsSettings.editLabelTooltip")}
                              onEdit={
                                canEditConnectionLabel
                                  ? () => openEditConnectionLabelModal(row)
                                  : undefined
                              }
                            />
                          </div>
                          {isPluginAdmin ? (
                            <>
                              <span className="text-[#D1D5DB]">·</span>
                              <div className={`flex min-w-0 max-w-[260px] items-center ${metadataValueGapClass}`}>
                                <span className="shrink-0 text-[11px] font-medium text-[#71717A]">
                                  {t("website.integrationsSettings.myAccess")}{getColon(locale)}
                                </span>
                                <TruncatedTooltipText
                                  text={currentUserAccessLabel}
                                  className="block min-w-0 truncate text-xs font-medium text-[#3D394D]"
                                />
                              </div>
                            </>
                          ) : null}
                          {hasAssignedBusinessGroups ? (
                            <>
                              <span className="text-[#D1D5DB]">·</span>
                              <div className={`flex min-w-0 max-w-[480px] items-center ${metadataValueGapClass}`}>
                                <span className="shrink-0 text-[11px] font-medium text-[#71717A]">
                                  {t("website.integrationsSettings.assignedBusinessGroups")}{getColon(locale)}
                                </span>
                                <TruncatedTooltipText
                                  text={assignedBrandDisplay}
                                  className="block min-w-0 truncate text-xs text-[#3D394D]"
                                  tooltipContent={
                                    <BusinessGroupTooltipList
                                      businessGroups={assignedBrands}
                                      label={t("website.brandsSettings.brand")}
                                    />
                                  }
                                  tooltipClassName={businessGroupTooltipContentClassName}
                                />
                              </div>
                            </>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {shouldAlignSimpleBrandAccess ? (
                    <>
                      <div className="min-w-0">
                        <span className="mb-1 block text-[10px] font-medium text-[#8A93B2]">
                          {t("website.integrationsSettings.businessGroupAccessCeiling")}
                          {getColon(locale)}
                        </span>
                        <span className="block max-w-full truncate text-xs text-[#3D394D]">
                          {businessGroupAccessCeilingLabel}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <span className="mb-1 block text-[10px] font-medium text-[#8A93B2]">
                          {t("website.integrationsSettings.myAccess")}
                          {getColon(locale)}
                        </span>
                        <span className="block max-w-full truncate text-xs text-[#3D394D]">
                          {currentUserAccessLabel}
                        </span>
                      </div>
                      <div className="flex min-h-8 min-w-0 items-center justify-start gap-2">
                        {isConnectionActive && canShowConnectionActions
                          ? connectionActionControls
                          : null}
                        {renderBusinessGroupMemberAccessAction(row)}
                      </div>
                    </>
                  ) : (
                    <div className="flex min-h-8 items-center justify-end">
                      {isConnectionActive && canShowConnectionActions
                        ? connectionActionControls
                        : null}
                    </div>
                  )}
                </div>

                {shouldRenderAccountTable ? (
                  <div className="mt-4 border-t border-slate-100 pt-3.5">
                    <div className="w-full overflow-hidden rounded-lg border border-[#E8EBF3] bg-white">
                      <div
                        className={clsx(
                          "hidden items-center gap-4 border-b border-[#E8EBF3] bg-[#FCFCFC] py-2 text-[11px] font-medium text-[#8A93B2] md:grid",
                          workspaceAccountTableHorizontalPaddingClass,
                          workspaceAccountTableGridClass,
                        )}
                      >
                        <span>{t("website.integrationsSettings.account")}</span>
                        {!brandId ? <span>{t("website.integrationsSettings.label")}</span> : null}
                        {brandId ? (
                          <span>
                            {t("website.integrationsSettings.businessGroupAccessCeiling")}
                          </span>
                        ) : null}
                        {brandId || showBusinessGroupMetadata || isPluginAdmin ? (
                          <span>
                            {t(
                              brandId || isPluginAdmin
                                ? "website.integrationsSettings.myAccess"
                                : "website.brandsSettings.brand",
                            )}
                          </span>
                        ) : null}
                        {!brandId || showBusinessGroupMemberAccessActions ? (
                          <span className={workspaceAccountActionHeaderAlignment}>{t("website.integrationsSettings.action")}</span>
                        ) : null}
                      </div>

                      <div className="divide-y divide-[#E8EBF3]">
                        {hasSubAccounts ? row.subAccounts?.map((subAccount) => {
                          const customerBrandKey = getGoogleAdsCustomerBrandKey(row.id, subAccount.customerId);
                          const customerBrands = getProjectIdsForRow(row, subAccount.customerId)
                            .map((brandId) => brandGroupData.brands.find((brand) => brand.id === brandId))
                            .filter((brand): brand is NonNullable<typeof brand> => Boolean(brand));
                          const customerBrandSummary = customerBrands
                            .slice(0, 2)
                            .map((brand) => brand.name)
                            .join(" · ");
                          const customerBrandDisplay =
                            customerBrands.length > 2
                              ? `${customerBrandSummary} · +${customerBrands.length - 2}`
                              : customerBrandSummary;
                          const customerAccountName = subAccount.accountName || subAccount.customerId;
                          const customerLabel =
                            connectionAliasOverrides[
                              getConnectionLabelOverrideKey(row.id, subAccount.customerId)
                            ] ?? subAccount.label ?? "";
                          const isCustomerBusinessGroupAssignmentPending = Boolean(
                            !brandId && pendingBusinessGroupAssignmentResourceKeys?.has(subAccount.customerId),
                          );
                          const canEditCustomerLabel = Boolean(
                            !brandId &&
                              canManageConnectionLifecycle &&
                              getConnectionAccessBackendId(row),
                          );
                          const isGoogleAdsCustomerDisconnecting = disconnectingGoogleAdsCustomerKeys.has(
                            getGoogleAdsCustomerDisconnectOperationKey(row, subAccount.customerId),
                          );
                          const loadedCustomerAccessByMember = googleAdsCustomerAccessLevelsById[row.id];
                          const pluginCustomerAccess = currentMemberKey && loadedCustomerAccessByMember
                            ? (loadedCustomerAccessByMember[currentMemberKey]?.[subAccount.customerId] ?? "no_access")
                            : row.currentUserAccess;
                          return (
                            <div
                              key={customerBrandKey}
                              className={clsx(
                                "grid items-center gap-x-4 gap-y-3 py-3",
                                workspaceAccountTableHorizontalPaddingClass,
                                workspaceAccountTableGridClass,
                              )}
                            >
                              <div className="group/label min-w-0">
                                <span className="mb-1 block text-[10px] font-medium text-[#8A93B2] md:hidden">
                                  {t("website.integrationsSettings.account")}
                                  {getColon(locale)}
                                </span>
                                <TruncatedTooltipText
                                  text={customerAccountName}
                                  className="block max-w-full truncate text-xs font-medium text-[#1A162F]"
                                />
                                <div className="mt-0.5 flex min-w-0 items-center gap-2">
                                  <span className="block truncate text-[11px] text-[#8A93B2]">{subAccount.customerId}</span>
                                </div>
                              </div>
                              {!brandId ? (
                                <div className="group/label min-w-0">
                                  <span className="mb-1 block text-[10px] font-medium text-[#8A93B2] md:hidden">
                                    {t("website.integrationsSettings.label")}
                                    {getColon(locale)}
                                  </span>
                                  <HoverEditLabel
                                    text={customerLabel || "-"}
                                    editLabelText={t("website.integrationsSettings.editLabelAction")}
                                    editLabelTooltip={t("website.integrationsSettings.editLabelTooltip")}
                                    onEdit={
                                      canEditCustomerLabel
                                        ? () => openEditConnectionLabelModal(row, {
                                            resourceKey: subAccount.customerId,
                                            resourceType: "CUSTOMER",
                                            label: subAccount.label,
                                          })
                                        : undefined
                                    }
                                  />
                                </div>
                              ) : null}
                              {brandId ? (
                                <div className="min-w-0">
                                  <span className="mb-1 block text-[10px] font-medium text-[#8A93B2] md:hidden">
                                    {t("website.integrationsSettings.businessGroupAccessCeiling")}
                                    {getColon(locale)}
                                  </span>
                                  <span className="block max-w-full truncate text-xs text-[#3D394D]">
                                    {accessLabel(
                                      getBusinessGroupAccessCeilingForRow(
                                        row,
                                        subAccount.customerId,
                                      ),
                                    )}
                                  </span>
                                </div>
                              ) : null}
                              {brandId || showBusinessGroupMetadata || isPluginAdmin ? (
                                <div className="min-w-0">
                                <span className="mb-1 block text-[10px] font-medium text-[#8A93B2] md:hidden">
                                  {t(
                                    brandId || isPluginAdmin
                                      ? "website.integrationsSettings.myAccess"
                                      : "website.brandsSettings.brand",
                                  )}
                                  {getColon(locale)}
                                </span>
                                {brandId ? (
                                  <span className="block max-w-full truncate text-xs text-[#3D394D]">
                                    {accessLabel(subAccount.projectAccess || "no_access")}
                                  </span>
                                ) : isPluginAdmin ? (
                                  <span className="block max-w-full truncate text-xs font-medium text-[#3D394D]">
                                    {accessLabel(pluginCustomerAccess)}
                                  </span>
                                ) : isCustomerBusinessGroupAssignmentPending ? (
                                  <span
                                    aria-busy="true"
                                    className="inline-flex h-5 w-5 items-center justify-center text-[#8A93B2]"
                                  >
                                    <CircleNotch size={15} weight="bold" className="animate-spin" />
                                  </span>
                                ) : (
                                  <TruncatedTooltipText
                                    text={customerBrandDisplay || "-"}
                                    className="block max-w-full truncate text-xs text-[#3D394D]"
                                    tooltipContent={
                                      customerBrands.length ? (
                                        <BusinessGroupTooltipList
                                          businessGroups={customerBrands}
                                          label={t("website.brandsSettings.brand")}
                                        />
                                      ) : undefined
                                    }
                                    tooltipClassName={businessGroupTooltipContentClassName}
                                  />
                                )}
                                </div>
                              ) : null}
                              {!brandId ? <div className={clsx("flex min-w-0 items-center justify-between gap-3", workspaceAccountActionAlignment)}>
                                <span className="text-[10px] font-medium text-[#8A93B2] md:hidden">
                                  {t("website.integrationsSettings.action")}
                                  {getColon(locale)}
                                </span>
                                {canShowConnectionActions ? (
                                  <>
                                    {!isConnectionDisconnecting ? (
                                      <div className="flex items-center gap-2">
                                        <ConnectionActionsMenu
                                          manageAccessText={t("website.integrationsSettings.manageConnectionAccess")}
                                          assignBrandsText={t("website.integrationsSettings.assignBrands")}
                                          disconnectText={t("website.integrationsSettings.disconnectGoogleAdsCustomer")}
                                          disconnectTooltip={t(
                                            isPluginAdmin
                                              ? "website.integrationsSettings.pluginDisconnectGoogleAdsCustomerTooltip"
                                              : "website.integrationsSettings.disconnectGoogleAdsCustomerTooltip",
                                          )}
                                          removeFromBrandText={t("website.integrationsSettings.removeConnectionFromBrand")}
                                          removeFromBrandTooltip={t("website.integrationsSettings.removeConnectionFromBrandTooltip")}
                                          onManageAccess={
                                            showPluginConnectionAccessAction
                                              ? () => openUserAccessModal(row, subAccount.customerId)
                                              : undefined
                                          }
                                          onAssignBrands={
                                            canAssignBusinessGroupsForRow(row) &&
                                            showBrandAssignmentControls &&
                                            !isCustomerBusinessGroupAssignmentPending
                                              ? () => openEditGoogleAdsCustomerBrandsModal(row, subAccount)
                                              : undefined
                                          }
                                          onDisconnect={
                                            !brandId && row.isManagedByCurrentUser
                                              ? () => handleGoogleAdsCustomerDisconnect(row, subAccount)
                                              : undefined
                                          }
                                          onRemoveFromBrand={
                                            canRemoveConnectionFromBrand
                                              ? () => handleRemoveGoogleAdsCustomerFromBrand(row, subAccount)
                                              : undefined
                                          }
                                          isDisconnecting={isGoogleAdsCustomerDisconnecting}
                                        />
                                      </div>
                                    ) : null}
                                  </>
                                ) : (
                                  <span className="text-xs text-[#8A93B2]">-</span>
                                )}
                              </div> : showBusinessGroupMemberAccessActions ? (
                                <div className="flex min-w-0 justify-start">
                                  {renderBusinessGroupMemberAccessAction(row, {
                                    resourceType: "CUSTOMER",
                                    resourceKey: subAccount.customerId,
                                  })}
                                </div>
                              ) : null}
                            </div>
                          );
                        }) : hasSecondaryProjectResources ? secondaryProjectResources.map((resource) => {
                          const resourceBrands = getProjectIdsForRow(row, resource.resourceKey)
                            .map((projectId) => brandGroupData.brands.find((brand) => brand.id === projectId))
                            .filter((brand): brand is NonNullable<typeof brand> => Boolean(brand));
                          const resourceBrandSummary = resourceBrands
                            .slice(0, 2)
                            .map((brand) => brand.name)
                            .join(" · ");
                          const resourceBrandDisplay = resourceBrands.length > 2
                            ? `${resourceBrandSummary} · +${resourceBrands.length - 2}`
                            : resourceBrandSummary;
                          const resourceAccountName = resource.name || resource.resourceKey;
                          const resourceLabel = connectionAliasOverrides[
                            getConnectionLabelOverrideKey(row.id, resource.resourceKey)
                          ] ?? resource.workspaceLabel ?? "";
                          const canEditResourceLabel = Boolean(
                            !brandId &&
                              canManageConnectionLifecycle &&
                              getConnectionAccessBackendId(row),
                          );
                          const googleResourceDisconnectProvider = getGoogleResourceDisconnectProvider(row.provider);
                          const googleResourceDisconnectOperationKey = getGoogleResourceDisconnectOperationKey(
                            row,
                            resource.resourceKey,
                          );
                          const canDisconnectGoogleResource = Boolean(
                            !brandId && row.isManagedByCurrentUser && googleResourceDisconnectProvider,
                          );
                          const secondaryColumn = resolveProjectResourceSecondaryColumn(
                            Boolean(brandId),
                            row.projectAccessByResource,
                            resource,
                            resourceBrandDisplay,
                          );

                          return (
                            <div
                              key={`${row.id}:${resource.resourceKey}`}
                              className={clsx(
                                "grid items-center gap-x-4 gap-y-3 py-3",
                                workspaceAccountTableHorizontalPaddingClass,
                                workspaceAccountTableGridClass,
                              )}
                            >
                              <div className="group/label min-w-0">
                                <span className="mb-1 block text-[10px] font-medium text-[#8A93B2] md:hidden">
                                  {t("website.integrationsSettings.account")}{getColon(locale)}
                                </span>
                                <TruncatedTooltipText
                                  text={resourceAccountName}
                                  className="block max-w-full truncate text-xs font-medium text-[#1A162F]"
                                />
                                <div className="mt-0.5 flex min-w-0 items-center gap-2">
                                  <span className="block truncate text-[11px] text-[#8A93B2]">{resource.resourceKey}</span>
                                </div>
                              </div>
                              {!brandId ? (
                                <div className="group/label min-w-0">
                                  <span className="mb-1 block text-[10px] font-medium text-[#8A93B2] md:hidden">
                                    {t("website.integrationsSettings.label")}{getColon(locale)}
                                  </span>
                                  <HoverEditLabel
                                    text={resourceLabel || "-"}
                                    editLabelText={t("website.integrationsSettings.editLabelAction")}
                                    editLabelTooltip={t("website.integrationsSettings.editLabelTooltip")}
                                    onEdit={
                                      canEditResourceLabel
                                        ? () => openEditConnectionLabelModal(row, {
                                            resourceKey: resource.resourceKey,
                                            resourceType: resource.resourceType,
                                            label: resource.workspaceLabel,
                                          })
                                        : undefined
                                    }
                                  />
                                </div>
                              ) : null}
                              {brandId ? (
                                <div className="min-w-0">
                                  <span className="mb-1 block text-[10px] font-medium text-[#8A93B2] md:hidden">
                                    {t("website.integrationsSettings.businessGroupAccessCeiling")}
                                    {getColon(locale)}
                                  </span>
                                  <span className="block max-w-full truncate text-xs text-[#3D394D]">
                                    {accessLabel(
                                      getBusinessGroupAccessCeilingForRow(
                                        row,
                                        resource.resourceKey,
                                      ),
                                    )}
                                  </span>
                                </div>
                              ) : null}
                              {brandId || showBusinessGroupMetadata || isPluginAdmin ? (
                                <div className="min-w-0">
                                <span className="mb-1 block text-[10px] font-medium text-[#8A93B2] md:hidden">
                                  {t(
                                    secondaryColumn.kind === "access" || isPluginAdmin
                                      ? "website.integrationsSettings.myAccess"
                                      : "website.brandsSettings.brand",
                                  )}{getColon(locale)}
                                </span>
                                {isPluginAdmin ? (
                                  <span className="block max-w-full truncate text-xs font-medium text-[#3D394D]">
                                    {currentUserAccessLabel}
                                  </span>
                                ) : secondaryColumn.kind === "access" ? (
                                  <span className="block max-w-full truncate text-xs text-[#3D394D]">
                                    {accessLabel(secondaryColumn.access)}
                                  </span>
                                ) : (
                                  <TruncatedTooltipText
                                    text={secondaryColumn.brandDisplay || "-"}
                                    className="block max-w-full truncate text-xs text-[#3D394D]"
                                    tooltipContent={resourceBrands.length ? (
                                      <BusinessGroupTooltipList
                                        businessGroups={resourceBrands}
                                        label={t("website.brandsSettings.brand")}
                                      />
                                    ) : undefined}
                                    tooltipClassName={businessGroupTooltipContentClassName}
                                  />
                                )}
                                </div>
                              ) : null}
                              {!brandId ? <div className={clsx("flex min-w-0 items-center justify-between gap-3", workspaceAccountActionAlignment)}>
                                <span className="text-[10px] font-medium text-[#8A93B2] md:hidden">
                                  {t("website.integrationsSettings.action")}{getColon(locale)}
                                </span>
                                {canShowConnectionActions ? (
                                  <ConnectionActionsMenu
                                    assignBrandsText={t("website.integrationsSettings.assignBrands")}
                                    disconnectText={t("website.integrationsSettings.disconnect")}
                                    disconnectTooltip={t(
                                      isPluginAdmin
                                        ? "website.integrationsSettings.pluginDisconnectTooltip"
                                        : "website.integrationsSettings.disconnectTooltip",
                                    )}
                                    removeFromBrandText={t("website.integrationsSettings.removeConnectionFromBrand")}
                                    removeFromBrandTooltip={t("website.integrationsSettings.removeConnectionFromBrandTooltip")}
                                    onAssignBrands={
                                      canAssignBusinessGroupsForRow(row) && showBrandAssignmentControls
                                        ? () => openEditSecondaryResourceBrandsModal(row, resource.resourceKey)
                                        : undefined
                                    }
                                    onDisconnect={
                                      canDisconnectGoogleResource
                                        ? () => handleGoogleResourceDisconnect(row, resource)
                                        : undefined
                                    }
                                    onRemoveFromBrand={
                                      canRemoveConnectionFromBrand
                                        ? () => handleRemoveConnectionFromBrand(row, resource.resourceKey)
                                        : undefined
                                    }
                                    isDisconnecting={disconnectingGoogleResourceKeys.has(googleResourceDisconnectOperationKey)}
                                  />
                                ) : (
                                  <span className="text-xs text-[#8A93B2]">-</span>
                                )}
                              </div> : showBusinessGroupMemberAccessActions ? (
                                <div className="flex min-w-0 justify-start">
                                  {renderBusinessGroupMemberAccessAction(row, {
                                    resourceType: resource.resourceType,
                                    resourceKey: resource.resourceKey,
                                  })}
                                </div>
                              ) : null}
                            </div>
                          );
                        }) : (
                          <div
                            className={clsx(
                              "grid items-center gap-x-4 gap-y-3 py-3",
                              workspaceAccountTableHorizontalPaddingClass,
                              workspaceAccountTableGridClass,
                            )}
                          >
                            <div className="min-w-0">
                              <span className="mb-1 block text-[10px] font-medium text-[#8A93B2] md:hidden">
                                {t("website.integrationsSettings.account")}
                                {getColon(locale)}
                              </span>
                              <TruncatedTooltipText
                                text={connectionAccountName}
                                className="block max-w-full truncate text-xs font-medium text-[#1A162F]"
                              />
                            </div>
                            {!brandId ? (
                              <div className="min-w-0">
                                <span className="mb-1 block text-[10px] font-medium text-[#8A93B2] md:hidden">
                                  {t("website.integrationsSettings.label")}
                                  {getColon(locale)}
                                </span>
                                <TruncatedTooltipText
                                  text={row.alias || "-"}
                                  className="block max-w-full truncate text-xs text-[#3D394D]"
                                />
                              </div>
                            ) : null}
                            {brandId ? (
                              <div className="min-w-0">
                                <span className="mb-1 block text-[10px] font-medium text-[#8A93B2] md:hidden">
                                  {t("website.integrationsSettings.businessGroupAccessCeiling")}
                                  {getColon(locale)}
                                </span>
                                <span className="block max-w-full truncate text-xs text-[#3D394D]">
                                  {businessGroupAccessCeilingLabel}
                                </span>
                              </div>
                            ) : null}
                            {brandId || showBusinessGroupMetadata || isPluginAdmin ? (
                              <div className="min-w-0">
                              <span className="mb-1 block text-[10px] font-medium text-[#8A93B2] md:hidden">
                                {t(
                                  brandId || isPluginAdmin
                                    ? "website.integrationsSettings.myAccess"
                                    : "website.brandsSettings.brand",
                                )}
                                {getColon(locale)}
                              </span>
                              {brandId ? (
                                <span className="block max-w-full truncate text-xs text-[#3D394D]">
                                  {currentUserAccessLabel}
                                </span>
                              ) : isPluginAdmin ? (
                                <span className="block max-w-full truncate text-xs font-medium text-[#3D394D]">
                                  {currentUserAccessLabel}
                                </span>
                              ) : (
                                <TruncatedTooltipText
                                  text={assignedBrandDisplay || "-"}
                                  className="block max-w-full truncate text-xs text-[#3D394D]"
                                  tooltipContent={
                                    assignedBrands.length ? (
                                      <BusinessGroupTooltipList
                                        businessGroups={assignedBrands}
                                        label={t("website.brandsSettings.brand")}
                                      />
                                    ) : undefined
                                  }
                                  tooltipClassName={businessGroupTooltipContentClassName}
                                />
                              )}
                              </div>
                            ) : null}
                            {!brandId ? <div className={clsx("flex min-w-0 items-center justify-between gap-3", workspaceAccountActionAlignment)}>
                              <span className="text-[10px] font-medium text-[#8A93B2] md:hidden">
                                {t("website.integrationsSettings.action")}
                                {getColon(locale)}
                              </span>
                              {isConnectionActive && canShowConnectionActions ? (
                                connectionActionControls
                              ) : (
                                <span className="text-xs text-[#8A93B2]">-</span>
                              )}
                            </div> : null}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}
              </article>
            );
          })}
        </section>
      )}

      <Modal
        isOpen={showBrandAssignmentControls && canAssignBusinessGroups && isBulkBrandAssignmentOpen}
        onClose={closeBulkBrandAssignmentModal}
        title={t("website.integrationsSettings.bulkAssignAppsToBrands")}
        width="max-w-4xl"
        contentScrollable={false}
        containerClassName="h-[680px] max-h-[calc(100vh-2rem)]"
        bodyClassName="flex min-h-0 flex-1 flex-col px-6 pb-6 pt-1"
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <p className="shrink-0 text-sm leading-6 text-[#666F8D]">
            {t("website.integrationsSettings.assignAppsToBrandsDescription")}
          </p>

          <section className="mt-5 shrink-0 rounded-xl border border-[#E8EBF3] bg-[#FCFCFC] p-4">
            <div className="max-w-md space-y-2">
              <label className="block text-sm font-medium text-[#1A162F]">
                {t("website.integrationsSettings.businessGroups")}
              </label>
              <BrandSelectMenu
                value={bulkBrandAssignmentBrandId || ""}
                options={businessGroupAssignmentOptions}
                emptyLabel={t("website.integrationsSettings.chooseBusinessGroup")}
                defaultOptionLabel={t("website.brandsSettings.simple.defaultBrandBadge")}
                ariaLabel={t("website.integrationsSettings.chooseBusinessGroup")}
                onChange={handleBulkBrandAssignmentBrandChange}
                triggerClassName="h-10 px-3 text-sm font-normal"
              />
            </div>
          </section>

          <section className="mt-5 flex min-h-0 flex-1 flex-col">
            <div className="mb-3 flex shrink-0 items-center justify-between gap-4 pr-8">
              <h3 className="text-sm font-medium text-[#1A162F]">
                {t("website.integrationsSettings.appAccounts")}
              </h3>
              <div className="flex shrink-0 items-center gap-2.5">
                <span className="text-xs font-medium text-[#666F8D]">
                  {t("website.integrationsSettings.applyToAll")}
                </span>
                <ApplicationAccessPermissionControl
                  value={bulkBrandAssignmentAccessLevel}
                  onChange={(accessLevel) => {
                    if (!canApplyBulkBrandAssignmentAccess) return;
                    updateAllBulkBrandAssignmentAccessLevels(
                      accessLevel === "action" ? "WRITE" : "READ",
                    );
                  }}
                  accessLabels={{
                    no_access: "",
                    read: t("website.integrationsSettings.viewOnly"),
                    action: t("website.integrationsSettings.viewAndTakeAction"),
                  }}
                  availableAccessLevels={["read", "action"]}
                  ariaLabel={t("website.integrationsSettings.applyToAll")}
                  canManageAccess={false}
                  onCanManageAccessChange={() => undefined}
                  manageAccessLabel=""
                  manageAccessHelp=""
                  showManageAccessControl={false}
                  density="compact"
                  disabled={!bulkBrandAssignmentBrandId || isSavingBrandAssignments || !canApplyBulkBrandAssignmentAccess}
                  className="w-[236px] shrink-0"
                  accessControlClassName="w-[236px] grid-cols-[72px_152px]"
                />
              </div>
            </div>

            <div className="integrations-apps-scroll-area min-h-0 flex-1 divide-y overflow-y-auto rounded-xl border border-[#E8EBF3] bg-white [overflow-anchor:none] [scrollbar-gutter:stable]">
              {orderedBrandAssignmentUnits.length > 0 ? orderedBrandAssignmentUnits.map((unit) => {
                const isSelected = bulkBrandAssignmentUnitKeys.includes(unit.key);
                const isAssignedToBusinessGroup = assignedBulkBrandAssignmentUnitKeys.has(unit.key);
                const isUnitSelectionDisabled = !bulkBrandAssignmentBrandId;
                const canSetBulkUnitPositiveAccess = canSetBulkUnitPositiveAccessForUnit(unit);
                const currentAccessLevel = getBulkBrandAssignmentUnitAccessLevel(
                  unit,
                  bulkBrandAssignmentBrandId || "",
                );
                const displayedAccessLevel = isSelected
                  ? canSetBulkUnitPositiveAccess
                    ? bulkBrandAssignmentAccessLevels[unit.key] === "WRITE" ? "action" : "read"
                    : currentAccessLevel === "WRITE" ? "action" : currentAccessLevel === "READ" ? "read" : "no_access"
                  : undefined;
                const provider = unit.row.integrationType || unit.row.provider;
                const accountName = unit.subAccount || unit.resourceKey
                  ? unit.name
                  : unit.detail || unit.name;
                const accountDetail = unit.subAccount || unit.resourceKey
                  ? unit.detail
                  : unit.detail && unit.detail !== unit.name ? unit.name : "";
                const icon = getBrandIcon(unit.row.provider);

                return (
                  <div
                    key={unit.key}
                    role={isUnitSelectionDisabled ? undefined : "button"}
                    tabIndex={isUnitSelectionDisabled ? undefined : 0}
                    onClick={() => !isUnitSelectionDisabled && toggleBulkBrandAssignmentUnit(unit.key)}
                    onKeyDown={(event) => {
                      if (!isUnitSelectionDisabled && (event.key === "Enter" || event.key === " ")) {
                        event.preventDefault();
                        toggleBulkBrandAssignmentUnit(unit.key);
                      }
                    }}
                    className={clsx(
                      "flex items-center gap-3 px-4 py-3 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#C8D0E2]",
                      isUnitSelectionDisabled ? "cursor-not-allowed bg-white" : "cursor-pointer bg-white hover:bg-[#FCFCFD]",
                    )}
                  >
                    <span onClick={(event) => event.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        disabled={isUnitSelectionDisabled}
                        onCheckedChange={() => !isUnitSelectionDisabled && toggleBulkBrandAssignmentUnit(unit.key)}
                        aria-label={`${provider} ${unit.name}`}
                      />
                    </span>
                    <span className={clsx("flex h-9 w-9 shrink-0 items-center justify-center rounded-lg", getBrandIconFrameClassName(unit.row.provider))}>
                      <img src={icon} alt="" className={getBrandIconImageClassName(unit.row.provider, "h-5 w-5")} onError={(event) => { event.currentTarget.style.display = "none"; }} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-[#1A162F]">{provider}</span>
                      <span className="mt-0.5 block truncate text-sm font-medium text-[#3D394D]">{accountName}</span>
                      {accountDetail ? (
                        <span className="mt-0.5 block truncate text-xs text-[#8A93B2]">{accountDetail}</span>
                      ) : null}
                      <span className="mt-0.5 flex min-w-0 items-center gap-2 text-[11px] text-[#8A93B2]">
                        <span className="truncate">{formatConnectionMetaLine(t, locale, unit.row)}</span>
                        {isAssignedToBusinessGroup ? (
                          <span className="shrink-0 rounded-full bg-[#EEF2F8] px-2 py-0.5 text-[10px] font-medium text-[#52617F]">
                            {t("website.integrationsSettings.assignedToBusinessGroup")}
                          </span>
                        ) : null}
                      </span>
                    </span>
                    <span className="ml-auto shrink-0" onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                      <ApplicationAccessPermissionControl
                        value={displayedAccessLevel}
                        onChange={(accessLevel) => {
                          if (!canSetBulkUnitPositiveAccess) return;
                          updateBulkBrandAssignmentAccessLevel(unit.key, accessLevel === "action" ? "WRITE" : "READ");
                        }}
                        accessLabels={{ no_access: "", read: t("website.integrationsSettings.viewOnly"), action: t("website.integrationsSettings.viewAndTakeAction") }}
                        availableAccessLevels={canSetBulkUnitPositiveAccess
                          ? ["read", "action"]
                          : isSelected && displayedAccessLevel && displayedAccessLevel !== "no_access" ? [displayedAccessLevel] : ["no_access"]}
                        disabled={isUnitSelectionDisabled || isSavingBrandAssignments || !canSetBulkUnitPositiveAccess}
                        ariaLabel={`${provider} ${unit.name} ${t("website.integrationsSettings.access")}`}
                        canManageAccess={false}
                        onCanManageAccessChange={() => undefined}
                        manageAccessLabel=""
                        manageAccessHelp=""
                        showManageAccessControl={false}
                        density="compact"
                        className="w-[236px] shrink-0"
                        accessControlClassName="w-[236px] grid-cols-[72px_152px]"
                      />
                    </span>
                  </div>
                );
              }) : (
                <div className="flex h-full min-h-40 items-center justify-center px-6 text-center text-sm text-[#8A93B2]">
                  {t("website.integrationsSettings.noAppAccounts")}
                </div>
              )}
            </div>
          </section>

          <div className="mt-5 flex shrink-0 items-center justify-between gap-4">
            <span className="text-sm text-[#666F8D]">
              {t("website.integrationsSettings.selectedAppAccounts", { count: selectedBulkBrandAssignmentUnitCount })}
            </span>
            <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={closeBulkBrandAssignmentModal}
              disabled={isSavingBrandAssignments}
              className="rounded-lg px-4 py-2 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-950"
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={saveBulkBrandAssignment}
              disabled={isSavingBrandAssignments || !bulkBrandAssignmentBrandId || !bulkBrandAssignmentHasChanges}
              className="rounded-lg bg-[#1A162F] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#1A162F]/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSavingBrandAssignments ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : t("website.integrationsSettings.saveBusinessGroupAssignments")}
            </button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!editingLabelConnection}
        onClose={closeEditConnectionLabelModal}
        title={t("website.integrationsSettings.businessGroupLabel")}
        width="max-w-md"
        bodyClassName="px-6 pb-6 pt-0"
      >
        <div className="space-y-3">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-950">
              {t("website.integrationsSettings.businessGroupLabel")}
            </label>
            <input
              value={editingLabelValue}
              onChange={(event) => setEditingLabelValue(event.target.value)}
              placeholder={t("website.integrationsSettings.businessGroupLabelPlaceholder")}
              disabled={isSavingConnectionLabel}
              className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-950 transition-all placeholder:text-slate-400 focus:border-[#1A162F]/30 focus:outline-none focus:ring-2 focus:ring-[#1A162F]/10 disabled:cursor-not-allowed disabled:bg-slate-50"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={closeEditConnectionLabelModal}
              disabled={isSavingConnectionLabel}
              className="rounded-lg px-4 py-2 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={handleSaveConnectionLabel}
              disabled={isSavingConnectionLabel}
              className="rounded-lg bg-[#1A162F] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#1A162F]/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSavingConnectionLabel ? (
                <span className="inline-flex items-center gap-2">
                  <CircleNotch size={14} className="animate-spin" />
                  {t("website.integrationsSettings.saving")}
                </span>
              ) : (
                t("common.save")
              )}
            </button>
          </div>
        </div>
      </Modal>

      <BusinessGroupAssignmentModal
        isOpen={showBrandAssignmentControls && editingBrandAssignableBusinessGroups.length > 0 && !!editingBrandConnection}
        onClose={closeEditConnectionBrandsModal}
        account={editingBrandConnection?.accountId || ""}
        brands={editingBrandAssignableBusinessGroups}
        selectedBrandIds={editingBrandIds}
        assignedBrandIds={
          editingBrandConnection ? getProjectIdsForRow(editingBrandConnection) : []
        }
        accessLevels={editingBrandMaxAccessLevels}
        onToggle={toggleEditingBrand}
        onAccessLevelChange={(brandId, level) =>
          setEditingBrandMaxAccessLevels((previous) => ({
            ...previous,
            [brandId]: level,
          }))
        }
        onSave={saveConnectionBrands}
        isSaving={isSavingBrandAssignments}
        isSaveDisabled={!editingBrandAssignmentHasChanges}
        canSetPositiveAccess={editingBrandConnection ? canDelegatePositiveProjectAccess(editingBrandConnection) : false}
        currentAccessLevels={editingBrandConnection ? getProjectConnectionForRow(editingBrandConnection)?.brandAccessCeilings : {}}
      />

      <BusinessGroupAssignmentModal
        isOpen={showBrandAssignmentControls && editingGoogleAdsAssignableBusinessGroups.length > 0 && !!editingGoogleAdsCustomerBrand}
        onClose={closeEditGoogleAdsCustomerBrandsModal}
        account={editingGoogleAdsCustomerBrand?.subAccount.customerId || ""}
        brands={editingGoogleAdsAssignableBusinessGroups}
        selectedBrandIds={editingGoogleAdsCustomerBrandIds}
        assignedBrandIds={
          editingGoogleAdsCustomerBrand
            ? getProjectIdsForRow(
                editingGoogleAdsCustomerBrand.connection,
                editingGoogleAdsCustomerBrand.subAccount.customerId,
              )
            : []
        }
        accessLevels={editingGoogleAdsCustomerBrandMaxAccessLevels}
        onToggle={toggleEditingGoogleAdsCustomerBrand}
        onAccessLevelChange={(brandId, level) =>
          setEditingGoogleAdsCustomerBrandMaxAccessLevels((previous) => ({
            ...previous,
            [brandId]: level,
          }))
        }
        onSave={saveGoogleAdsCustomerBrands}
        isSaving={isSavingBrandAssignments}
        isSaveDisabled={!editingGoogleAdsCustomerBrandAssignmentHasChanges}
        canSetPositiveAccess={editingGoogleAdsCustomerBrand
          ? canDelegatePositiveProjectAccess(editingGoogleAdsCustomerBrand.connection)
          : false}
        currentAccessLevels={editingGoogleAdsCustomerBrand
          ? getProjectResourcesForRow(
              editingGoogleAdsCustomerBrand.connection,
              editingGoogleAdsCustomerBrand.subAccount.customerId,
            )[0]?.brandAccessCeilings || {}
          : {}}
      />

      <BusinessGroupAssignmentModal
        isOpen={showBrandAssignmentControls && editingSecondaryAssignableBusinessGroups.length > 0 && !!editingSecondaryResourceBrand}
        onClose={closeEditSecondaryResourceBrandsModal}
        account={
          editingSecondaryResourceBrand?.resource.name ||
          editingSecondaryResourceBrand?.resource.resourceKey ||
          ""
        }
        brands={editingSecondaryAssignableBusinessGroups}
        selectedBrandIds={editingSecondaryResourceBrandIds}
        assignedBrandIds={editingSecondaryResourceBrand?.resource.brandIds || []}
        accessLevels={editingSecondaryResourceBrandMaxAccessLevels}
        onToggle={toggleEditingSecondaryResourceBrand}
        onAccessLevelChange={(brandId, level) =>
          setEditingSecondaryResourceBrandMaxAccessLevels((previous) => ({
            ...previous,
            [brandId]: level,
          }))
        }
        onSave={saveSecondaryResourceBrands}
        isSaving={isSavingBrandAssignments}
        isSaveDisabled={!editingSecondaryResourceBrandAssignmentHasChanges}
        canSetPositiveAccess={editingSecondaryResourceBrand
          ? canDelegatePositiveProjectAccess(editingSecondaryResourceBrand.connection)
          : false}
        currentAccessLevels={editingSecondaryResourceBrand?.resource.brandAccessCeilings || {}}
      />

      <Modal
        isOpen={!!activeAccessRow}
        onClose={closeUserAccessModal}
        title={
          isActiveAccessGoogleAds && activeAccessCustomerId
            ? t("website.integrationsSettings.userAccessForGoogleAdsAccount", {
                account: activeAccessMinimumUnitName || activeAccessCustomerId,
              })
            : t("website.integrationsSettings.userAccessForConnection")
        }
        width={isPluginAdmin ? "max-w-[1080px]" : "max-w-[900px]"}
        bodyClassName="px-6 pb-6 pt-1"
      >
        {activeAccessRow ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-500 -mt-2">
              {t("website.integrationsSettings.connectionAccessDescription")}
            </p>
            {activeAccessError ? (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {activeAccessError}
              </div>
            ) : null}
            {activeAssignableMembers.length > 0 && (
              <div className="flex flex-col gap-3 px-1 text-xs sm:flex-row sm:items-center sm:justify-between">
                <div className="relative w-full sm:max-w-[280px]">
                  <MagnifyingGlass
                    size={16}
                    weight="regular"
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  />
                  <input
                    type="text"
                    value={memberSearchQuery}
                    onChange={(e) => {
                      setMemberSearchQuery(e.target.value);
                      setMembersPage(1);
                    }}
                    placeholder={t("website.integrationsSettings.searchMembers") || "Search users..."}
                    className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-700 placeholder:text-slate-400 outline-none transition-colors hover:border-slate-300 focus:border-[#1A162F] focus:ring-2 focus:ring-[#1A162F]/10"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                  <span className="font-medium text-[#666F8D]">{t("website.integrationsSettings.applyToAll")}</span>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => handleSelectAllAccess("no_access")}
                      disabled={
                        !canEditActiveConnectionMemberAccess || isActiveAccessLoading || isSavingActiveAccess
                      }
                      className={clsx(
                        "rounded px-2.5 py-1 text-xs font-medium border transition-colors",
                        selectAllState.no_access
                          ? "bg-[#1A162F] border-[#1A162F] text-white"
                          : "bg-white border-slate-200 text-slate-600 hover:border-slate-300",
                      )}
                    >
                      {t("website.integrationsSettings.noAccess")}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSelectAllAccess("read")}
                      disabled={
                        !canEditActiveConnectionMemberAccess || isActiveAccessLoading || isSavingActiveAccess
                      }
                      className={clsx(
                        "rounded px-2.5 py-1 text-xs font-medium border transition-colors",
                        selectAllState.read
                          ? "bg-[#1A162F] border-[#1A162F] text-white"
                          : "bg-white border-slate-200 text-slate-600 hover:border-slate-300",
                      )}
                    >
                      {t("website.integrationsSettings.viewOnly")}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSelectAllAccess("action")}
                      disabled={
                        !canEditActiveConnectionMemberAccess || isActiveAccessLoading || isSavingActiveAccess
                      }
                      className={clsx(
                        "rounded px-2.5 py-1 text-xs font-medium border transition-colors",
                        selectAllState.action
                          ? "bg-[#1A162F] border-[#1A162F] text-white"
                          : "bg-white border-slate-200 text-slate-600 hover:border-slate-300",
                      )}
                    >
                      {t("website.integrationsSettings.viewAndTakeAction")}
                    </button>
                  </div>
                </div>
              </div>
            )}
            {isActiveAccessLoading ? (
              <ConnectionAccessTableSkeleton />
            ) : activeAssignableMembers.length === 0 ? (
              <div className="rounded-lg border border-slate-200 bg-white px-4 py-4 text-sm text-slate-500">
                {t("website.integrationsSettings.noAssignableMembers")}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                  {/* Column Headers */}
                  <div
                    className={clsx(
                      "grid items-center gap-4 border-b border-slate-100 bg-slate-50/40 px-4 py-2.5",
                      isPluginAdmin
                        ? "grid-cols-[minmax(300px,1fr)_140px_200px_156px]"
                        : "grid-cols-[minmax(300px,1fr)_140px_200px]",
                    )}
                  >
                    <div className="min-w-0 pl-11">
                      <span className="text-xs font-medium text-[#666F8D]">
                        {t("website.integrationsSettings.user")}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <span className="text-xs font-medium text-[#666F8D]">
                        {t("website.integrationsSettings.role")}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <span className="text-xs font-medium text-[#666F8D]">
                        {t("website.integrationsSettings.connectionAccessLevel")}
                      </span>
                    </div>
                    {isPluginAdmin ? (
                      <div className="flex min-w-0 items-center gap-1.5 whitespace-nowrap">
                        <span className="text-xs font-medium text-[#666F8D]">
                          {t("website.integrationsSettings.canManageAccess")}
                        </span>
                        <Tooltip
                          content={t("website.integrationsSettings.canManageAccessHelp")}
                          side="top"
                          align="center"
                          sideOffset={6}
                          contentClassName="!w-[420px] !max-w-[calc(100vw-32px)] !whitespace-normal !text-left"
                        >
                          <button
                            type="button"
                            aria-label={t("website.integrationsSettings.canManageAccessHelp")}
                            className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-[#8A93B2] transition-colors hover:text-[#1A162F] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]/20"
                          >
                            <Question size={14} weight="bold" />
                          </button>
                        </Tooltip>
                      </div>
                    ) : null}
                  </div>

                  {/* Compact User List */}
                  <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto custom-scrollbar sm:max-h-[55vh]">
                    {paginatedMembers.length > 0 ? (
                      paginatedMembers.map((member) => {
                        const isCreator = member.key === activeAccessRow.connectionOwnerKey;
                        const canEditManageAccess = activeAccessRow.isConnectionOwnedByCurrentUser;
                        const isCurrentUserGrantedManager = member.isCurrentUser && activeAccessRow.canManageAccess;
                        const savedAccess = connectionAccessById[activeAccessRow.id]?.[member.key] ?? "no_access";
                        const savedManageAccess = connectionManageAccessById[activeAccessRow.id]?.[member.key] ?? false;
                        const access = modalAccessDraft[member.key] ?? savedAccess;
                        const canManageAccess = isCurrentUserGrantedManager
                          ? true
                          : modalManageAccessDraft[member.key] ?? savedManageAccess;
                        const locked =
                          !canEditActiveConnectionMemberAccess || isActiveAccessLoading || isSavingActiveAccess;
                        const canEditMemberAccess = activeEditableAssignableMemberKeys.has(member.key);
                        const isConnectionOwnerAccessProtected = Boolean(
                          isPluginAdmin &&
                            isCreator &&
                            !activeAccessRow.isConnectionOwnedByCurrentUser &&
                            canEditActiveConnectionMemberAccess,
                        );
                        const memberAccessLocked = locked || !canEditMemberAccess;
                        const displayRole = getMemberDisplayRole(member, t);

                        return (
                          <div
                            key={`${activeAccessRow.id}-${member.key}`}
                            className={clsx(
                              "grid items-center gap-4 px-4 py-3 transition-colors hover:bg-slate-50/40",
                              isPluginAdmin
                                ? "grid-cols-[minmax(300px,1fr)_140px_200px_156px]"
                                : "grid-cols-[minmax(300px,1fr)_140px_200px]",
                            )}
                          >
                            {/* Identity */}
                            <div className="flex min-w-0 items-center gap-3">
                              {member.avatar ? (
                                <img
                                  src={member.avatar}
                                  alt=""
                                  className="h-8 w-8 shrink-0 rounded-full object-cover ring-1 ring-slate-100"
                                />
                              ) : (
                                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
                                  {member.name.slice(0, 1).toUpperCase()}
                                </div>
                              )}
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-1.5">
                                  <TruncatedTooltipText
                                    text={member.name}
                                    className="block min-w-0 truncate text-sm font-medium text-slate-900"
                                  />
                                  {member.isCurrentUser && (
                                    <span className="shrink-0 rounded-full bg-[#1A162F]/5 px-1.5 py-0.5 text-[10px] font-semibold text-[#1A162F]">
                                      {t("website.integrationsSettings.you")}
                                    </span>
                                  )}
                                  {isCreator && (
                                    <span className="shrink-0 rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-600 ring-1 ring-inset ring-blue-500/20">
                                      {t("website.integrationsSettings.connectionOwner")}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1 text-xs text-slate-500">
                                  <TruncatedTooltipText
                                    text={member.email || t("website.integrationsSettings.member")}
                                    className="block min-w-0 truncate"
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Role */}
                            <div className="min-w-0">
                              <TruncatedTooltipText
                                text={displayRole || t("website.integrationsSettings.member")}
                                className="block min-w-0 truncate text-sm font-normal text-[#666F8D]"
                              />
                            </div>

                            {/* Access Level Dropdown */}
                            <div className="flex justify-start">
                              {isConnectionOwnerAccessProtected ? (
                                <Tooltip
                                  content={t("website.integrationsSettings.connectionOwnerAccessProtected")}
                                  side="top"
                                  align="center"
                                  sideOffset={6}
                                >
                                  <span
                                    className="inline-flex cursor-not-allowed [&>button]:pointer-events-none"
                                    tabIndex={0}
                                    aria-label={t("website.integrationsSettings.connectionOwnerAccessProtected")}
                                  >
                                    <AccessLevelDropdown
                                      value={access}
                                      onChange={(level) => handleAccessDraftChange(member.key, level)}
                                      disabled
                                      accessLabel={accessLabel}
                                    />
                                  </span>
                                </Tooltip>
                              ) : (
                                <AccessLevelDropdown
                                  value={access}
                                  onChange={(level) => handleAccessDraftChange(member.key, level)}
                                  disabled={memberAccessLocked}
                                  accessLabel={accessLabel}
                                />
                              )}
                            </div>

                            {isPluginAdmin ? (
                              <div className="flex justify-start">
                                {canEditManageAccess ? (
                                  <Tooltip
                                    content={
                                      isCreator
                                        ? t("website.integrationsSettings.connectionOwnerManageAccessLocked")
                                        : undefined
                                    }
                                    side="top"
                                    align="center"
                                    sideOffset={6}
                                  >
                                    <span
                                      className={clsx(
                                        "inline-flex",
                                        isCreator && "cursor-not-allowed [&>button]:pointer-events-none",
                                      )}
                                      tabIndex={isCreator ? 0 : undefined}
                                      aria-label={
                                        isCreator
                                          ? t("website.integrationsSettings.connectionOwnerManageAccessLocked")
                                          : undefined
                                      }
                                    >
                                      <ManageAccessSwitch
                                        checked={canManageAccess}
                                        disabled={isCreator || locked}
                                        onChange={(checked) => handleManageAccessDraftChange(member.key, checked)}
                                        ariaLabel={`${member.name} - ${t("website.integrationsSettings.canManageAccess")}`}
                                      />
                                    </span>
                                  </Tooltip>
                                ) : (
                                  <ManageAccessStatus
                                    enabled={canManageAccess}
                                    enabledLabel={t("website.integrationsSettings.canManageAccessStatus")}
                                    disabledLabel={t("website.integrationsSettings.cannotManageAccessStatus")}
                                  />
                                )}
                              </div>
                            ) : null}

                          </div>
                        );
                      })
                    ) : (
                      <div className="flex flex-col items-center justify-center py-12 text-text-sub">
                        <User size={48} weight="duotone" className="mb-4 opacity-50" />
                        <p>
                          {t("website.membersSettings.noUsersMatching", {
                            query: memberSearchQuery,
                          })}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Pagination Footer */}
                  {totalMembers > MEMBERS_PER_PAGE ? (
                    <div className="flex items-center justify-between border-t border-[#ECEFF5] bg-[#FCFCFC] px-6 py-2.5 rounded-lg border">
                      <span className="text-xs font-normal text-[#666F8D]">
                        {t("website.billing.showingHistoryRange", {
                          start: (membersPage - 1) * MEMBERS_PER_PAGE + 1,
                          end: Math.min(membersPage * MEMBERS_PER_PAGE, totalMembers),
                          total: totalMembers,
                        })}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setMembersPage((page) => Math.max(1, page - 1))}
                          disabled={membersPage === 1}
                          className="rounded-lg p-1.5 text-[#8A93B2] transition-colors hover:bg-[#F4F5F8] hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <CaretLeft size={14} weight="regular" />
                        </button>
                        <div className="flex items-center gap-1">
                          {visibleMembersPages.map((page) => (
                            <button
                              key={page}
                              type="button"
                              onClick={() => setMembersPage(page)}
                              className={clsx(
                                "h-7 w-7 rounded-md text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                                membersPage === page
                                  ? "bg-[#1A162F] text-white hover:bg-primary/90"
                                  : "text-[#666F8D] hover:bg-[#F4F5F8] hover:text-[#1A162F]",
                              )}
                            >
                              {page}
                            </button>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() => setMembersPage((page) => Math.min(totalMembersPages, page + 1))}
                          disabled={membersPage === totalMembersPages}
                          className="rounded-lg p-1.5 text-[#8A93B2] transition-colors hover:bg-[#F4F5F8] hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <CaretRight size={14} weight="regular" />
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={closeUserAccessModal}
                className="rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={handleSaveUserAccess}
                disabled={!canEditActiveConnectionMemberAccess || isActiveAccessLoading || isSavingActiveAccess}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t("website.integrationsSettings.updateAccess")}
              </button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default ConnectionAccessSettings;
